import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_CONFIG } from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import Button from '../components/Button';
import { clampPercentage, getProportionalScoreColor } from '../utils/scoringUtils';
import { computeAssessmentScore } from '../logic/scoring';
import { BadgeCheck, BadgeAlert } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import { useAutoRefreshAssessments } from '../hooks/useAutoRefreshAssessments';
import {
  fetchAllAssessmentData,
  AssessmentType,
  MergedAssessmentData,
  getCacheKey,
  loadFromCache,
  saveToCache
} from '../logic/insights';

// Lazy load charting component
const Charts = React.lazy(() => import('../components/Charts'));

interface SpiritualAssessmentResult {
  id: string;
  assessmentType: 'fasdl' | 'facit-sp-12';
  assessmentData: {
    scores: {
      overall: number;
      subscales: {
        connectedness?: number;
        mindfulness?: number;
        compassion?: number;
        gratitude?: number;
        innerPeace?: number;
        spiritualWellBeing?: number;
      };
    };
    results: {
      level: 'Low' | 'Moderate' | 'High';
      interpretation: string;
    };
  };
  spiritualData?: {
    chronicIllness: boolean;
    assessmentType: 'fasdl' | 'facit-sp-12';
  };
  createdAt: string;
  updatedAt?: string;
}

interface NutritionalAssessmentResult {
  id: string;
  assessmentType: 'phil-hei';
  scores: {
    overall: number;
    healthyFoods: number;
    unhealthyFoods: number;
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface PhysicalAssessmentResult {
  id: string;
  assessmentType: 'physical';
  scores?: {
    overall: number;
    categories?: {
      name: string;
      score: number;
      description: string;
    }[];
    ipaqSf?: {
      totalMET: number;
      activityLevel: string;
      sittingHours: number;
      sittingLevel: string;
    };
    psqi?: {
      componentScores: {
        sleepQuality: number;
        sleepLatency: number;
        sleepDuration: number;
        sleepEfficiency: number;
        sleepDisturbances: number;
        sleepMedication: number;
        daytimeDysfunction: number;
      };
      globalScore: number;
      sleepQuality: string;
    };
  };
  results?: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations?: any[];
  };
  assessmentData?: {
    scores: {
      overall: number;
      categories?: {
        name: string;
        score: number;
        description: string;
      }[];
      ipaqSf?: {
        totalMET: number;
        activityLevel: string;
        sittingHours: number;
        sittingLevel: string;
      };
      psqi?: {
        componentScores: {
          sleepQuality: number;
          sleepLatency: number;
          sleepDuration: number;
          sleepEfficiency: number;
          sleepDisturbances: number;
          sleepMedication: number;
          daytimeDysfunction: number;
        };
        globalScore: number;
        sleepQuality: string;
      };
    };
    results: {
      level: 'Low' | 'Moderate' | 'High';
      interpretation: string;
      recommendations?: any[];
    };
  };
  createdAt: string;
}

interface EmotioPsychoRationalAssessmentResult {
  id: string;
  selectedCategory: number;
  scores: {
    overall: number;
    category1: number;
    category2: number;
    category3: number;
    category4: number;
    subscales?: {
      depression: number;
      anxiety: number;
      stress: number;
    };
    phqTotal?: number;
    gadTotal?: number;
    genderTotal?: number;
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface LegendarialAssessmentResult {
  id: string;
  selectedCategory: number; // 1 = SWLS, 2 = PIL
  scores: {
    swlsTotal?: number;
    pilTotal?: number;
  };
  results: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface ReligioTraditioCulturalAssessmentResult {
  id: string;
  selectedCategory: number; // 1 = Brief RCOPE, 2 = CVSCALE, 3 = TVAS
  scores: any;
  results: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface SocioRationalAssessmentResult {
  id: string;
  selectedCategory?: number; // 1 = Gender Sensitivity STAR, 2 = MSPSS
  scores?: {
    socioRelationalScore: number;
    subscales?: {
      significantOther?: number;
      family?: number;
      friends?: number;
    };
  };
  results?: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  assessmentData?: {
    selectedCategory?: number;
    scores: {
      socioRelationalScore: number;
      subscales?: {
        significantOther?: number;
        family?: number;
        friends?: number;
      };
    };
    results: {
      level: string;
      interpretation: string;
      recommendations: string[];
    };
  };
  createdAt: string;
}

interface FinancialAssessmentResult {
  id: string;
  selectedCategory: number; // 1 = Budgeting, 2 = Savings, 3 = Investment
  scores: {
    overall: number;
    categoryScore: number;
  };
  results: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface EnvironmentalAssessmentResult {
  id: string;
  scores: {
    rawScore: number;
    transformedScore: number;
  };
  results: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

interface OccupationalAssessmentResult {
  id: string;
  selectedCategory: number; // 1 = CBI, 2 = UWES, 3 = Employee Engagement
  scores: any;
  results: {
    level: string;
    interpretation: string;
    recommendations: string[];
  };
  createdAt: string;
}

// Assessment configuration for dynamic history modal buttons
// To add a new assessment with categories:
// 1. Add a new entry to ASSESSMENT_CONFIGS
// 2. Set displayName to the assessment title
// 3. Add categories array with id, name, and optional historyType
// 4. Add all related history types to groupTypes array
// 5. Update the backend API to handle the new assessment types

interface AssessmentCategory {
  id: number;
  name: string;
  historyType?: string; // If different from the main assessment type
}

interface AssessmentConfig {
  displayName: string;
  categories: AssessmentCategory[];
  groupTypes: string[]; // All history types that belong to this assessment group
}

const ASSESSMENT_CONFIGS: Record<string, AssessmentConfig> = {
  spiritual: {
    displayName: 'Spiritual',
    categories: [
      { id: 0, name: 'All Spiritual', historyType: 'spiritual' },
      { id: 1, name: 'FASDL', historyType: 'fasdl' },
      { id: 2, name: 'FACIT-Sp-12', historyType: 'facit-sp-12' }
    ],
    groupTypes: ['spiritual', 'fasdl', 'facit-sp-12']
  },
  'mental-health': {
    displayName: 'Emotio-Psycho-Rational',
    categories: [
      { id: 0, name: 'All Emotio-Psycho-Rational', historyType: 'mental-health' },
      { id: 1, name: 'DASS-21', historyType: 'dass-21' },
      { id: 2, name: 'PHQ-9', historyType: 'phq-9' },
      { id: 3, name: 'GAD-7', historyType: 'gad-7' },
      { id: 4, name: 'Gender Sensitivity', historyType: 'gender' }
    ],
    groupTypes: ['mental-health', 'dass-21', 'phq-9', 'gad-7', 'gender']
  },
  legendarial: {
    displayName: 'Legendarial',
    categories: [
      { id: 0, name: 'All Legendarial', historyType: 'legendarial' },
      { id: 1, name: 'SWLS' },
      { id: 2, name: 'PIL' }
    ],
    groupTypes: ['legendarial']
  },
  religio: {
    displayName: 'Religio-Traditio-Cultural',
    categories: [
      { id: 0, name: 'All Religio-Traditio-Cultural', historyType: 'religio' },
      { id: 1, name: 'Brief RCOPE' },
      { id: 2, name: 'CVSCALE' },
      { id: 3, name: 'TVAS' }
    ],
    groupTypes: ['religio']
  },
  'socio-relational': {
    displayName: 'Socio-Relational',
    categories: [
      { id: 0, name: 'All Socio-Relational', historyType: 'socio-relational' },
      { id: 1, name: 'Gender Sensitivity STAR', historyType: 'socio-relational' },
      { id: 2, name: 'MSPSS', historyType: 'socio-relational' }
    ],
    groupTypes: ['socio-relational']
  },
  occupational: {
    displayName: 'Occupational',
    categories: [
      { id: 0, name: 'All Occupational', historyType: 'occupational' },
      { id: 1, name: 'CBI' },
      { id: 2, name: 'UWES' },
      { id: 3, name: 'Employee Engagement' }
    ],
    groupTypes: ['occupational']
  },
  physical: {
    displayName: 'Physical',
    categories: [
      { id: 0, name: 'All Physical', historyType: 'physical' },
      { id: 2, name: 'IPAQ-SF', historyType: 'physical-ipaq' },
      { id: 3, name: 'PSQI', historyType: 'physical-psqi' }
    ],
    groupTypes: ['physical', 'physical-ipaq', 'physical-psqi']
  },
  financial: {
    displayName: 'Financial',
    categories: [
      { id: 0, name: 'All Financial', historyType: 'financial' },
      { id: 1, name: 'Budgeting' },
      { id: 2, name: 'Savings' },
      { id: 3, name: 'Investment' }
    ],
    groupTypes: ['financial']
  }
};

// Helper function to get assessment group from history type
const getAssessmentGroup = (historyType: string): string | null => {
  for (const [group, config] of Object.entries(ASSESSMENT_CONFIGS)) {
    if (config.groupTypes.includes(historyType)) {
      return group;
    }
  }
  return null;
};

// Helper to get correct level for financial
function getFinancialLevel(percentage: number): 'Low' | 'Moderate' | 'High' {
  if (percentage >= 70) return 'High';
  if (percentage >= 40) return 'Moderate';
  return 'Low';
}

// SOWB level from average score (0-4 scale, same as consultant backend)
function getSowbLevelFromScore(averageScore: number): string {
  if (averageScore >= 3.5) return 'At Your Well-Being State';
  if (averageScore >= 2.5) return 'Feeling Empowered';
  if (averageScore >= 1.5) return 'Taking Steps';
  return 'Needs Support';
}

const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Use new merged assessment data structure
  const cacheKey = getCacheKey(user?.id);
  const cachedData = loadFromCache(cacheKey);
  const hasCache = !!cachedData;
  
  // Initialize with cached data but exclude mental-health to prevent stale flicker during page load
  const initialData: Record<AssessmentType, MergedAssessmentData> = cachedData 
    ? Object.fromEntries(
        Object.entries(cachedData)
      ) as Record<AssessmentType, MergedAssessmentData>
    : ({} as Record<AssessmentType, MergedAssessmentData>);

  const [assessmentData, setAssessmentData] = useState<Record<AssessmentType, MergedAssessmentData>>(
    initialData
  );
  const [isLoading, setIsLoading] = useState(!hasCache);
  const [hasLoadedFreshData, setHasLoadedFreshData] = useState(false);

  // Backward compatibility accessors for existing code
  // Find latest FASDL and FACIT-Sp-12 from history for correct progress calculation
  const spiritualHistory = assessmentData['spiritual']?.history as SpiritualAssessmentResult[] || [];
  const fasdlAssessment = spiritualHistory
    .filter(a => a.assessmentType === 'fasdl')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  const facitAssessment = spiritualHistory
    .filter(a => a.assessmentType === 'facit-sp-12')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  const nutritionalAssessment = assessmentData['nutritional']?.latest as NutritionalAssessmentResult | null;
  const physicalAssessment = assessmentData['physical']?.latest as PhysicalAssessmentResult | null;
  
  // Get mental-health assessments by selectedCategory from history
  const mentalHealthHistory = assessmentData['mental-health']?.history as EmotioPsychoRationalAssessmentResult[] || [];
  const dassAssessment = mentalHealthHistory
    .filter(a => a.selectedCategory === 1)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  const phqAssessment = mentalHealthHistory
    .filter(a => a.selectedCategory === 2)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  const gadAssessment = mentalHealthHistory
    .filter(a => a.selectedCategory === 3)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  const genderAssessment = mentalHealthHistory
    .filter(a => a.selectedCategory === 4)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] || null;
  
  // Get only the LATEST assessment for each category type (not all history)
  const legendarialHistory = assessmentData['legendarial']?.history as LegendarialAssessmentResult[] || [];
  const religioHistory = assessmentData['religio-traditio-cultural']?.history as ReligioTraditioCulturalAssessmentResult[] || [];
  const occupationalHistory = assessmentData['occupational']?.history as OccupationalAssessmentResult[] || [];
  const socioRationalAssessments = assessmentData['socio-relational']?.history as SocioRationalAssessmentResult[] || [];
  const financialAssessments = assessmentData['financial']?.history as FinancialAssessmentResult[] || [];
  // Rename for consistency with rest of code - pass FULL histories to getCompletedCategoryPercents
  const legendarialAssessments = legendarialHistory;
  const religioAssessments = religioHistory;
  const occupationalAssessments = occupationalHistory;
  // Get latest financial assessment and compute correct level
  const latestFinancial = financialAssessments.length > 0 ? financialAssessments[0] : null;
  let financialPercentage = 0;
  let financialLevel: 'Low' | 'Moderate' | 'High' = 'Low';
  if (latestFinancial) {
    const maxScore = 80;
    financialPercentage = latestFinancial.scores && maxScore > 0 ? Math.round((latestFinancial.scores.overall / maxScore) * 100) : 0;
    financialPercentage = clampPercentage(financialPercentage);
    financialLevel = getFinancialLevel(financialPercentage);
  }
  const environmentalAssessment = assessmentData['environmental']?.latest as EnvironmentalAssessmentResult | null;
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyType, setHistoryType] = useState<string>('');
  const [selectedHistoryCategory, setSelectedHistoryCategory] = useState<number | null>(null);
  const [showChartModal, setShowChartModal] = useState(false);
  const [chartType, setChartType] = useState<'line' | 'bar' | 'pie'>('line');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressData, setProgressData] = useState<any[]>([]);
  const [progressTitle, setProgressTitle] = useState<string>('');

  // Daily Inspiration state
  const [dailyInspiration, setDailyInspiration] = useState<string>('');
  const [isLoadingInspiration, setIsLoadingInspiration] = useState<boolean>(false);
  const [inspirationError, setInspirationError] = useState<string>('');

  // SOWB (Star of Well-Being) assessment history for sidebar card
  interface SOWBHistoryItem {
    id: string;
    createdAt: string;
    overallScore: number;
    overallLevel: string;
    percent: number;
  }
  const [sowbHistory, setSowbHistory] = useState<SOWBHistoryItem[]>([]);
  const [sowbHistoryLoading, setSowbHistoryLoading] = useState(true);
  const [sowbHistoryError, setSowbHistoryError] = useState<string | null>(null);
  const [sowbRefreshing, setSowbRefreshing] = useState(false);
  const [showSowbHistoryModal, setShowSowbHistoryModal] = useState(false);

  const ASSESSMENT_HISTORY_MAX_VISIBLE = 4;

  // Set loading to false when no cache (cache is already used for initial state; do not overwrite assessmentData here or it would replace fresh fetch with stale cache)
  useEffect(() => {
    const run = () => {
      const cached = loadFromCache(cacheKey);
      if (!cached) {
        setIsLoading(false);
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1000 });
    } else {
      setTimeout(run, 100);
    }
  }, [cacheKey]);

  // Save to cache whenever assessments change
  useEffect(() => {
    // Only cache if at least one assessment exists
    const hasAnyAssessment = Object.values(assessmentData).some(data =>
      data?.latest || data?.history?.length > 0
    );

    if (hasAnyAssessment) {
      saveToCache(cacheKey, assessmentData);
    }
  }, [assessmentData, cacheKey]);

  useEffect(() => {
    const fetchAllAssessments = async () => {
      try {
        // Clear mental health and physical assessment cache to force fresh data fetch
        const cacheKey = user?.id ? `lifepsensor_insights_cache_${user.id}` : 'lifepsensor_insights_cache';
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          // Remove mental health and physical assessments from cache to force refetch
          delete parsed['mental-health'];
          delete parsed.physical;
          localStorage.setItem(cacheKey, JSON.stringify(parsed));
          console.log('[InsightsPage] Cleared mental-health and physical assessment cache to force refetch');
        }
        
        const result = await fetchAllAssessmentData(user?.id);
        setAssessmentData(result.data);
        setHasLoadedFreshData(true);

        // Log any errors for debugging
        if (Object.keys(result.errors).length > 0) {
          console.warn('Assessment fetch errors:', result.errors);
        }
      } catch (error) {
        console.error('Error fetching assessments:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllAssessments();
  }, [user?.id]);

  // Fetch SOWB (Star of Well-Being) assessment history for sidebar card
  const fetchSowbHistory = useCallback(async (isRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setSowbHistoryLoading(false);
      return;
    }
    if (isRefresh) setSowbRefreshing(true);
    else setSowbHistoryLoading(true);
    setSowbHistoryError(null);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING}/history?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch SOWB history');
      }
      const json = await response.json();
      const raw = json?.data || [];
      const normalized: SOWBHistoryItem[] = raw.map((item: any) => {
        const overallScore = item.scores?.overall ?? 0;
        const averageScore = overallScore / 10;
        const percent = Math.round((overallScore / 40) * 100);
        const overallLevel = item.results?.overallLevel || getSowbLevelFromScore(averageScore);
        return {
          id: item.id || item._id,
          createdAt: item.createdAt,
          overallScore,
          overallLevel,
          percent
        };
      });
      setSowbHistory(normalized);
    } catch (err) {
      console.warn('Error fetching SOWB history:', err);
      setSowbHistoryError(err instanceof Error ? err.message : 'Failed to load history');
      setSowbHistory([]);
    } finally {
      if (isRefresh) setSowbRefreshing(false);
      else setSowbHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSowbHistory();
  }, [fetchSowbHistory]);

  // Refetch assessments and SOWB history when navigating to insights page
  useEffect(() => {
    if (location.pathname === '/panel/insights') {
      const timer = setTimeout(async () => {
        try {
          // Skip cache to get fresh data when returning to insights page
          const result = await fetchAllAssessmentData(user?.id, true);
          setAssessmentData(result.data);
          setHasLoadedFreshData(true);
        } catch (error) {
          console.error('Error refetching assessments:', error);
        }
        fetchSowbHistory(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, user?.id, fetchSowbHistory]);

  // Check for auto-open history parameter
  useEffect(() => {
    const autoOpenHistory = searchParams.get('showHistory');
    if (autoOpenHistory && !isLoading) {
      // Delay slightly to ensure assessments are loaded
      setTimeout(() => {
        fetchAssessmentHistory(autoOpenHistory);
        // Clean up URL parameter
        setSearchParams({});
      }, 500);
    }
  }, [isLoading, searchParams, setSearchParams]);

  // Set up auto-refresh for assessment data
  const handleAutoRefresh = useCallback(async () => {
    try {
      // Skip cache to get fresh data when auto-refreshing
      const result = await fetchAllAssessmentData(user?.id, true);
      setAssessmentData(result.data);
      // Also refresh SOWB history
      await fetchSowbHistory(true);
    } catch (error) {
      console.error('[InsightsPage] Error in auto-refresh:', error);
    }
  }, [user?.id, fetchSowbHistory]);

  useAutoRefreshAssessments({
    enabled: true,
    pollingIntervalMs: 3000, // Poll every 3 seconds for near real-time updates
    onRefresh: handleAutoRefresh
  });

  // Load daily inspiration on component mount
  useEffect(() => {
    const loadDailyInspiration = async () => {
      setIsLoadingInspiration(true);
      setInspirationError('');

      try {
        // Get both highest and lowest assessments for personalized inspiration
        const assessments = [
          { name: 'Spiritual', data: fasdlAssessment || facitAssessment, type: 'spiritual' },
          { name: 'Nutritional', data: nutritionalAssessment, type: 'nutritional' },
          { name: 'Physical', data: physicalAssessment, type: 'physical' },
          { name: 'Emotio-Psycho-Rational', data: dassAssessment || phqAssessment || gadAssessment || genderAssessment, type: 'mental-health' },
          { name: 'Financial', data: financialAssessments.length > 0 ? financialAssessments[0] : null, type: 'financial' },
          { name: 'Environmental', data: environmentalAssessment, type: 'environmental' },
          { name: 'Legendarial', data: legendarialAssessments.length > 0 ? legendarialAssessments[0] : null, type: 'legendarial' },
          { name: 'Religio-Traditio-Cultural', data: religioAssessments.length > 0 ? religioAssessments[0] : null, type: 'religio' },
          { name: 'Socio-Relational', data: socioRationalAssessments.length > 0 ? socioRationalAssessments[0] : null, type: 'socio-relational' },
          { name: 'Occupational', data: occupationalAssessments.length > 0 ? occupationalAssessments[0] : null, type: 'occupational' }
        ];

        const validAssessments = assessments.filter(({ data, type }) => {
          if (!data) return false;

          switch (type) {
            case 'spiritual':
              const spiritualData = data as SpiritualAssessmentResult;
              return spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
            case 'nutritional':
              const nutritionalData = data as NutritionalAssessmentResult;
              return nutritionalData.scores && nutritionalData.results;
            case 'physical':
              const physicalData = data as PhysicalAssessmentResult;
              return (physicalData.scores && physicalData.results) ||
                     (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results);
            case 'mental-health':
              return dassAssessment || phqAssessment || gadAssessment || genderAssessment;
            case 'financial':
              const financialData = data as FinancialAssessmentResult;
              return financialData.scores && financialData.results;
            case 'environmental':
              const environmentalData = data as EnvironmentalAssessmentResult;
              return environmentalData.scores && environmentalData.results;
            case 'legendarial':
              const legendarialData = data as LegendarialAssessmentResult;
              return legendarialData.scores && legendarialData.results;
            case 'religio':
              const religioData = data as ReligioTraditioCulturalAssessmentResult;
              return religioData.scores && religioData.results;
            case 'socio-relational':
              const socioData = data as SocioRationalAssessmentResult;
              return (socioData.scores && socioData.results) ||
                     (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results);
            case 'occupational':
              const occupationalData = data as OccupationalAssessmentResult;
              return occupationalData.scores && occupationalData.results;
            default:
              return false;
          }
        });

        if (validAssessments.length >= 2) {
          // Calculate percentages using the same logic as the chart
          const assessmentsWithPercentages = validAssessments.map(({ name, data, type }) => {
            let percentage = 0;

            switch (type) {
              case 'spiritual':
                const spiritualData = data as SpiritualAssessmentResult;
                const fasdlCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
                const facitCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;

                if (fasdlCompleted && facitCompleted) {
                  // Use the actual FASDL and FACIT assessment objects for correct scores
                  const fasdlCompletedObj = typeof fasdlAssessment !== 'undefined' && fasdlAssessment && fasdlAssessment.assessmentData && fasdlAssessment.assessmentData.scores && fasdlAssessment.assessmentData.results;
                  const facitCompletedObj = typeof facitAssessment !== 'undefined' && facitAssessment && facitAssessment.assessmentData && facitAssessment.assessmentData.scores && facitAssessment.assessmentData.results;
                  let fasdlScore = fasdlCompletedObj ? fasdlAssessment.assessmentData.scores.overall : null;
                  let facitScore = facitCompletedObj ? facitAssessment.assessmentData.scores.overall : null;
                  let fasdlPercentage = fasdlScore !== null ? Math.round(clampPercentage((fasdlScore / 50) * 100)) : null;
                  let facitPercentage = facitScore !== null ? Math.round(clampPercentage((facitScore / 48) * 100)) : null;
                  if (fasdlPercentage !== null && facitPercentage !== null) {
                    percentage = Math.min(100, Math.round((fasdlPercentage + facitPercentage) / 2));
                  } else if (fasdlPercentage !== null) {
                    percentage = Math.min(100, fasdlPercentage);
                  } else if (facitPercentage !== null) {
                    percentage = Math.min(100, facitPercentage);
                  } else {
                    percentage = 0;
                  }
                } else if (fasdlCompleted) {
                  percentage = clampPercentage(Math.round((spiritualData.assessmentData.scores.overall / 50) * 100));
                } else if (facitCompleted) {
                  percentage = clampPercentage(Math.round((spiritualData.assessmentData.scores.overall / 48) * 100));
                }
                break;

              case 'mental-health':
                // Rubric-aligned: use highest DASS-21 subscale, PHQ-9/27, GAD-7/21, Gender/20, average only latest completed per subcategory
                // Only use the latest assessment for each subcategory
                const getLatestByType = (historyArr: any[] | undefined, type: number): any | null => {
                  if (!historyArr) return null;
                  const filtered = historyArr.filter((a: any) => a.selectedCategory === type);
                  if (filtered.length === 0) return null;
                  filtered.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                  if (filtered.length > 1) {
                    console.log(`[DEBUG] Subcategory ${type} assessment dates:`, filtered.map(f => f.createdAt));
                  }
                  const latest = filtered[0];
                  if (latest) {
                    let raw = null, percent = null;
                    if (type === 1 && latest.scores) {
                      // Use total score for DASS-21 (not subscales)
                      raw = latest.scores.overall;
                      percent = Math.round(clampPercentage((raw / 42) * 100));
                    } else if (type === 2 && latest.scores) {
                      raw = latest.scores.overall;
                      percent = Math.round(clampPercentage((raw / 27) * 100));
                    } else if (type === 3 && latest.scores) {
                      raw = latest.scores.overall;
                      percent = Math.round(clampPercentage((raw / 21) * 100));
                    } else if (type === 4 && latest.scores) {
                      raw = latest.scores.overall;
                      // Invert: higher raw means lower percent
                      percent = Math.round(clampPercentage(100 - ((raw / 20) * 100)));
                    }
                    console.log(`[DEBUG] Subcategory ${type} latest assessment:`, { createdAt: latest.createdAt, raw, percent });
                  }
                  return latest;
                };
                const mhHistory = (assessmentData['mental-health']?.history || []).filter((item: any) => typeof item.selectedCategory === 'number');
                const dassLatest = getLatestByType(mhHistory, 1);
                const phqLatest = getLatestByType(mhHistory, 2);
                const gadLatest = getLatestByType(mhHistory, 3);
                const genderLatest = getLatestByType(mhHistory, 4);

                // Always build the array in the order: DASS-21, PHQ-9, GAD-7, Gender Sensitivity
                let dassRaw = null, phqRaw = null, gadRaw = null, genderRaw = null;
                let dassPercent = null, phqPercent = null, gadPercent = null, genderPercent = null;
                if (dassLatest && dassLatest.scores) {
                  // Use total score for DASS-21
                  dassRaw = dassLatest.scores.overall ?? null;
                  if (dassRaw !== null) {
                    dassPercent = Math.round(clampPercentage((dassRaw / 42) * 100));
                  }
                }
                if (phqLatest && phqLatest.scores) {
                  phqRaw = phqLatest.scores.overall;
                  phqPercent = Math.round(clampPercentage((phqRaw / 27) * 100));
                }
                if (gadLatest && gadLatest.scores) {
                  gadRaw = gadLatest.scores.overall;
                  gadPercent = Math.round(clampPercentage((gadRaw / 21) * 100));
                }
                if (genderLatest && genderLatest.scores) {
                  genderRaw = genderLatest.scores.overall;
                  // Invert: higher raw means lower percent
                  genderPercent = Math.round(clampPercentage(100 - ((genderRaw / 20) * 100)));
                }
                // Only average the assessments that were actually taken (don't include untaken as 0%)
                const percentages = [];
                if (dassPercent !== null) percentages.push(dassPercent);
                if (phqPercent !== null) percentages.push(phqPercent);
                if (gadPercent !== null) percentages.push(gadPercent);
                if (genderPercent !== null) percentages.push(genderPercent);
                console.log('[DEBUG] Emotio-Psycho-Rational-Assessment raw and percent:', {
                  dass: { raw: dassRaw, percent: dassPercent },
                  phq: { raw: phqRaw, percent: phqPercent },
                  gad: { raw: gadRaw, percent: gadPercent },
                  gender: { raw: genderRaw, percent: genderPercent },
                  takenCount: percentages.length
                });

                if (percentages.length > 0) {
                  console.log('[DEBUG] Emotio-Psycho-Rational-Assessment percentages used:', {
                    dass: dassPercent,
                    phq: phqPercent,
                    gad: gadPercent,
                    gender: genderPercent,
                    percentages: percentages
                  });
                  percentage = Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length);
                  percentage = clampPercentage(percentage);
                  // Set level/interpretation based on average percentage
                  let level = 'Low';
                  if (percentage >= 70) level = 'High';
                  else if (percentage >= 40) level = 'Moderate';
                  // Attach to the main assessment object for rendering if needed
                  if (dassLatest) {
                    dassLatest.results = dassLatest.results || {};
                    dassLatest.results.level = level as 'Low' | 'Moderate' | 'High';
                  }
                  if (phqLatest) {
                    phqLatest.results = phqLatest.results || {};
                    phqLatest.results.level = level as 'Low' | 'Moderate' | 'High';
                  }
                  if (gadLatest) {
                    gadLatest.results = gadLatest.results || {};
                    gadLatest.results.level = level as 'Low' | 'Moderate' | 'High';
                  }
                  if (genderLatest) {
                    genderLatest.results = genderLatest.results || {};
                    genderLatest.results.level = level as 'Low' | 'Moderate' | 'High';
                  }
                }
                break;

              case 'legendarial':
                const legendarialData = data as LegendarialAssessmentResult;
                const swlsAssessment = legendarialAssessments.find(a => a.selectedCategory === 1);
                const pilAssessment = legendarialAssessments.find(a => a.selectedCategory === 2);

                if (swlsAssessment && pilAssessment) {
                          const swlsPercentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                          const pilPercentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                          percentage = clampPercentage(Math.round((swlsPercentage + pilPercentage) / 2));
                } else if (swlsAssessment) {
                  percentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                } else if (pilAssessment) {
                  percentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                }
                break;

              case 'religio':
                const religioData = data as ReligioTraditioCulturalAssessmentResult;
                const briefRcopeAssessment = religioAssessments.find(a => a.selectedCategory === 1);
                const cvscaleAssessment = religioAssessments.find(a => a.selectedCategory === 2);
                const tvasAssessment = religioAssessments.find(a => a.selectedCategory === 3);

                      const religioPercentages = [];
                      if (briefRcopeAssessment) religioPercentages.push(Math.round(clampPercentage(((briefRcopeAssessment.scores.category1 || briefRcopeAssessment.scores.prcTotal || 0) - (briefRcopeAssessment.scores.nrcTotal || 0) + 21) / 42 * 100)));
                      if (cvscaleAssessment) religioPercentages.push(Math.round(clampPercentage(((cvscaleAssessment.scores.cvscaleTotal || 0) / 5) * 100)));
                      if (tvasAssessment) religioPercentages.push(Math.round(clampPercentage(((tvasAssessment.scores.respectForTraditionTotal || 0) / 50) * 100)));                if (religioPercentages.length > 0) {
                  percentage = Math.round(religioPercentages.reduce((sum, p) => sum + p, 0) / religioPercentages.length);
                }
                break;

              case 'occupational':
                const occupationalData = data as OccupationalAssessmentResult;
                // Use only the latest for each subcategory
                const cbiAssessment = occupationalAssessments
                  .filter(a => a.selectedCategory === 1)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                const uwesAssessment = occupationalAssessments
                  .filter(a => a.selectedCategory === 2)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                const employeeEngagementAssessment = occupationalAssessments
                  .filter(a => a.selectedCategory === 3)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

                const occupationalPercentages = [];
                // FIXED: UWES max=6, Employee Engagement max=5, CBI max=100 - 2025-01-27
                if (cbiAssessment) occupationalPercentages.push(Math.round(clampPercentage(((cbiAssessment.scores.cbiTotal || 0) / 100) * 100)));
                if (uwesAssessment) occupationalPercentages.push(Math.round(clampPercentage((1 - ((uwesAssessment.scores.uwesTotal || 0) / 6)) * 100)));
                if (employeeEngagementAssessment) occupationalPercentages.push(Math.round(clampPercentage(((5 - (employeeEngagementAssessment.scores.engagementTotal || 0)) / 5) * 100)));

                if (occupationalPercentages.length > 0) {
                  percentage = Math.round(occupationalPercentages.reduce((sum, p) => sum + p, 0) / occupationalPercentages.length);
                }
                break;

              case 'nutritional':
                const nutritionalData = data as NutritionalAssessmentResult;
                if (nutritionalData.scores && nutritionalData.results) {
                  percentage = Math.round(clampPercentage((nutritionalData.scores.overall / 100) * 100));
                }
                break;

              case 'physical':
                const physicalData = data as PhysicalAssessmentResult;
                let physicalScores = physicalData.scores || physicalData.assessmentData?.scores;
                let physicalResults = physicalData.results || physicalData.assessmentData?.results;
                
                console.log('[InsightsPage Physical] Full data:', physicalData);
                console.log('[InsightsPage Physical] Scores object:', physicalScores);
                
                if (physicalScores && physicalResults) {
                  // Calculate average of rounded IPAQ-SF and PSQI categories
                  const categories = physicalScores.categories || [];
                  console.log('[InsightsPage Physical] Categories array:', categories);
                  
                  const ipaqCategory = categories.find((c: any) => c.name === 'IPAQ-SF');
                  const psqiCategory = categories.find((c: any) => c.name === 'PSQI');
                  
                  console.log('[InsightsPage Physical] IPAQ Category:', ipaqCategory);
                  console.log('[InsightsPage Physical] PSQI Category:', psqiCategory);
                  
                  const roundedCatScores = [];
                  if (ipaqCategory && typeof ipaqCategory.score === 'number') {
                    const roundedScore = Math.round(ipaqCategory.score);
                    console.log('[InsightsPage Physical] Adding IPAQ score:', roundedScore);
                    roundedCatScores.push(roundedScore);
                  }
                  if (psqiCategory && typeof psqiCategory.score === 'number') {
                    const roundedScore = Math.round(psqiCategory.score);
                    console.log('[InsightsPage Physical] Adding PSQI score:', roundedScore);
                    roundedCatScores.push(roundedScore);
                  }
                  
                  console.log('[InsightsPage Physical] Rounded scores array:', roundedCatScores);
                  
                  if (roundedCatScores.length > 0) {
                    percentage = Math.round(roundedCatScores.reduce((a: number, b: number) => a + b, 0) / roundedCatScores.length);
                    console.log('[InsightsPage Physical] Calculated average percentage:', percentage);
                  } else {
                    console.log('[InsightsPage Physical] No valid category scores found, using overall:', physicalScores.overall);
                    percentage = physicalScores.overall || 0;
                  }
                }
                break;

              case 'financial':
                const financialData = data as FinancialAssessmentResult;
                if (financialData.scores && financialData.results) {
                  const rawScore = financialData.scores?.overall || financialData.scores?.categoryScore || 0;
                  percentage = Math.round(clampPercentage((rawScore / 80) * 100));
                }
                break;

              case 'environmental':
                const environmentalData = data as EnvironmentalAssessmentResult;
                if (environmentalData.scores && environmentalData.results) {
                  percentage = Math.round(clampPercentage((environmentalData.scores.transformedScore / 100) * 100));
                }
                break;

              case 'socio-relational':
                const socioData = data as SocioRationalAssessmentResult;
                // MSPSS: 12 items, 1–7 scale, max total = 84, mean = 7. But we use socioRelationalScore which is mean (1-7)
                // The scoring logic uses max=20, so we divide by 20 to get percentage
                if (socioData.scores && socioData.results) {
                  percentage = Math.round(clampPercentage((socioData.scores.socioRelationalScore / 20) * 100));
                } else if (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results) {
                  percentage = Math.round(clampPercentage((socioData.assessmentData.scores.socioRelationalScore / 20) * 100));
                }
                break;
            }

            return { name, data, type, percentage };
          }).filter(item => item.percentage > 0 && item.percentage <= 100);

          if (assessmentsWithPercentages.length === 1) {
            // Clamp the single percentage value
            assessmentsWithPercentages[0].percentage = clampPercentage(assessmentsWithPercentages[0].percentage);
          }
          if (assessmentsWithPercentages.length >= 2) {
            const sortedAssessments = assessmentsWithPercentages.sort((a, b) => b.percentage - a.percentage);
            const highest = sortedAssessments[0];
            const lowest = sortedAssessments[sortedAssessments.length - 1];

            const inspiration = generateAIInspiration(lowest);
            setDailyInspiration(inspiration);
          }
        }
      } catch (error) {
        console.error('Error loading daily inspiration:', error);
        setInspirationError('Failed to load inspiration');
      } finally {
        setIsLoadingInspiration(false);
      }
    };

    loadDailyInspiration();
  }, []);

  // Check for twice-daily updates (12:00 AM/PM)
  useEffect(() => {
    const checkTimeAndUpdate = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Check if it's 12:00 AM/PM (±5 minutes for flexibility)
      if ((currentHour === 0 || currentHour === 12) && currentMinute <= 5) {
        const lastUpdateKey = `daily_inspiration_last_update_${user?.id}`;
        const lastUpdate = localStorage.getItem(lastUpdateKey);
        const today = new Date().toDateString();

        if (!lastUpdate || lastUpdate !== today) {
          // Update daily inspiration
          const loadNewInspiration = async () => {
            setIsLoadingInspiration(true);
            try {
              // Get both highest and lowest assessments for personalized inspiration
              const assessments = [
                { name: 'Spiritual', data: fasdlAssessment || facitAssessment, type: 'spiritual' },
                { name: 'Nutritional', data: nutritionalAssessment, type: 'nutritional' },
                { name: 'Physical', data: physicalAssessment, type: 'physical' },
                { name: 'Emotio-Psycho-Rational', data: dassAssessment || phqAssessment || gadAssessment || genderAssessment, type: 'mental-health' },
                { name: 'Financial', data: financialAssessments.length > 0 ? financialAssessments[0] : null, type: 'financial' },
                { name: 'Environmental', data: environmentalAssessment, type: 'environmental' },
                { name: 'Legendarial', data: legendarialAssessments.length > 0 ? legendarialAssessments[0] : null, type: 'legendarial' },
                { name: 'Religio-Traditio-Cultural', data: religioAssessments.length > 0 ? religioAssessments[0] : null, type: 'religio' },
                { name: 'Socio-Relational', data: socioRationalAssessments.length > 0 ? socioRationalAssessments[0] : null, type: 'socio-relational' },
                { name: 'Occupational', data: occupationalAssessments.length > 0 ? occupationalAssessments[0] : null, type: 'occupational' }
              ];

              const validAssessments = assessments.filter(({ data, type }) => {
                if (!data) return false;

                switch (type) {
                  case 'spiritual':
                    const spiritualData = data as SpiritualAssessmentResult;
                    return spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
                  case 'nutritional':
                    const nutritionalData = data as NutritionalAssessmentResult;
                    return nutritionalData.scores && nutritionalData.results;
                  case 'physical':
                    const physicalData = data as PhysicalAssessmentResult;
                    return (physicalData.scores && physicalData.results) ||
                           (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results);
                  case 'mental-health':
                    return dassAssessment || phqAssessment || gadAssessment || genderAssessment;
                  case 'financial':
                    const financialData = data as FinancialAssessmentResult;
                    return financialData.scores && financialData.results;
                  case 'environmental':
                    const environmentalData = data as EnvironmentalAssessmentResult;
                    return environmentalData.scores && environmentalData.results;
                  case 'legendarial':
                    const legendarialData = data as LegendarialAssessmentResult;
                    return legendarialData.scores && legendarialData.results;
                  case 'religio':
                    const religioData = data as ReligioTraditioCulturalAssessmentResult;
                    return religioData.scores && religioData.results;
                  case 'socio-relational':
                    const socioData = data as SocioRationalAssessmentResult;
                    return (socioData.scores && socioData.results) ||
                           (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results);
                  case 'occupational':
                    const occupationalData = data as OccupationalAssessmentResult;
                    return occupationalData.scores && occupationalData.results;
                  default:
                    return false;
                }
              });

              if (validAssessments.length >= 2) {
                // Calculate percentages using the same logic as the chart
                const assessmentsWithPercentages = validAssessments.map(({ name, data, type }) => {
                  let percentage = 0;

                  switch (type) {
                    case 'spiritual':
                      const spiritualData = data as SpiritualAssessmentResult;
                      const fasdlCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
                      const facitCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;

                      if (fasdlCompleted && facitCompleted) {
                        const fasdlPercentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 50) * 100));
                        const facitPercentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 48) * 100));
                        percentage = Math.round((fasdlPercentage + facitPercentage) / 2);
                      } else if (fasdlCompleted) {
                        percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 50) * 100));
                      } else if (facitCompleted) {
                        percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 48) * 100));
                      }
                      break;

                    case 'mental-health':
                      const dassCompleted = dassAssessment && dassAssessment.scores && dassAssessment.results;
                      const phqCompleted = phqAssessment && phqAssessment.scores && phqAssessment.results;
                      const gadCompleted = gadAssessment && gadAssessment.scores && gadAssessment.results;
                      const genderCompleted = genderAssessment && genderAssessment.scores && genderAssessment.results;

                      const percentages = [];
                      if (dassCompleted) {
                        const subscales = dassAssessment.scores.subscales || {};
                        const highest = Math.max(Number((subscales as any).depression ?? 0), Number((subscales as any).anxiety ?? 0), Number((subscales as any).stress ?? 0));
                        percentages.push(Math.round(clampPercentage((highest / 42) * 100)));
                      }
                      if (phqCompleted) {
                        // For PHQ-9, higher score = higher percentage (see provided table)
                        const phqScore = phqAssessment.scores.overall || 0;
                        const phqPercent = Math.round(clampPercentage((phqScore / 27) * 100));
                        percentages.push(phqPercent);
                        // Attach PHQ-9 level for insights interpretation
                        let phqLevel = 'Low';
                        if (phqPercent >= 52) phqLevel = 'High';
                        else if (phqPercent >= 15) phqLevel = 'Moderate';
                        // Attach to assessment object if needed for rendering
                        phqAssessment.results = phqAssessment.results || {};
                        phqAssessment.results.level = phqLevel as 'Low' | 'Moderate' | 'High';
                      }
                      if (gadCompleted) percentages.push(Math.round(clampPercentage((gadAssessment.scores.overall / 21) * 100)));
                      if (genderCompleted) percentages.push(Math.round(clampPercentage((genderAssessment.scores.overall / 20) * 100)));

                      if (percentages.length > 0) {
                        percentage = Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length);
                      }
                      break;

                    case 'legendarial':
                      const legendarialData = data as LegendarialAssessmentResult;
                      const swlsAssessment = legendarialAssessments.find(a => a.selectedCategory === 1);
                      const pilAssessment = legendarialAssessments.find(a => a.selectedCategory === 2);

                      if (swlsAssessment && pilAssessment) {
                          const swlsPercentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                          const pilPercentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                        percentage = Math.round((swlsPercentage + pilPercentage) / 2);
                      } else if (swlsAssessment) {
                        percentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                      } else if (pilAssessment) {
                        percentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                      }
                      break;

                    case 'religio':
                      const religioData = data as ReligioTraditioCulturalAssessmentResult;
                      const briefRcopeAssessment = religioAssessments.find(a => a.selectedCategory === 1);
                      const cvscaleAssessment = religioAssessments.find(a => a.selectedCategory === 2);
                      const tvasAssessment = religioAssessments.find(a => a.selectedCategory === 3);

                      const religioPercentages = [];
                if (briefRcopeAssessment) religioPercentages.push(Math.round(clampPercentage(((briefRcopeAssessment.scores.category1 || briefRcopeAssessment.scores.prcTotal || 0) - (briefRcopeAssessment.scores.nrcTotal || 0) + 21) / 42 * 100)));
                      if (cvscaleAssessment) religioPercentages.push(Math.round(clampPercentage(((cvscaleAssessment.scores.cvscaleTotal || 0) / 5) * 100)));
                      if (tvasAssessment) religioPercentages.push(Math.round(clampPercentage(((tvasAssessment.scores.respectForTraditionTotal || 0) / 50) * 100)));

                      if (religioPercentages.length > 0) {
                        percentage = clampPercentage(Math.round(religioPercentages.reduce((sum, p) => sum + p, 0) / religioPercentages.length));
                      }
                      break;

                    case 'occupational':
                      const occupationalData = data as OccupationalAssessmentResult;
                      // Use only the latest for each subcategory
                      const cbiAssessment = occupationalAssessments
                        .filter(a => a.selectedCategory === 1)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                      const uwesAssessment = occupationalAssessments
                        .filter(a => a.selectedCategory === 2)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
                      const employeeEngagementAssessment = occupationalAssessments
                        .filter(a => a.selectedCategory === 3)
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

                      const occupationalPercentages = [];
                      // CORRECTED SCORING: CBI=100, UWES=6, Employee Engagement=5
                      if (cbiAssessment) occupationalPercentages.push(Math.round(clampPercentage(((cbiAssessment.scores.cbiTotal || 0) / 100) * 100)));
                      if (uwesAssessment) occupationalPercentages.push(Math.round(clampPercentage((1 - ((uwesAssessment.scores.uwesTotal || 0) / 6)) * 100)));
                      if (employeeEngagementAssessment) occupationalPercentages.push(Math.round(clampPercentage(((5 - (employeeEngagementAssessment.scores.engagementTotal || 0)) / 5) * 100)));

                      if (occupationalPercentages.length > 0) {
                        percentage = clampPercentage(Math.round(occupationalPercentages.reduce((sum, p) => sum + p, 0) / occupationalPercentages.length));
                      }
                      break;

                    case 'nutritional':
                      const nutritionalData = data as NutritionalAssessmentResult;
                      if (nutritionalData.scores && nutritionalData.results) {
                        percentage = Math.round(clampPercentage((nutritionalData.scores.overall / 100) * 100));
                      }
                      break;

                    case 'physical':
                      const physicalData = data as PhysicalAssessmentResult;
                      if (physicalData.scores && physicalData.results) {
                        percentage = Math.round(clampPercentage((physicalData.scores.overall / 100) * 100));
                      } else if (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results) {
                        percentage = Math.round(clampPercentage((physicalData.assessmentData.scores.overall / 100) * 100));
                      }
                      break;

                    case 'financial':
                      const financialData = data as FinancialAssessmentResult;
                      if (financialData.scores && financialData.results) {
                        const rawScore = financialData.scores.overall || financialData.scores.categoryScore || 0;
                        percentage = Math.round(clampPercentage((rawScore / 80) * 100));
                      }
                      break;

                    case 'environmental':
                      const environmentalData = data as EnvironmentalAssessmentResult;
                      if (environmentalData.scores && environmentalData.results) {
                        percentage = Math.round(clampPercentage((environmentalData.scores.transformedScore / 100) * 100));
                      }
                      break;

                    case 'socio-relational':
                      const socioData = data as SocioRationalAssessmentResult;
                      if (socioData.scores && socioData.results) {
                        percentage = Math.round(clampPercentage((socioData.scores.socioRelationalScore / 20) * 100));
                      } else if (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results) {
                        percentage = Math.round(clampPercentage((socioData.assessmentData.scores.socioRelationalScore / 20) * 100));
                      }
                      break;
                  }

                  return { name, data, type, percentage };
                }).filter(item => item.percentage > 0);

                if (assessmentsWithPercentages.length >= 2) {
                  const sortedAssessments = assessmentsWithPercentages.sort((a, b) => b.percentage - a.percentage);
                  const highest = sortedAssessments[0];
                  const lowest = sortedAssessments[sortedAssessments.length - 1];

                  const inspiration = generateAIInspiration(lowest);
                  setDailyInspiration(inspiration);
                  localStorage.setItem(lastUpdateKey, today);
                }
              }
            } catch (error) {
              console.error('Error updating daily inspiration:', error);
            } finally {
              setIsLoadingInspiration(false);
            }
          };

          loadNewInspiration();
        }
      }
    };

    // Check immediately and then every minute
    checkTimeAndUpdate();
    const interval = setInterval(checkTimeAndUpdate, 60000);

    return () => clearInterval(interval);
  }, [user?.id]);

  // Manual refresh function
  const refreshInspiration = async () => {
    setIsLoadingInspiration(true);
    setInspirationError('');

    try {
      // Get both highest and lowest assessments for personalized inspiration
      const assessments = [
        { name: 'Spiritual', data: fasdlAssessment || facitAssessment, type: 'spiritual' },
        { name: 'Nutritional', data: nutritionalAssessment, type: 'nutritional' },
        { name: 'Physical', data: physicalAssessment, type: 'physical' },
        { name: 'Emotio-Psycho-Rational', data: dassAssessment || phqAssessment || gadAssessment || genderAssessment, type: 'mental-health' },
        { name: 'Financial', data: financialAssessments.length > 0 ? financialAssessments[0] : null, type: 'financial' },
        { name: 'Environmental', data: environmentalAssessment, type: 'environmental' },
        { name: 'Legendarial', data: legendarialAssessments.length > 0 ? legendarialAssessments[0] : null, type: 'legendarial' },
        { name: 'Religio-Traditio-Cultural', data: religioAssessments.length > 0 ? religioAssessments[0] : null, type: 'religio' },
        { name: 'Socio-Relational', data: socioRationalAssessments.length > 0 ? socioRationalAssessments[0] : null, type: 'socio-relational' },
        { name: 'Occupational', data: occupationalAssessments.length > 0 ? occupationalAssessments[0] : null, type: 'occupational' }
      ];

      const validAssessments = assessments.filter(({ data, type }) => {
        if (!data) return false;

        switch (type) {
          case 'spiritual':
            const spiritualData = data as SpiritualAssessmentResult;
            return spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
          case 'nutritional':
            const nutritionalData = data as NutritionalAssessmentResult;
            return nutritionalData.scores && nutritionalData.results;
          case 'physical':
            const physicalData = data as PhysicalAssessmentResult;
            return (physicalData.scores && physicalData.results) ||
                   (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results);
          case 'mental-health':
            return dassAssessment || phqAssessment || gadAssessment || genderAssessment;
          case 'financial':
            const financialData = data as FinancialAssessmentResult;
            return financialData.scores && financialData.results;
          case 'environmental':
            const environmentalData = data as EnvironmentalAssessmentResult;
            return environmentalData.scores && environmentalData.results;
          case 'legendarial':
            const legendarialData = data as LegendarialAssessmentResult;
            return legendarialData.scores && legendarialData.results;
          case 'religio':
            const religioData = data as ReligioTraditioCulturalAssessmentResult;
            return religioData.scores && religioData.results;
          case 'socio-relational':
            const socioData = data as SocioRationalAssessmentResult;
            return (socioData.scores && socioData.results) ||
                   (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results);
          case 'occupational':
            const occupationalData = data as OccupationalAssessmentResult;
            return occupationalData.scores && occupationalData.results;
          default:
            return false;
        }
      });

      if (validAssessments.length >= 2) {
        // Calculate percentages using the same logic as the chart
        const assessmentsWithPercentages = validAssessments.map(({ name, data, type }) => {
          let percentage = 0;

          switch (type) {
            case 'spiritual':
              const spiritualData = data as SpiritualAssessmentResult;
              const fasdlCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
              const facitCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;

              if (fasdlCompleted && facitCompleted) {
                const fasdlPercentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 50) * 100));
                const facitPercentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 48) * 100));
                percentage = Math.round((fasdlPercentage + facitPercentage) / 2);
              } else if (fasdlCompleted) {
                percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 50) * 100));
              } else if (facitCompleted) {
                percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 48) * 100));
              }
              break;

                    case 'mental-health':
                      const dassCompleted = dassAssessment && dassAssessment.scores && dassAssessment.results;
                      const phqCompleted = phqAssessment && phqAssessment.scores && phqAssessment.results;
                      const gadCompleted = gadAssessment && gadAssessment.scores && gadAssessment.results;
                      const genderCompleted = genderAssessment && genderAssessment.scores && genderAssessment.results;

                      const dassPercentDI = (dassCompleted && dassAssessment.scores)
                        ? Math.round(clampPercentage((dassAssessment.scores.overall / 42) * 100))
                        : null;
                      const phqPercentDI = (phqCompleted && phqAssessment.scores)
                        ? Math.round(clampPercentage((phqAssessment.scores.overall / 27) * 100))
                        : null;
                      const gadPercentDI = (gadCompleted && gadAssessment.scores)
                        ? Math.round(clampPercentage((gadAssessment.scores.overall / 21) * 100))
                        : null;
                      const genderPercentDI = (genderCompleted && genderAssessment.scores)
                        ? Math.round(clampPercentage(100 - ((genderAssessment.scores.overall / 20) * 100)))
                        : null;
                      const percentagesDI = [
                        dassPercentDI !== null ? dassPercentDI : 0,
                        phqPercentDI !== null ? phqPercentDI : 0,
                        gadPercentDI !== null ? gadPercentDI : 0,
                        genderPercentDI !== null ? genderPercentDI : 0
                      ];
                      if (percentagesDI.length > 0) {
                        percentage = Math.round(percentagesDI.reduce((sum, p) => sum + p, 0) / percentagesDI.length);
                      }
                      break;            case 'legendarial':
              const legendarialData = data as LegendarialAssessmentResult;
              const swlsAssessment = legendarialAssessments.find(a => a.selectedCategory === 1);
              const pilAssessment = legendarialAssessments.find(a => a.selectedCategory === 2);

              if (swlsAssessment && pilAssessment) {
                          const swlsPercentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                          const pilPercentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                percentage = Math.round((swlsPercentage + pilPercentage) / 2);
              } else if (swlsAssessment) {
                percentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
              } else if (pilAssessment) {
                percentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
              }
              break;

            case 'religio':
              const religioData = data as ReligioTraditioCulturalAssessmentResult;
              const briefRcopeAssessment = religioAssessments.find(a => a.selectedCategory === 1);
              const cvscaleAssessment = religioAssessments.find(a => a.selectedCategory === 2);
              const tvasAssessment = religioAssessments.find(a => a.selectedCategory === 3);

              const religioPercentages = [];
              if (briefRcopeAssessment) religioPercentages.push(Math.round(clampPercentage(((briefRcopeAssessment.scores.prcTotal || 0) - (briefRcopeAssessment.scores.nrcTotal || 0) + 21) / 42 * 100)));
              if (cvscaleAssessment) religioPercentages.push(Math.round(clampPercentage(((cvscaleAssessment.scores.cvscaleTotal || 0) / 5) * 100)));
              if (tvasAssessment) religioPercentages.push(Math.round(clampPercentage(((tvasAssessment.scores.respectForTraditionTotal || 0) / 50) * 100)));

              if (religioPercentages.length > 0) {
                  percentage = clampPercentage(Math.round(religioPercentages.reduce((sum, p) => sum + p, 0) / religioPercentages.length));
              }
              break;

            case 'occupational':
              const occupationalData = data as OccupationalAssessmentResult;
              // Use only the latest for each subcategory
              const cbiAssessment = occupationalAssessments
                .filter(a => a.selectedCategory === 1)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              const uwesAssessment = occupationalAssessments
                .filter(a => a.selectedCategory === 2)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              const employeeEngagementAssessment = occupationalAssessments
                .filter(a => a.selectedCategory === 3)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

              const occupationalPercentages = [];
              if (cbiAssessment) occupationalPercentages.push(Math.round(clampPercentage(((cbiAssessment.scores.cbiTotal || 0) / 100) * 100)));
              if (uwesAssessment) occupationalPercentages.push(Math.round(clampPercentage((1 - ((uwesAssessment.scores.uwesTotal || 0) / 6)) * 100)));
              if (employeeEngagementAssessment) occupationalPercentages.push(Math.round(clampPercentage(((5 - (employeeEngagementAssessment.scores.engagementTotal || 0)) / 5) * 100)));

              if (occupationalPercentages.length > 0) {
                  percentage = clampPercentage(Math.round(occupationalPercentages.reduce((sum, p) => sum + p, 0) / occupationalPercentages.length));
              }
              break;

            case 'nutritional':
              const nutritionalData = data as NutritionalAssessmentResult;
              if (nutritionalData.scores && nutritionalData.results) {
                percentage = Math.round(clampPercentage((nutritionalData.scores.overall / 100) * 100));
              }
              break;

            case 'physical':
              const physicalData = data as PhysicalAssessmentResult;
              if (physicalData.scores && physicalData.results) {
                percentage = Math.round(clampPercentage((physicalData.scores.overall / 100) * 100));
              } else if (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results) {
                percentage = Math.round(clampPercentage((physicalData.assessmentData.scores.overall / 100) * 100));
              }
              break;

            case 'financial':
              const financialData = data as FinancialAssessmentResult;
              if (financialData.scores && financialData.results) {
                const rawScore = financialData.scores?.overall || financialData.scores?.categoryScore || 0;
                percentage = Math.round(clampPercentage((rawScore / 80) * 100));
              }
              break;

            case 'environmental':
              const environmentalData = data as EnvironmentalAssessmentResult;
              if (environmentalData.scores && environmentalData.results) {
                percentage = Math.round(clampPercentage((environmentalData.scores.transformedScore / 100) * 100));
              }
              break;

            case 'socio-relational':
              const socioData = data as SocioRationalAssessmentResult;
              if (socioData.scores && socioData.results) {
                percentage = Math.round(clampPercentage((socioData.scores.socioRelationalScore / 20) * 100));
              } else if (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results) {
                percentage = Math.round(clampPercentage((socioData.assessmentData.scores.socioRelationalScore / 20) * 100));
              }
              break;
          }

          return { name, data, type, percentage };
        }).filter(item => item.percentage > 0);

        if (assessmentsWithPercentages.length >= 2) {
          const sortedAssessments = assessmentsWithPercentages.sort((a, b) => b.percentage - a.percentage);
          const highest = sortedAssessments[0];
          const lowest = sortedAssessments[sortedAssessments.length - 1];

          const inspiration = generateAIInspiration(lowest);
          setDailyInspiration(inspiration);
        }
      }
    } catch (error) {
      console.error('Error refreshing inspiration:', error);
      setInspirationError('Failed to refresh inspiration');
    } finally {
      setIsLoadingInspiration(false);
    }
  };

  // Compile all assessment history from all 10 assessment types
  const fetchAllAssessmentHistory = () => {
    const allHistoryItems: any[] = [];

    // Helper function to get test name
    const getTestName = (assessmentType: string, category?: number): string => {
      switch (assessmentType) {
        case 'spiritual':
          return category === 1 ? 'FASDL' : 'FACIT-Sp-12';
        case 'nutritional':
          return 'Nutritional';
        case 'physical':
          return 'Physical';
        case 'mental-health':
          if (category === 1) return 'DASS-21';
          if (category === 2) return 'PHQ-9';
          if (category === 3) return 'GAD-7';
          if (category === 4) return 'Gender Sensitivity';
          return 'Mental Health';
        case 'legendarial':
          return category === 1 ? 'SWLS' : 'PIL';
        case 'religio-traditio-cultural':
          if (category === 1) return 'Brief RCOPE';
          if (category === 2) return 'CV Scale';
          if (category === 3) return 'TVAS';
          return 'Religio-Traditio-Cultural';
        case 'socio-relational':
          return 'Socio-Relational';
        case 'financial':
          return 'Financial';
        case 'environmental':
          return 'Environmental';
        case 'occupational':
          if (category === 1) return 'CBI';
          if (category === 2) return 'UWES';
          if (category === 3) return 'Employee Engagement';
          return 'Occupational';
        default:
          return 'Assessment';
      }
    };

    // Iterate through each assessment type
    const assessmentTypes: AssessmentType[] = ['spiritual', 'nutritional', 'physical', 'mental-health', 'legendarial', 'religio-traditio-cultural', 'socio-relational', 'financial', 'environmental', 'occupational'];

    for (const assessmentType of assessmentTypes) {
      const assessment = assessmentData[assessmentType];
      if (!assessment || !assessment.history) continue;

      if (assessmentType === 'spiritual') {
        for (const item of assessment.history) {
          const type = item.assessmentType || 'fasdl';
          const category = type === 'fasdl' ? 1 : 2;
          allHistoryItems.push({
            ...item,
            _testType: type,
            _category: category,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType, category)
          });
        }
      } else if (assessmentType === 'mental-health') {
        for (const item of assessment.history) {
          const category = item.selectedCategory || 1;
          allHistoryItems.push({
            ...item,
            _category: category,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType, category)
          });
        }
      } else if (assessmentType === 'legendarial') {
        for (const item of assessment.history) {
          const category = item.selectedCategory || 1;
          allHistoryItems.push({
            ...item,
            _category: category,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType, category)
          });
        }
      } else if (assessmentType === 'religio-traditio-cultural') {
        for (const item of assessment.history) {
          const category = item.selectedCategory || 1;
          allHistoryItems.push({
            ...item,
            _category: category,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType, category)
          });
        }
      } else if (assessmentType === 'occupational') {
        for (const item of assessment.history) {
          const category = item.selectedCategory || 1;
          allHistoryItems.push({
            ...item,
            _category: category,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType, category)
          });
        }
      } else {
        // For assessments without categories
        for (const item of assessment.history) {
          allHistoryItems.push({
            ...item,
            _assessmentType: assessmentType,
            _label: getTestName(assessmentType)
          });
        }
      }
    }

    // Sort by completion date descending
    allHistoryItems.sort((a, b) => {
      const dateA = new Date(a.completedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.completedAt || b.createdAt || 0).getTime();
      return dateB - dateA;
    });

    setHistoryData(allHistoryItems);
    setHistoryType('all-assessments');
  };

  const fetchAssessmentHistory = async (type: string, category?: number) => {
    try {
      // Use merged data instead of separate API calls
      const assessmentType = mapHistoryTypeToAssessmentType(type);
      const mergedData = assessmentData[assessmentType];

      if (!mergedData) {
        console.warn(`No data available for assessment type: ${assessmentType}`);
        return;
      }

      let filteredData = mergedData.history;

      // Apply type-specific filtering logic
      if (type === 'physical-ipaq' || type === 'physical-psqi') {
        // Filter physical assessments by category
        const targetCategoryName = getPhysicalCategoryName(type);
        filteredData = mergedData.history.filter(item =>
          item._categoryName === targetCategoryName
        );
      } else if (type === 'dass-21' || type === 'phq-9' || type === 'gad-7' || type === 'gender') {
        // Filter mental health assessments by category
        const categoryMap: { [key: string]: number } = {
          'dass-21': 1,
          'phq-9': 2,
          'gad-7': 3,
          'gender': 4
        };
        const targetCategory = categoryMap[type];
        if (targetCategory) {
          filteredData = mergedData.history.filter(item =>
            item.selectedCategory === targetCategory
          );
        }
      } else if (type === 'fasdl' || type === 'facit-sp-12') {
        // Filter spiritual assessments by specific assessment type
        filteredData = mergedData.history.filter(item =>
          item.assessmentType === type
        );
      } else if (category !== undefined) {
        // Filter by category for other assessment types
        // Special handling for Socio-Relational: MSPSS (category 2)
        if (assessmentType === 'socio-relational' && category === 2) {
          filteredData = mergedData.history.filter(item => item.selectedCategory === 2);
        } else {
          filteredData = mergedData.history.filter(item => item.selectedCategory === category);
        }
      }

      // Patch: For Brief RCOPE and All Religio-Traditio-Cultural, override level mapping for history modal
      if (
        type === 'Brief RCOPE' ||
        (type === 'religio' && (category === 1 || category === undefined)) ||
        type === 'religio-traditio-cultural' // covers 'All Religio-Traditio-Cultural'
      ) {
        filteredData = filteredData.map(item => {
          // Only patch Brief RCOPE entries (selectedCategory === 1)
          if (item.selectedCategory === 1) {
            const raw = item.scores?.category1 || item.scores?.prcTotal || 0;
            const nrcTotal = item.scores?.nrcTotal || 0;
            const percent = Math.round(clampPercentage(((raw - nrcTotal + 21) / 42) * 100));
            let level = 'Low';
            if (percent >= 75) level = 'High';
            else if (percent >= 50) level = 'Moderate';
            return { ...item, results: { ...item.results, level } };
          }
          return item;
        });
      }
      setHistoryData(filteredData);
      setHistoryType(type);
      setSelectedHistoryCategory(category || null);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching assessment history:', error);
    }
  };

  // Helper function to map history types to assessment types
  const mapHistoryTypeToAssessmentType = (historyType: string): AssessmentType => {
    const typeMap: Record<string, AssessmentType> = {
      'fasdl': 'spiritual',
      'facit-sp-12': 'spiritual',
      'spiritual': 'spiritual',
      'nutritional': 'nutritional',
      'physical': 'physical',
      'physical-ipaq': 'physical',
      'physical-psqi': 'physical',
      'dass-21': 'mental-health',
      'phq-9': 'mental-health',
      'gad-7': 'mental-health',
      'gender': 'mental-health',
      'mental-health': 'mental-health',
      'legendarial': 'legendarial',
      'religio': 'religio-traditio-cultural',
      'socio-relational': 'socio-relational',
      'financial': 'financial',
      'environmental': 'environmental',
      'occupational': 'occupational'
    };
    return typeMap[historyType] || 'spiritual'; // fallback
  };

  // Helper function to get physical category names
  const getPhysicalCategoryName = (type: string): string => {
    switch (type) {
      case 'physical-ipaq': return 'IPAQ-SF';
      case 'physical-psqi': return 'PSQI';
      default: return '';
    }
  };

  const getScoreColor = (level: string, score?: number, maxScore?: number, assessmentType?: string, severityCategory?: string) => {


    // PHQ-9 severity category color logic for history: Minimal=green, Mild/Moderate=orange, Moderately Severe/Severe=red
    if (assessmentType === 'phq-9' && severityCategory) {
      switch (severityCategory.toLowerCase()) {
        case 'minimal': return '#059669'; // Green
        case 'mild':
        case 'moderate': return '#e85818'; // Orange
        case 'moderately severe':
        case 'severe': return '#dc2626'; // Red
        default: return '#6B7280';
      }
    }
    // Spiritual and Legendarial assessment color logic: High=green, Moderate=orange, Low=red
    const path = window?.location?.pathname || '';
    if (typeof score === 'undefined' && typeof maxScore === 'undefined' && (path.includes('spiritual') || path.includes('legendarial'))) {
      switch (level?.toLowerCase()) {
        case 'high': return '#059669'; // Green
        case 'moderate': return '#ea580c'; // Orange
        case 'low': return '#dc2626'; // Red
        default: return '#6B7280';
      }
    }

    // Occupational assessment color logic: High=red (bad), Moderate=orange, Low=green (good)
    if (assessmentType === 'occupational' || path.includes('occupational')) {
      switch (level?.toLowerCase()) {
        case 'high': return '#dc2626'; // Red (high burnout = bad)
        case 'moderate': return '#ea580c'; // Orange
        case 'low': return '#059669'; // Green (low burnout = good)
        default: return '#6B7280';
      }
    }

    // Financial and Nutritional assessment color logic: High=green, Moderate=orange, Low=red
    if (assessmentType === 'financial' || path.includes('financial') || assessmentType === 'nutritional' || path.includes('nutritional')) {
      switch (level?.toLowerCase()) {
        case 'high': return '#059669'; // Green
        case 'moderate': return '#ea580c'; // Orange
        case 'low': return '#dc2626'; // Red
        default: return '#6B7280';
      }
    }

    // If we have score and maxScore, use proportional scoring for colors
    if (score !== undefined && maxScore !== undefined) {
      if (assessmentType === 'phq-9') {
        const percentage = Math.max(0, Math.min(100, (score / maxScore) * 100));
        if (percentage >= 67) return '#dc2626'; // High = Red (67%+)
        if (percentage >= 34) return '#e85818'; // Moderate = Orange (34-66%)
        if (percentage >= 0) return '#059669'; // Low = Green (0-33%)
      }
      if (assessmentType === 'dass-21') {
        const percentage = Math.max(0, Math.min(100, (score / maxScore) * 100));
        if (percentage >= 67) return '#dc2626'; // High = Red (67%+)
        if (percentage >= 34) return '#ea580c'; // Moderate = Orange (34-66%)
        return '#059669'; // Low = Green (0-33%)
      }
      if (assessmentType === 'gad-7') {
        const percentage = Math.max(0, Math.min(100, (score / maxScore) * 100));
        if (percentage >= 67) return '#dc2626'; // High = Red (67%+)
        if (percentage >= 34) return '#ea580c'; // Moderate = Orange (34-66%)
        return '#059669'; // Low = Green (0-33%)
      }
      return getProportionalScoreColor(score, maxScore);
    }

    // Fallback to level-based coloring (legacy compatibility)
    const mapToStandardLevel = (backendLevel: string) => {
      switch (backendLevel.toLowerCase()) {
        case 'excellent':
        case 'good':
        case 'low':
        case 'normal':
          return 'low'; // Green
        case 'fair':
        case 'moderate':
          return 'moderate'; // Orange
        case 'poor':
        case 'high':
          return 'high'; // Red
        case 'critical':
        case 'very high':
          return 'very high'; // Dark red
        default:
          return 'low'; // Default to green
      }
    };

    const standardLevel = mapToStandardLevel(level);

    switch (standardLevel) {
      case 'high': return '#dc2626'; // Red for High
      case 'moderate': return '#ea580c'; // Orange for Moderate
      case 'low': return '#059669'; // Green for Low
      case 'very high': return '#991b1b'; // Dark red for Very High
      default: return '#6B7280'; // Gray
    }
  };


  const getLevelClass = (level: string, assessmentType?: string) => {
    const normalizedLevel = level.toLowerCase().replace(/\s+/g, '-');
    
    // Consistent color coding for ALL assessments:
    // Low = Red (poor wellness)
    // Moderate = Orange
    // High = Green (good wellness)
    return `level-${normalizedLevel}`;
  };

// Function to get chart color for each assessment type
const getChartColor = (assessmentName: string): string => {
  switch (assessmentName) {
    case 'Spiritual': return '#BFE9DC'; // Changed from light purple to teal
    case 'Emotio-Psycho-Rational': return '#BEE1E6';
    case 'Nutritional': return '#CDEAC0';
    case 'Physical': return '#FFD6A5';
    case 'Physical Activity': return '#FFD6A5'; // Subcategory of Physical
    case 'Sleep Quality': return '#FFD6A5'; // Subcategory of Physical
    case 'Financial': return '#F9E79F';
    case 'Environmental': return '#BFE9DC'; // Changed from pale green to teal
    case 'Legendarial': return '#FFB5C8';
    case 'Religio-Traditio-Cultural': return '#FFF1C1';
    case 'Socio-Relational': return '#BFE9DC'; // Changed from lavender to teal
    case 'Occupational': return '#BFE9DC'; // Changed from mint to teal
    default: return '#6b7280'; // Gray fallback
  }
};

// Function to get DASS-21 severity-based color
const getDASS21SeverityColor = (label: string, score: number): string => {
  // Extract subscale type from label (e.g., "DASS-21 Depression" -> "depression")
  const subscaleMatch = label.match(/DASS-21\s+(\w+)/i);
  if (!subscaleMatch) return '#6b7280'; // Gray fallback
  
  const subscale = subscaleMatch[1].toLowerCase();
  
  // Determine severity level based on subscale and score
  let severityLevel = 'normal';
  
  if (subscale === 'depression') {
    if (score <= 9) severityLevel = 'normal';
    else if (score <= 13) severityLevel = 'mild';
    else if (score <= 20) severityLevel = 'moderate';
    else if (score <= 27) severityLevel = 'severe';
    else severityLevel = 'extremely-severe';
  } else if (subscale === 'anxiety') {
    if (score <= 7) severityLevel = 'normal';
    else if (score <= 9) severityLevel = 'mild';
    else if (score <= 14) severityLevel = 'moderate';
    else if (score <= 19) severityLevel = 'severe';
    else severityLevel = 'extremely-severe';
  } else if (subscale === 'stress') {
    if (score <= 14) severityLevel = 'normal';
    else if (score <= 18) severityLevel = 'mild';
    else if (score <= 25) severityLevel = 'moderate';
    else if (score <= 33) severityLevel = 'severe';
    else severityLevel = 'extremely-severe';
  }
  
  // Return color based on severity level
  switch (severityLevel) {
    case 'normal': return '#059669'; // Green for normal
    case 'mild': return '#eab308'; // Yellow for mild
    case 'moderate': return '#ea580c'; // Orange for moderate
    case 'severe': return '#dc2626'; // Red for severe
    case 'extremely-severe': return '#991b1b'; // Dark red for extremely severe
    default: return '#6b7280'; // Gray fallback
  }
};

// Function to find the lowest assessment score
const getLowestAssessment = () => {
  const assessments = [
    { name: 'Spiritual', data: fasdlAssessment || facitAssessment, type: 'spiritual' },
    { name: 'Nutritional', data: nutritionalAssessment, type: 'nutritional' },
    { name: 'Physical', data: physicalAssessment, type: 'physical' },
    { name: 'Emotio-Psycho-Rational', data: dassAssessment || phqAssessment || gadAssessment || genderAssessment, type: 'mental-health' },
    { name: 'Financial', data: financialAssessments.length > 0 ? financialAssessments[0] : null, type: 'financial' },
    { name: 'Environmental', data: environmentalAssessment, type: 'environmental' },
    { name: 'Legendarial', data: legendarialAssessments.length > 0 ? legendarialAssessments[0] : null, type: 'legendarial' },
    { name: 'Religio-Traditio-Cultural', data: religioAssessments.length > 0 ? religioAssessments[0] : null, type: 'religio' },
    { name: 'Socio-Relational', data: socioRationalAssessments.length > 0 ? socioRationalAssessments[0] : null, type: 'socio-relational' },
    { name: 'Occupational', data: occupationalAssessments.length > 0 ? occupationalAssessments[0] : null, type: 'occupational' }
  ];

  let lowestScore = { name: '', score: 100, level: 'High' };

  assessments.forEach(({ name, data, type }) => {
    if (data) {
      const { percentage, level } = getAssessmentDisplayData(data, type);
      if (percentage < lowestScore.score) {
        lowestScore = { name, score: percentage, level };
      }
    }
  });

  return lowestScore;
};

// Function to generate inspirational quote based on lowest assessment
const generateAIInspiration = (lowestAssessment: { name: string; percentage: number }) => {
  const assessmentName = lowestAssessment.name.toLowerCase();

  // Create purely inspirational quotes based on the lowest scoring area
  const inspirationalQuotes = {
    'spiritual': [
      'The journey inward reveals the path to true peace. Embrace the quiet moments that nourish your spirit.',
      'In the stillness of your heart lies the wisdom to heal and grow. Listen to your inner voice.',
      'Every sunrise brings a new opportunity to connect with your deepest self. Let your spirit guide you.',
      'The path to spiritual wellness is paved with moments of reflection and gratitude. Walk it with an open heart.',
      'True peace comes from within. Nurture your spirit with kindness, patience, and self-compassion.'
    ],
    'nutritional': [
      'Nourish your body as you would a cherished garden. Every healthy choice plants seeds of vitality.',
      'The food you choose becomes the energy that fuels your dreams. Choose wisely, choose health.',
      'Eating well is an act of self-love. Let every meal be a celebration of the life within you.',
      'Your body is a temple - honor it with nourishment that sustains and energizes your journey.',
      'Healthy eating is not about perfection, but about making choices that make you feel alive and strong.'
    ],
    'physical': [
      'Movement is medicine for both body and soul. Find joy in the rhythm of your own strength.',
      'Your body is capable of incredible things. Honor its power with care and consistent movement.',
      'Every step you take builds a stronger foundation for your dreams. Keep moving forward.',
      'Physical wellness is a gift you give yourself. Celebrate your body\'s amazing capabilities.',
      'The path to physical vitality is walked one step at a time. Trust in your body\'s wisdom to heal and grow.'
    ],
    'Emotio-Psycho-Rational': [
      'Your mind is a garden - tend to it with thoughts that bloom with possibility and hope.',
      'Mental wellness begins with self-compassion. Be gentle with yourself as you navigate life\'s journey.',
      'The strength of your mind determines the quality of your days. Nurture it with care and intention.',
      'In the quiet spaces of your mind lie the answers you seek. Listen with patience and kindness.',
      'Mental health is not the absence of challenges, but the courage to face them with grace and resilience.'
    ],
    'financial': [
      'Financial freedom begins with mindful choices. Every decision is a step toward abundance and peace.',
      'True wealth is measured not in possessions, but in the freedom to live according to your values.',
      'Financial wellness creates space for dreams to flourish. Plant seeds of intention in your daily choices.',
      'Money is a tool for creating the life you envision. Use it wisely to build your ideal future.',
      'Financial peace comes from aligning your resources with your deepest values and aspirations.'
    ],
    'environmental': [
      'Your connection to the earth sustains your spirit. Honor the world that nurtures you.',
      'Environmental consciousness is an act of love for future generations. Choose sustainability with every breath.',
      'The earth provides abundantly - give back with gratitude and care for the world around you.',
      'Living in harmony with nature brings harmony within. Let the natural world inspire your daily choices.',
      'Environmental wellness connects us all. Your mindful actions create ripples of positive change.'
    ],
    'legendarial': [
      'Life satisfaction blooms from within. Cultivate gratitude for the journey that has shaped you.',
      'Purpose is found not in destinations, but in the meaning we create along the way.',
      'Every experience adds color to your life\'s canvas. Embrace the full spectrum of your journey.',
      'Satisfaction comes from living authentically, honoring your values in every choice you make.',
      'The quality of your life is determined by the quality of your thoughts and intentions.'
    ],
    'religio-traditio-cultural': [
      'Cultural heritage connects us to our roots and guides our future. Honor your traditions with an open heart.',
      'Spiritual traditions offer wisdom for modern living. Find comfort in the timeless truths they hold.',
      'Cultural identity is a gift passed through generations. Wear it with pride and share it with love.',
      'Religious and cultural practices provide anchors in life\'s storms. Find solace in their familiar rhythms.',
      'The beauty of diverse traditions enriches our world. Celebrate the unique path that shaped you.'
    ],
    'socio-relational': [
      'Connection is the heartbeat of humanity. Nurture relationships that bring joy and understanding.',
      'Social wellness blooms from authentic connections. Be present with those who matter most.',
      'The quality of your relationships determines the quality of your life. Invest in them with love and care.',
      'Community support is a wellspring of strength. Share your journey and let others share theirs.',
      'Meaningful connections create a tapestry of shared experiences. Weave yours with intention and kindness.'
    ],
    'occupational': [
      'Work that aligns with your values brings fulfillment beyond measure. Find purpose in what you do.',
      'Professional growth is a journey of self-discovery. Embrace challenges as opportunities to learn.',
      'Occupational wellness means finding joy in your daily contributions. Let your work reflect your best self.',
      'Career satisfaction comes from doing what matters to you. Align your path with your deepest values.',
      'Every role you play in your work contributes to something greater. Find meaning in your contributions.'
    ]
  };

  const quotes = inspirationalQuotes[assessmentName as keyof typeof inspirationalQuotes] || [
    'Every small step counts towards a healthier you.',
    'Wellness is a journey, not a destination. Embrace the path.',
    'Your potential for growth is limitless. Believe in your ability to improve.',
    'Self-care is not selfish; it\'s essential for a life well-lived.',
    'The seeds of wellness you plant today will bloom into tomorrow\'s vitality.'
  ];

  // Return a random inspirational quote from the appropriate category
  return quotes[Math.floor(Math.random() * quotes.length)];
};

  // Helper function to calculate completed category percentages from assessment history
  const getCompletedCategoryPercents = (
    group: 'spiritual' | 'mental-health' | 'legendarial' | 'religio' | 'occupational' | 'physical' | 'socio-relational',
    historicalData?: any[],
    legendarialData?: any[],
    occupationalData?: any[],
    socioRelationalData?: any[]
  ): number[] => {
    const percents: number[] = [];

    switch (group) {
      case 'spiritual':
        // Read from passed historicalData array
        const historyToUse = historicalData || assessmentData['spiritual']?.history || [];
        const fasdlAssessment = historyToUse
          ?.filter((a: any) => a.assessmentType === 'fasdl' || a.selectedCategory == 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const facitAssessment = historyToUse
          ?.filter((a: any) => a.assessmentType === 'facit-sp-12' || a.selectedCategory == 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const fasdlCompleted = fasdlAssessment && ((fasdlAssessment.assessmentData && fasdlAssessment.assessmentData.scores && fasdlAssessment.assessmentData.results) || (fasdlAssessment.scores && fasdlAssessment.results));
        const facitCompleted = facitAssessment && ((facitAssessment.assessmentData && facitAssessment.assessmentData.scores && facitAssessment.assessmentData.results) || (facitAssessment.scores && facitAssessment.results));

        if (fasdlCompleted) {
          // FASDL: higher score = better (green)
          const fasdlRaw = (fasdlAssessment.assessmentData?.scores?.overall || fasdlAssessment.scores?.overall || 0);
          const fasdlPercent = Math.round((fasdlRaw / 50) * 100);
          percents.push(fasdlPercent);
        }
        if (facitCompleted) {
          // FACIT-Sp-12: higher score = better (green)
          const facitRaw = (facitAssessment.assessmentData?.scores?.overall || facitAssessment.scores?.overall || 0);
          const facitPercent = Math.round((facitRaw / 48) * 100);
          percents.push(facitPercent);
        }
        break;

      case 'mental-health':
        // Get individual assessments from history by selectedCategory (use latest per category)
        const mentalHealthHistory = assessmentData['mental-health']?.history || [];
        
        // DASS-21 is selectedCategory: 1 (max score: 42 per subscale)
        const dassItem = mentalHealthHistory
          .filter((item: any) => item.selectedCategory == 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const dassCompleted = dassItem && dassItem.scores && dassItem.results;
        
        // PHQ-9 is selectedCategory: 2 (max score: 27)
        const phqItem = mentalHealthHistory
          .filter((item: any) => item.selectedCategory == 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const phqCompleted = phqItem && phqItem.scores && phqItem.results;
        
        // GAD-7 is selectedCategory: 3 (max score: 21)
        const gadItem = mentalHealthHistory
          .filter((item: any) => item.selectedCategory == 3)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const gadCompleted = gadItem && gadItem.scores && gadItem.results;
        
        // Gender Sensitivity is selectedCategory: 4 (max score: 20)
        const genderItem = mentalHealthHistory
          .filter((item: any) => item.selectedCategory == 4)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const genderCompleted = genderItem && genderItem.scores && genderItem.results;

        if (dassCompleted) {
          // For DASS-21, use the highest subscale score for percentage calculation (higher = more severe symptoms)
          const subscales = dassItem.scores.subscales || {};
          const depressionScore = Number(subscales.depression ?? 0);
          const anxietyScore = Number(subscales.anxiety ?? 0);
          const stressScore = Number(subscales.stress ?? 0);
          const highestSubscaleScore = Math.max(depressionScore, anxietyScore, stressScore);
          percents.push(Math.round(clampPercentage((highestSubscaleScore / 42) * 100)));
        }
        if (phqCompleted) {
          // PHQ-9: higher score = more depression symptoms (normal scale)
          percents.push(Math.round(clampPercentage((phqItem.scores.overall / 27) * 100)));
        }
        if (gadCompleted) {
          // GAD-7: higher score = more anxiety symptoms (normal scale)
          percents.push(Math.round(clampPercentage((gadItem.scores.overall / 21) * 100)));
        }
        if (genderCompleted) {
          percents.push(Math.round(clampPercentage(100 - ((genderItem.scores.overall / 20) * 100))));
        }
        break;

      case 'legendarial':
        const legHistory = legendarialData || assessmentData['legendarial']?.history || [];
        const swlsAssessment = legHistory
          ?.filter((a: any) => a.selectedCategory == 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const pilAssessment = legHistory
          ?.filter((a: any) => a.selectedCategory == 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const swlsCompleted = swlsAssessment && ((swlsAssessment.scores && swlsAssessment.results) || (swlsAssessment.assessmentData && swlsAssessment.assessmentData.scores && swlsAssessment.assessmentData.results));
        const pilCompleted = pilAssessment && ((pilAssessment.scores && pilAssessment.results) || (pilAssessment.assessmentData && pilAssessment.assessmentData.scores && pilAssessment.assessmentData.results));

        if (swlsCompleted) {
          const swlsTotal = swlsAssessment.scores?.swlsTotal || swlsAssessment.assessmentData?.scores?.swlsTotal || 0;
          percents.push(Math.round((swlsTotal / 35) * 100));
        }
        if (pilCompleted) {
          const pilTotal = pilAssessment.scores?.pilTotal || pilAssessment.assessmentData?.scores?.pilTotal || 0;
          percents.push(Math.round((pilTotal / 70) * 100));
        }
        break;

      case 'religio':
        const religioHistory = historicalData || assessmentData['religio-traditio-cultural']?.history || [];
        const briefRcopeAssessment = religioHistory
          ?.filter((a: any) => a.selectedCategory === 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const cvscaleAssessment = religioHistory
          ?.filter((a: any) => a.selectedCategory === 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const tvasAssessment = religioHistory
          ?.filter((a: any) => a.selectedCategory === 3)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const briefRcopeCompleted = briefRcopeAssessment && ((briefRcopeAssessment.scores && briefRcopeAssessment.results) || (briefRcopeAssessment.assessmentData && briefRcopeAssessment.assessmentData.scores && briefRcopeAssessment.assessmentData.results));
        const cvscaleCompleted = cvscaleAssessment && ((cvscaleAssessment.scores && cvscaleAssessment.results) || (cvscaleAssessment.assessmentData && cvscaleAssessment.assessmentData.scores && cvscaleAssessment.assessmentData.results));
        const tvasCompleted = tvasAssessment && ((tvasAssessment.scores && tvasAssessment.results) || (tvasAssessment.assessmentData && tvasAssessment.assessmentData.scores && tvasAssessment.assessmentData.results));

        // Compute average of all completed religio categories (using latest per category)
        const religioCategoryPercents: number[] = [];
        if (briefRcopeCompleted) {
          const prcTotal = briefRcopeAssessment.scores?.prcTotal || briefRcopeAssessment.assessmentData?.scores?.prcTotal || 0;
          const nrcTotal = briefRcopeAssessment.scores?.nrcTotal || briefRcopeAssessment.assessmentData?.scores?.nrcTotal || 0;
          const category1 = briefRcopeAssessment.scores?.category1 || briefRcopeAssessment.assessmentData?.scores?.category1;
          religioCategoryPercents.push(Math.round(clampPercentage(((category1 || prcTotal || 0) - (nrcTotal || 0) + 21) / 42 * 100)));
        }
        if (cvscaleCompleted) {
          // CVSCALE: normalize 1-7 scale to 0-100%
          const cvscaleTotal = cvscaleAssessment.scores?.cvscaleTotal || cvscaleAssessment.assessmentData?.scores?.cvscaleTotal || 0;
          religioCategoryPercents.push(Math.round(clampPercentage((cvscaleTotal - 1) / 6 * 100)));
        }
        if (tvasCompleted) {
          // TVAS: normalize 10-50 scale to 0-100%
          const tvasTotal = tvasAssessment.scores?.tvasTotal || tvasAssessment.assessmentData?.scores?.tvasTotal || 0;
          religioCategoryPercents.push(Math.round(clampPercentage((tvasTotal - 10) / 40 * 100)));
        }
        if (religioCategoryPercents.length > 0) {
          const avg = Math.floor(religioCategoryPercents.reduce((sum, p) => sum + p, 0) / religioCategoryPercents.length);
          percents.push(avg);
        }
        break;

      case 'occupational':
        const occupationalHistory = occupationalData || assessmentData['occupational']?.history || [];
        const cbiAssessment = occupationalHistory
          ?.filter((a: any) => a.selectedCategory == 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const uwesAssessment = occupationalHistory
          ?.filter((a: any) => a.selectedCategory == 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const employeeEngagementAssessment = occupationalHistory
          ?.filter((a: any) => a.selectedCategory == 3)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const cbiCompleted = cbiAssessment && ((cbiAssessment.scores && cbiAssessment.results) || (cbiAssessment.assessmentData && cbiAssessment.assessmentData.scores && cbiAssessment.assessmentData.results));
        const uwesCompleted = uwesAssessment && ((uwesAssessment.scores && uwesAssessment.results) || (uwesAssessment.assessmentData && uwesAssessment.assessmentData.scores && uwesAssessment.assessmentData.results));
        const employeeEngagementCompleted = employeeEngagementAssessment && ((employeeEngagementAssessment.scores && employeeEngagementAssessment.results) || (employeeEngagementAssessment.assessmentData && employeeEngagementAssessment.assessmentData.scores && employeeEngagementAssessment.assessmentData.results));

        if (cbiCompleted) {
          const cbiTotal = cbiAssessment.scores?.cbiTotal || cbiAssessment.assessmentData?.scores?.cbiTotal || 0;
          const cbiPercent = Math.round((cbiTotal / 100) * 100);
          percents.push(cbiPercent);
        }
        if (uwesCompleted) {
          // UWES: invert to show burnout (6/6 = 0%, 0/6 = 100%)
          const uwesTotal = uwesAssessment.scores?.uwesTotal || uwesAssessment.assessmentData?.scores?.uwesTotal || 0;
          percents.push(Math.round((1 - (uwesTotal / 6)) * 100));
        }
        if (employeeEngagementCompleted) {
          // Employee Engagement: invert to show burnout (higher engagement = lower burnout percentage)
          const engagementTotal = employeeEngagementAssessment.scores?.engagementTotal || employeeEngagementAssessment.assessmentData?.scores?.engagementTotal || 0;
          percents.push(Math.round(clampPercentage((1 - (engagementTotal / 5)) * 100)));
        }
        break;

      case 'socio-relational':
        const socioHistory = socioRelationalData || assessmentData['socio-relational']?.history || [];
        const starAssessment = socioHistory
          ?.filter((a: any) => a.selectedCategory === 1)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const mspssAssessment = socioHistory
          ?.filter((a: any) => a.selectedCategory === 2)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const starCompleted = starAssessment && ((starAssessment.scores && starAssessment.results) || (starAssessment.assessmentData && starAssessment.assessmentData.scores && starAssessment.assessmentData.results));
        const mspssCompleted = mspssAssessment && ((mspssAssessment.scores && mspssAssessment.results) || (mspssAssessment.assessmentData && mspssAssessment.assessmentData.scores && mspssAssessment.assessmentData.results));

        if (starCompleted) {
          const starTotal = starAssessment.scores?.socioRelationalScore || starAssessment.assessmentData?.scores?.socioRelationalScore || 0;
          const starPercent = Math.round((starTotal / 20) * 100);
          percents.push(starPercent);
        }
        if (mspssCompleted) {
          const mspssTotal = mspssAssessment.scores?.socioRelationalScore || mspssAssessment.assessmentData?.scores?.socioRelationalScore || 0;
          const mspssPercent = Math.round((mspssTotal / 7) * 100);
          percents.push(mspssPercent);
        }
        break;

      case 'physical':
        // Use LATEST data from the merged data (from /latest endpoint)
        const latestPhysicalData = assessmentData['physical']?.latest;
        
        if (latestPhysicalData) {
          const categories = latestPhysicalData?.scores?.categories || latestPhysicalData?.assessmentData?.scores?.categories || [];
          
          // Get IPAQ-SF and PSQI scores from latest assessment
          const ipaqCategory = categories.find((c: any) => c.name === 'IPAQ-SF');
          const psqiCategory = categories.find((c: any) => c.name === 'PSQI');
          
          // Add scores if they exist
          if (ipaqCategory && typeof ipaqCategory.score === 'number') {
            percents.push(Math.round(ipaqCategory.score));
          }
          if (psqiCategory && typeof psqiCategory.score === 'number') {
            percents.push(Math.round(psqiCategory.score));
          }
        }
        break;
    }

    return percents;
  };

  // Function to calculate chart data from all assessment sources
  const calculateChartData = (
    assessmentData: any
  ) => {
    const result: Array<{ name: string; percentage: number; level: string; fill: string; type: string }> = [];

    // Helper function to ensure all 10 categories are included
    const addCategoryOrPlaceholder = (name: string, type: string, percentage: number | null, level: string | null) => {
      if (percentage !== null && level) {
        // Assessment was taken - show actual percentage and level (even if 0%)
        result.push({ name, percentage: Math.round(percentage), level, fill: getChartColor(name), type });
      } else {
        // Assessment not taken yet - show "Not Started"
        result.push({ name, percentage: 0, level: 'Not Started', fill: getChartColor(name), type });
      }
    };

    // Extract assessment histories from the provided assessmentData (same data used by cards)
    const spiritualHistory = assessmentData['spiritual']?.history || [];
    const mentalHealthHistory = assessmentData['mental-health']?.history || [];
    const legendarialHistory = assessmentData['legendarial']?.history || [];
    const religioHistory = assessmentData['religio-traditio-cultural']?.history || [];
    const socioRelationalHistory = assessmentData['socio-relational']?.history || [];
    const occupationalHistory = assessmentData['occupational']?.history || [];

    // 1. Spiritual - Use getCompletedCategoryPercents with history data (same as card rendering)
    const spiritualPercents = getCompletedCategoryPercents('spiritual', spiritualHistory);
    let spiritualPercent = spiritualPercents.length > 0 ? Math.round(spiritualPercents.reduce((a, b) => a + b, 0) / spiritualPercents.length) : 0;
    const spiritualLevel = spiritualPercent >= 75 ? 'High' : spiritualPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Spiritual', 'spiritual', spiritualPercents.length > 0 ? spiritualPercent : null, spiritualPercents.length > 0 ? spiritualLevel : null);

    // 2. Nutritional - Match progress circle calculation
    const nutritionalData = assessmentData['nutritional']?.latest;
    if (nutritionalData) {
      const { percentage, level } = getAssessmentDisplayData(nutritionalData, 'nutritional');
      addCategoryOrPlaceholder('Nutritional', 'nutritional', percentage, percentage >= 0 ? level : null);
    } else {
      addCategoryOrPlaceholder('Nutritional', 'nutritional', null, null);
    }

    // 3. Physical - Use getCompletedCategoryPercents (same as card rendering)
    const physicalPercents = getCompletedCategoryPercents('physical');
    let physicalPercent = physicalPercents.length > 0 ? Math.round(physicalPercents.reduce((a, b) => a + b, 0) / physicalPercents.length) : 0;
    const physicalLevel = physicalPercent >= 75 ? 'High' : physicalPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Physical', 'physical', physicalPercents.length > 0 ? physicalPercent : null, physicalPercents.length > 0 ? physicalLevel : null);

    // 4. Emotio-Psycho-Rational (Mental-Health) - Use getCompletedCategoryPercents with history data (same as card rendering)
    const mentalPercents = getCompletedCategoryPercents('mental-health', mentalHealthHistory);
    let mentalPercent = mentalPercents.length > 0 ? Math.round(mentalPercents.reduce((a, b) => a + b, 0) / mentalPercents.length) : 0;
    const mentalLevel = mentalPercent >= 75 ? 'High' : mentalPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Emotio-Psycho-Rational', 'mental-health', mentalPercents.length > 0 ? mentalPercent : null, mentalPercents.length > 0 ? mentalLevel : null);

    // 5. Financial - Match progress circle calculation (use exact same logic as card at line 462)
    const financialAssessments = assessmentData['financial']?.history as any[] || [];
    const latestFinancial = financialAssessments.length > 0 ? financialAssessments[0] : null;
    let financialPercent = 0;
    if (latestFinancial) {
      const maxScore = 80;
      financialPercent = latestFinancial.scores && maxScore > 0 ? Math.round((latestFinancial.scores.overall / maxScore) * 100) : 0;
      financialPercent = clampPercentage(financialPercent);
    }
    const financialLevel = financialPercent >= 75 ? 'High' : financialPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Financial', 'financial', latestFinancial ? financialPercent : null, latestFinancial ? financialLevel : null);

    // 6. Environmental - Match progress circle calculation
    const envLatest = assessmentData['environmental']?.latest;
    if (envLatest) {
      const { percentage, level } = getAssessmentDisplayData(envLatest, 'environmental');
      addCategoryOrPlaceholder('Environmental', 'environmental', percentage, percentage >= 0 ? level : null);
    } else {
      addCategoryOrPlaceholder('Environmental', 'environmental', null, null);
    }

    // 7. Legendarial - Use getCompletedCategoryPercents with history data (same as card rendering)
    const legendarialPercents = getCompletedCategoryPercents('legendarial', undefined, legendarialHistory);
    let legPercent = legendarialPercents.length > 0 ? Math.round(legendarialPercents.reduce((a, b) => a + b, 0) / legendarialPercents.length) : 0;
    const legLevel = legPercent >= 75 ? 'High' : legPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Legendarial', 'legendarial', legendarialPercents.length > 0 ? legPercent : null, legendarialPercents.length > 0 ? legLevel : null);

    // 8. Religio-Traditio-Cultural - Use getCompletedCategoryPercents with history data (same as card rendering)
    const religioPercents = getCompletedCategoryPercents('religio', religioHistory);
    let religioPercent = religioPercents.length > 0 ? Math.round(religioPercents.reduce((a, b) => a + b, 0) / religioPercents.length) : 0;
    const religioLevel = religioPercent >= 75 ? 'High' : religioPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Religio-Traditio-Cultural', 'religio', religioPercents.length > 0 ? religioPercent : null, religioPercents.length > 0 ? religioLevel : null);

    // 9. Socio-Relational - Use getCompletedCategoryPercents with history data (same as card rendering)
    const socioPercents = getCompletedCategoryPercents('socio-relational', undefined, undefined, undefined, socioRelationalHistory);
    let socioPercent = socioPercents.length > 0 ? Math.round(socioPercents.reduce((a, b) => a + b, 0) / socioPercents.length) : 0;
    const socioLevel = socioPercent >= 75 ? 'High' : socioPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Socio-Relational', 'socio-relational', socioPercents.length > 0 ? socioPercent : null, socioPercents.length > 0 ? socioLevel : null);

    // 10. Occupational - Use getCompletedCategoryPercents with history data (same as card rendering)
    const occupationalPercents = getCompletedCategoryPercents('occupational', undefined, undefined, occupationalHistory);
    let occPercent = occupationalPercents.length > 0 ? Math.round(occupationalPercents.reduce((a, b) => a + b, 0) / occupationalPercents.length) : 0;
    const occLevel = occPercent >= 75 ? 'High' : occPercent >= 50 ? 'Moderate' : 'Low';
    addCategoryOrPlaceholder('Occupational', 'occupational', occupationalPercents.length > 0 ? occPercent : null, occupationalPercents.length > 0 ? occLevel : null);

    return result;
  };

    // Memoized chart data calculation using useCallback for performance
  const getChartData = useCallback(() => {
    return calculateChartData(assessmentData);
  }, [assessmentData]);

  if (isLoading) {
    return <LoadingScreen message="Loading insights..." fullScreen />;
  }

  // Helper function to get level class for progress circle based on assessment level
  const getProgressCircleClass = (level: string, assessmentType?: string) => {
    if (!level) return 'progress-circle-unknown';


    // For occupational assessments, invert the color logic (high = bad = red, low = good = green)
    if (assessmentType === 'occupational') {
      switch (level.toLowerCase()) {
        case 'high':
          return 'progress-circle-low'; // Red (high burnout = bad)
        case 'moderate':
          return 'progress-circle-medium'; // Orange
        case 'low':
          return 'progress-circle-high'; // Green (low burnout = good)
        default:
          return 'progress-circle-unknown';
      }
    }

    // For financial and nutritional, always: High = green, Moderate = orange, Low = red
    if (assessmentType === 'financial' || assessmentType === 'nutritional') {
      switch (level.toLowerCase()) {
        case 'high':
          return 'progress-circle-high'; // Green
        case 'moderate':
          return 'progress-circle-medium'; // Orange
        case 'low':
          return 'progress-circle-low'; // Red
        default:
          return 'progress-circle-unknown';
      }
    }

    // Standard logic for all other assessments
    switch (level.toLowerCase()) {
      case 'high':
      case 'excellent':
      case 'good':
      case 'excellent mental health':
      case 'good mental health':
        return 'progress-circle-high'; // Green
      case 'moderate':
      case 'fair':
      case 'moderate mental health':
        return 'progress-circle-medium'; // Orange
      case 'low':
      case 'poor':
      case 'needs mental health support':
        return 'progress-circle-low'; // Red
      case 'critical':
      case 'very high':
        return 'progress-circle-unknown'; // Gray or custom
      default:
        return 'progress-circle-unknown';
    }
  };

  // Helper function to get score label for assessment type
  const getScoreLabel = (type: string): string => {
    switch (type) {
      case 'fasdl':
        return 'Spiritual Integration';
      case 'facit-sp-12':
        return 'Spiritual Well-Being';
      case 'nutritional':
        return 'Diet Quality';
      case 'physical':
        return 'Wellness Score';
      case 'dass-21':
        return 'Emotio-Psycho-Rational Score';
      case 'phq-9':
        return 'Depression Score';
      case 'gad-7':
        return 'Anxiety Score';
      case 'gender':
        return 'Gender Equity Score';
      case 'legendarial-swls':
        return 'Life Satisfaction Score';
      case 'legendarial-pil':
        return 'Purpose in Life Score';
      case 'religio-brief-rcope':
        return 'Religious Coping Score';
      case 'religio-cvscale':
        return 'Religious Values Score';
      case 'religio-tvas':
        return 'Cultural Tradition Score';
      case 'socio-relational':
        return 'Social Connections Score';
      case 'financial':
        return 'Financial Wellness Score';
      case 'environmental':
        return 'Environmental Impact Score';
      case 'occupational-cbi':
      case 'occupational-uwes':
      case 'occupational-employee':
        return 'Workplace Wellness Score';
      default:
        return 'Assessment Score';
    }
  };

  // Helper function to calculate percentage and get status text
  const getAssessmentDisplayData = (assessment: any, type: string) => {
    let level = 'Please Complete the assessments first';
    let score = 0;
    let maxScore = 100;
    let percentage = 0;

    // Check if assessment has the expected structure
    if (assessment && (
        (assessment.assessmentData && assessment.assessmentData.scores && assessment.assessmentData.results) ||
        (assessment.scores && assessment.results)
      )) {
      try {
        // Use the unified scoring system
        const scoreResult = computeAssessmentScore(assessment, type as keyof typeof import('../logic/scoring').scoringConfig);

        score = scoreResult.raw;
        maxScore = scoreResult.max;
        percentage = scoreResult.percent;
        level = scoreResult.level;

        // Override level with backend result if available (for backward compatibility)
        const assessmentResults = assessment.assessmentData ? assessment.assessmentData.results : assessment.results;
        const backendLevel = assessmentResults?.level;
        if (backendLevel) {
          level = backendLevel.charAt(0).toUpperCase() + backendLevel.slice(1).toLowerCase();
        }
      } catch (error) {
        console.error(`Error computing score for assessment type ${type}:`, error);
        // Fallback to basic calculation if type not supported
        const scores = assessment?.scores ?? assessment?.assessmentData?.scores ?? {};
        score = Number(scores.overall ?? 0);
        percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
      }
    }

    const scoreLabel = getScoreLabel(type);
    const result = { score, maxScore, percentage, level, scoreLabel };
    return result;
  };

  // Helper function to get status indicators
  const getStatusIndicators = (assessment: any, type: string) => {
    const mainIndicators: any[] = [];
    let updateIndicator: any = null;

    // Add specific indicators based on assessment type
    switch (type) {
      case 'fasdl':
      case 'facit-sp-12':
        mainIndicators.push(
          { BadgeIcon: BadgeCheck, text: 'Spiritual Well-Being', status: 'positive' },
          { BadgeIcon: BadgeCheck, text: 'Faith Integration', status: 'positive' }
        );
        updateIndicator = { icon: 'schedule', text: 'Updated 2 days ago', status: 'neutral' };
        break;
      case 'dass-21':
        mainIndicators.push(
          { BadgeIcon: BadgeCheck, text: 'Emotio-Psycho-Rational', status: 'positive' },
          { BadgeIcon: BadgeAlert, text: 'Stress/Anxiety', status: 'negative' }
        );
        updateIndicator = { icon: 'schedule', text: 'Updated yesterday', status: 'neutral' };
        break;
      case 'phq-9':
        mainIndicators.push(
          { BadgeIcon: BadgeCheck, text: 'Mood Stability', status: 'positive' },
          { BadgeIcon: BadgeAlert, text: 'Depression Symptoms', status: 'negative' }
        );
        updateIndicator = { icon: 'schedule', text: 'Updated 2 days ago', status: 'neutral' };
        break;
      case 'gad-7':
        mainIndicators.push(
          { BadgeIcon: BadgeCheck, text: 'Calmness', status: 'positive' },
          { BadgeIcon: BadgeAlert, text: 'Anxiety Levels', status: 'negative' }
        );
        updateIndicator = { icon: 'schedule', text: 'Updated yesterday', status: 'neutral' };
        break;
      case 'financial': {
        // Only show a single completion indicator for financial
        const isFinancialCompleted = assessment && (
          (assessment.scores && assessment.results) ||
          (assessment.assessmentData && assessment.assessmentData.scores && assessment.assessmentData.results)
        );
        mainIndicators.push(
          { BadgeIcon: isFinancialCompleted ? BadgeCheck : BadgeAlert, text: isFinancialCompleted ? 'Completed' : 'Not Completed', status: isFinancialCompleted ? 'positive' : 'negative' }
        );
        // Calculate actual last updated time using local timezone
        let financialUpdateText = 'Not completed';
        if (assessment && assessment.createdAt) {
          const createdDate = new Date(assessment.createdAt);
          const now = new Date();
          const todayStr = now.toLocaleDateString();
          const createdDateStr = createdDate.toLocaleDateString();
          const today = new Date(todayStr);
          const created = new Date(createdDateStr);
          const diffTime = today.getTime() - created.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays === 0) {
            financialUpdateText = 'Updated today';
          } else if (diffDays === 1) {
            financialUpdateText = 'Updated yesterday';
          } else if (diffDays <= 7) {
            financialUpdateText = `Updated ${diffDays} days ago`;
          } else {
            financialUpdateText = `Updated ${formatDate(createdDate)}`;
          }
        }
        updateIndicator = { icon: 'schedule', text: financialUpdateText, status: 'neutral' };
        break;
      }
      case 'mental':
        mainIndicators.push(
          { BadgeIcon: BadgeCheck, text: 'Mindfulness', status: 'positive' },
          { BadgeIcon: BadgeAlert, text: 'Stress Levels', status: 'negative' },
          { BadgeIcon: BadgeCheck, text: 'Sleep Quality', status: 'positive' }
        );
        updateIndicator = { icon: 'schedule', text: 'Updated yesterday', status: 'neutral' };
        break;
      case 'physical':
        // Show the three physical assessment categories
        const physicalScores = assessment.scores?.categories || assessment.assessmentData?.scores?.categories || [];
        if (physicalScores.length > 0) {
          physicalScores.forEach((category: any) => {
            const categoryName = category.name || '';
            const status = category.score >= 50 ? 'positive' : 'negative'; // treat >=50 as completed

            let displayName = '';
            if (categoryName === 'IPAQ-SF') displayName = 'Physical Activity';
            else if (categoryName === 'PSQI') displayName = 'Sleep Quality';

            if (displayName) {
              mainIndicators.push({
                BadgeIcon: status === 'positive' ? BadgeCheck : BadgeAlert,
                text: `${displayName} (${category.score}%)`,
                status: status
              });
            }
          });
        } else {
          // Fallback if no category data
          mainIndicators.push(
            { BadgeIcon: BadgeCheck, text: 'Physical Assessment', status: 'positive' }
          );
        }
        updateIndicator = { icon: 'schedule', text: 'Updated recently', status: 'neutral' };
        break;
      default:
        // Check if assessment is actually completed (has scores and results)
        const isCompleted = assessment && (
          (assessment.scores && assessment.results) ||
          (assessment.assessmentData && assessment.assessmentData.scores && assessment.assessmentData.results)
        );
        mainIndicators.push(
          { BadgeIcon: isCompleted ? BadgeCheck : BadgeAlert, text: isCompleted ? 'Completed' : 'Not Completed', status: isCompleted ? 'positive' : 'negative' }
        );

        // Calculate actual last updated time using local timezone
        let updateText = 'Not completed';
        if (assessment && assessment.createdAt) {
          const createdDate = new Date(assessment.createdAt);
          const now = new Date();

          // Convert both dates to local timezone date strings for comparison
          const todayStr = now.toLocaleDateString();
          const createdDateStr = createdDate.toLocaleDateString();

          // Calculate difference in days using date strings to avoid timezone issues
          const today = new Date(todayStr);
          const created = new Date(createdDateStr);
          const diffTime = today.getTime() - created.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays === 0) {
            updateText = 'Updated today';
          } else if (diffDays === 1) {
            updateText = 'Updated yesterday';
          } else if (diffDays <= 7) {
            updateText = `Updated ${diffDays} days ago`;
          } else {
            updateText = `Updated ${formatDate(createdDate)}`;
          }
        }
        updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };
    }

    return { mainIndicators, updateIndicator };
  };

  // Render combined socio-relational assessment card
  const renderCombinedSocioRelationalAssessment = (socioRationalAssessment: SocioRationalAssessmentResult) => {
    // Calculate combined score using only the latest assessment for each subcategory (STAR and MSPSS)
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Socio-Relational Assessment Score';

    // Find latest STAR (selectedCategory: 1) and MSPSS (selectedCategory: 2)
    const latestStar = socioRationalAssessments
      .filter(a => (a.selectedCategory === 1 || (a.assessmentData && a.assessmentData.selectedCategory === 1)))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const latestMSPSS = socioRationalAssessments
      .filter(a => (a.selectedCategory === 2 || (a.assessmentData && a.assessmentData.selectedCategory === 2)))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    // Compute percentages for each if completed
    const starCompleted = latestStar && ((latestStar.scores && latestStar.results) || (latestStar.assessmentData && latestStar.assessmentData.scores && latestStar.assessmentData.results));
    const mspssCompleted = latestMSPSS && ((latestMSPSS.scores && latestMSPSS.results) || (latestMSPSS.assessmentData && latestMSPSS.assessmentData.scores && latestMSPSS.assessmentData.results));

    let starPercent = 0;
    let mspssPercent = 0;
    if (starCompleted) {
      // STAR: out of 20
      let score = latestStar.scores?.socioRelationalScore ?? latestStar.assessmentData?.scores?.socioRelationalScore ?? 0;
      starPercent = Math.round((score / 20) * 100);
    }
    if (mspssCompleted) {
      // MSPSS: out of 7 (mean)
      let score = latestMSPSS.scores?.socioRelationalScore ?? latestMSPSS.assessmentData?.scores?.socioRelationalScore ?? 0;
      mspssPercent = Math.round((score / 7) * 100);
    }

    // Average only the available completed subcategories
    const completedPercents = [];
    if (starCompleted) completedPercents.push(starPercent);
    if (mspssCompleted) completedPercents.push(mspssPercent);
    if (completedPercents.length > 0) {
      combinedScore = Math.round(completedPercents.reduce((sum, p) => sum + p, 0) / completedPercents.length);
    }

    // Determine level based on combined percentage score
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];
    if (completedPercents.length > 0) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Socio-Relational Assessment Completed', status: 'positive' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];
    const hasStar = !!starCompleted;
    const hasMSPSS = !!mspssCompleted;
    completionIndicators.push({
      BadgeIcon: hasStar ? BadgeCheck : BadgeAlert,
      text: 'Gender Sensitivity STAR',
      status: hasStar ? 'completed' : 'pending',
      badgeType: hasStar ? 'check' : 'alert'
    });
    completionIndicators.push({
      BadgeIcon: hasMSPSS ? BadgeCheck : BadgeAlert,
      text: 'MSPSS',
      status: hasMSPSS ? 'completed' : 'pending',
      badgeType: hasMSPSS ? 'check' : 'alert'
    });

    // Update indicator: use the most recent of the two assessments
    let updateText = 'Not completed';
    let latestDate: Date | null = null;
    if (starCompleted && latestStar && latestStar.createdAt) {
      latestDate = new Date(latestStar.createdAt);
    }
    if (mspssCompleted && latestMSPSS && latestMSPSS.createdAt) {
      const mspssDate = new Date(latestMSPSS.createdAt);
      if (!latestDate || mspssDate > latestDate) {
        latestDate = mspssDate;
      }
    }
    if (latestDate) {
      const now = new Date();
      const diffMs = now.getTime() - latestDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        updateText = 'Updated just now';
      } else if (diffMinutes < 60) {
        updateText = `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        updateText = `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        updateText = `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }
    const updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };

    // Determine assessment status
    const isCompleted = completedPercents.length > 0;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key="socio-relational-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level)}`}
                style={{ '--progress': (combinedScore / combinedMaxScore) } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text" style={{ color: getScoreColor(level, combinedScore, combinedMaxScore) }}>
              <div className="status-level">{level}</div>
              <div className="status-label">
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Socio-Relational Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('socio-relational')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('socio-relational', 'Socio-Relational')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get completed category percentages for assessment groups


  // Helper function to get category breakdown for each assessment type
  const getCategoryBreakdown = (assessmentType: string): { label: string; percentage: number; }[] => {
    const breakdown: { label: string; percentage: number; }[] = [];

    switch (assessmentType) {
      case 'spiritual':
        const fasdlCompleted = fasdlAssessment && fasdlAssessment.assessmentData && fasdlAssessment.assessmentData.scores && fasdlAssessment.assessmentData.results;
        const facitCompleted = facitAssessment && facitAssessment.assessmentData && facitAssessment.assessmentData.scores && facitAssessment.assessmentData.results;

        if (fasdlCompleted) {
          // FASDL: higher score = better (green)
          const fasdlRaw = fasdlAssessment.assessmentData.scores.overall || 0;
          const fasdlPercent = Math.round((fasdlRaw / 50) * 100);
          breakdown.push({
            label: 'FASDL',
            percentage: fasdlPercent
          });
        }
        if (facitCompleted) {
          // FACIT-Sp-12: higher score = better (green)
          const facitRaw = facitAssessment.assessmentData.scores.overall || 0;
          const facitPercent = Math.round((facitRaw / 48) * 100);
          breakdown.push({
            label: 'FACIT-Sp-12',
            percentage: facitPercent
          });
        }
        break;

      case 'physical':
        // Use LATEST data from the merged data (from /latest endpoint)
        const latestPhysicalBreakdown = assessmentData['physical']?.latest;
        
        if (latestPhysicalBreakdown) {
          const categories = latestPhysicalBreakdown?.scores?.categories || latestPhysicalBreakdown?.assessmentData?.scores?.categories || [];
          
          categories.forEach((category: any) => {
            const categoryName = category.name || '';
            let displayName = '';
            
            // Only include IPAQ-SF and PSQI, skip General Physical Assessment
            if (categoryName === 'IPAQ-SF') displayName = 'Physical Activity';
            else if (categoryName === 'PSQI') displayName = 'Sleep Quality';
            else return; // Skip other categories

            // Only include categories that have a valid score
            const isCompleted = typeof category.score === 'number' && category.score !== null && category.score !== undefined;
            if (displayName && isCompleted) {
              breakdown.push({
                label: displayName,
                percentage: Math.round(category.score) // Already in percentage format
              });
            }
          });
        }
        break;

      case 'legendarial':
        const swlsAssessment = legendarialAssessments
          .filter(a => a.selectedCategory == 1)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const pilAssessment = legendarialAssessments
          .filter(a => a.selectedCategory == 2)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const swlsCompleted = swlsAssessment && swlsAssessment.scores && swlsAssessment.results;
        const pilCompleted = pilAssessment && pilAssessment.scores && pilAssessment.results;

        if (swlsCompleted) {
          breakdown.push({
            label: 'SWLS',
            percentage: Math.round(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100)
          });
        }
        if (pilCompleted) {
          breakdown.push({
            label: 'PIL',
            percentage: Math.round(((pilAssessment.scores.pilTotal || 0) / 70) * 100)
          });
        }
        break;

      case 'religio':
        const briefRcopeAssessment = religioAssessments
          .filter(a => a.selectedCategory == 1)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const cvscaleAssessment = religioAssessments
          .filter(a => a.selectedCategory == 2)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const tvasAssessment = religioAssessments
          .filter(a => a.selectedCategory == 3)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const briefRcopeCompleted = briefRcopeAssessment && briefRcopeAssessment.scores && briefRcopeAssessment.results;
        const cvscaleCompleted = cvscaleAssessment && cvscaleAssessment.scores && cvscaleAssessment.results;
        const tvasCompleted = tvasAssessment && tvasAssessment.scores && tvasAssessment.results;

        if (briefRcopeCompleted) {
          // Brief RCOPE: higher score = better (green)
          const prcTotal = briefRcopeAssessment.scores.prcTotal || 0;
          const nrcTotal = briefRcopeAssessment.scores.nrcTotal || 0;
          const rcopePercent = Math.round(((prcTotal - nrcTotal + 21) / 42) * 100);
          breakdown.push({
            label: 'Brief RCOPE',
            percentage: rcopePercent
          });
        }
        if (cvscaleCompleted) {
          // CV Scale: higher score = better (green)
          const cvscaleRaw = cvscaleAssessment.scores.cvscaleTotal || 0;
          const cvscalePercent = Math.round((cvscaleRaw / 5) * 100);
          breakdown.push({
            label: 'CV Scale',
            percentage: cvscalePercent
          });
        }
        if (tvasCompleted) {
          // TVAS: higher score = better (green)
          const tvasRaw = tvasAssessment.scores.respectForTraditionTotal || 0;
          const tvasPercent = Math.round((tvasRaw / 50) * 100);
          breakdown.push({
            label: 'TVAS',
            percentage: tvasPercent
          });
        }
        break;

      case 'mental-health':
        // Get mental-health history to find latest completed assessments by selectedCategory
        const mentalHealthBreakdownHistory = assessmentData['mental-health']?.history as EmotioPsychoRationalAssessmentResult[] || [];
        
        // DASS-21 (selectedCategory: 1) - get latest
        const dassItem = mentalHealthBreakdownHistory
          .filter(item => item.selectedCategory == 1 && item.scores && item.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (dassItem) {
          const dassRaw = dassItem.scores.overall || 0;
          const dassPercent = Math.round(clampPercentage((dassRaw / 42) * 100));
          breakdown.push({
            label: 'DASS-21',
            percentage: dassPercent
          });
        }
        
        // PHQ-9 (selectedCategory: 2) - get latest
        const phqItem = mentalHealthBreakdownHistory
          .filter(item => item.selectedCategory == 2 && item.scores && item.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (phqItem) {
          const phqRaw = phqItem.scores.overall || 0;
          const phqPercent = Math.round(clampPercentage((phqRaw / 27) * 100));
          breakdown.push({
            label: 'PHQ-9',
            percentage: phqPercent
          });
        }
        
        // GAD-7 (selectedCategory: 3) - get latest
        const gadItem = mentalHealthBreakdownHistory
          .filter(item => item.selectedCategory == 3 && item.scores && item.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (gadItem) {
          const gadRaw = gadItem.scores.overall || 0;
          const gadPercent = Math.round(clampPercentage((gadRaw / 21) * 100));
          breakdown.push({
            label: 'GAD-7',
            percentage: gadPercent
          });
        }
        
        // Gender Sensitivity (selectedCategory: 4) - get latest
        const genderItem = mentalHealthBreakdownHistory
          .filter(item => item.selectedCategory == 4 && item.scores && item.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (genderItem) {
          const genderRaw = genderItem.scores.overall || 0;
          const genderPercent = Math.round(clampPercentage((genderRaw / 20) * 100));
          breakdown.push({
            label: 'Gender Sensitivity',
            percentage: genderPercent
          });
        }
        break;

      case 'occupational':
        const cbiAssessment = occupationalAssessments
          .filter(a => a.selectedCategory == 1)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const uwesAssessment = occupationalAssessments
          .filter(a => a.selectedCategory == 2)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const employeeEngagementAssessment = occupationalAssessments
          .filter(a => a.selectedCategory == 3)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        const cbiCompleted = cbiAssessment && cbiAssessment.scores && cbiAssessment.results;
        const uwesCompleted = uwesAssessment && uwesAssessment.scores && uwesAssessment.results;
        const employeeEngagementCompleted = employeeEngagementAssessment && employeeEngagementAssessment.scores && employeeEngagementAssessment.results;

        if (cbiCompleted) {
          const cbiRaw = cbiAssessment.scores.cbiTotal || 0;
          const cbiPercent = Math.round((cbiRaw / 100) * 100);
          breakdown.push({
            label: 'CBI',
            percentage: cbiPercent
          });
        }
        if (uwesCompleted) {
          breakdown.push({
            label: 'UWES',
            percentage: Math.round(((uwesAssessment.scores.uwesTotal || 0) / 6) * 100)
          });
        }
        if (employeeEngagementCompleted) {
          breakdown.push({
            label: 'Employee Engagement',
            percentage: Math.round(((employeeEngagementAssessment.scores.engagementTotal || 0) / 5) * 100)
          });
        }
        break;

      case 'nutritional':
        const nutritionalCompleted = nutritionalAssessment && nutritionalAssessment.scores && nutritionalAssessment.results;
        if (nutritionalCompleted) {
          breakdown.push({
            label: 'Nutritional',
            percentage: Math.round((nutritionalAssessment.scores.overall / 100) * 100)
          });
        }
        break;

      case 'environmental':
        const environmentalCompleted = environmentalAssessment && environmentalAssessment.scores && environmentalAssessment.results;
        if (environmentalCompleted) {
          breakdown.push({
            label: 'Environmental',
            percentage: Math.round((environmentalAssessment.scores.transformedScore / 100) * 100)
          });
        }
        break;

      case 'socio-relational':
        // Get latest assessment for each category
        const genderSensitivitySTAR = socioRationalAssessments
          .filter(a => a.selectedCategory === 1 && a.scores && a.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        const mspss = socioRationalAssessments
          .filter(a => a.selectedCategory === 2 && a.scores && a.results)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        
        if (genderSensitivitySTAR) {
          const score = genderSensitivitySTAR.scores?.socioRelationalScore || 0;
          breakdown.push({
            label: 'Gender Sensitivity STAR',
            percentage: Math.round((score / 20) * 100)
          });
        }
        if (mspss) {
          const score = mspss.scores?.socioRelationalScore || 0;
          breakdown.push({
            label: 'MSPSS',
            percentage: Math.round((score / 7) * 100)
          });
        }
        break;

      case 'financial':
        // Get only the latest financial assessment (no subcategories)
        const latestFinancialAssessment = financialAssessments.length > 0 
          ? financialAssessments
              .filter(a => a.scores && a.results)
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
          : null;
        
        if (latestFinancialAssessment) {
          const score = latestFinancialAssessment.scores?.overall || latestFinancialAssessment.scores?.categoryScore || 0;
          breakdown.push({
            label: 'Financial Well-Being',
            percentage: Math.round((score / 80) * 100)
          });
        }
        break;
        }

    return breakdown;
  };

// Function to show progress chart for a specific assessment
const showProgressChart = (assessmentType: string, title: string) => {
  // Get category breakdown for the assessment type
  const breakdown = getCategoryBreakdown(assessmentType);

  // Convert breakdown to chart data format
  const chartData = breakdown.map((item, itemIndex) => {
    let fillColor = '#6b7280'; // Default gray
    
    // Color mapping by assessment type
    const colorMap: { [key: string]: { [key: string]: string } } = {
      'mental-health': {
        'DASS-21': '#BEE1E6',
        'PHQ-9': '#BEE1E6',
        'GAD-7': '#BEE1E6',
        'Gender Sensitivity': '#BEE1E6'
      },
      'spiritual': {
        'FASDL': '#BFE9DC', // Changed from light purple to teal
        'FACIT-Sp-12': '#BFE9DC' // Changed from light purple to teal
      },
      'physical': {
        'Physical Activity': '#FFD6A5',
        'Sleep Quality': '#FFD6A5'
      },
      'nutritional': {
        'Nutritional': '#CDEAC0'
      },
      'environmental': {
        'Environmental': '#BFE9DC' // Changed from pale green to teal
      },
      'financial': {
        'Financial Well-Being': '#F9E79F',
        'Budgeting': '#F9E79F',
        'Savings': '#F9E79F',
        'Investment': '#F9E79F'
      },
      'legendarial': {
        'SWLS': '#FFB5C8',
        'PIL': '#FFB5C8'
      },
      'religio': {
        'Brief RCOPE': '#FFF1C1',
        'CV Scale': '#FFF1C1',
        'TVAS': '#FFF1C1'
      },
      'socio-relational': {
        'Gender Sensitivity STAR': '#BFE9DC', // Changed from lavender to teal
        'MSPSS': '#BFE9DC' // Changed from lavender to teal
      },
      'occupational': {
        'CBI': '#BFE9DC', // Changed from mint to teal
        'UWES': '#BFE9DC', // Changed from mint to teal
        'Employee Engagement': '#BFE9DC' // Changed from mint to teal
      }
    };
    
    // Apply color mapping - use category colors for all subcategories
    if (colorMap[assessmentType] && colorMap[assessmentType][item.label]) {
      fillColor = colorMap[assessmentType][item.label];
    }
    
    return {
      name: item.label,
      percentage: item.percentage,
      score: item.percentage,
      maxScore: 100,
      fill: fillColor
    };
  });

  setProgressData(chartData);
  setProgressTitle(`${title} Breakdown`);
  setShowProgressModal(true);
};
  // Render combined Emotio-Psycho-Rational assessment card
  const renderCombinedMentalHealthAssessment = (dassAssessment: any, phqAssessment: any, gadAssessment: any, genderAssessment: any) => {
    // Declare mentalHealthHistory at the very top so it's available everywhere
    const mentalHealthHistory: any[] = assessmentData['mental-health']?.history || [];
    // Update indicator for last assessment
    let updateText = 'Not completed';
    const mostRecentMentalHealth = mentalHealthHistory
      .filter((item: any) => item.createdAt)
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (mostRecentMentalHealth && mostRecentMentalHealth.createdAt) {
      const createdAt = new Date(mostRecentMentalHealth.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        updateText = 'Updated just now';
      } else if (diffMinutes < 60) {
        updateText = `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        updateText = `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        updateText = `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }

    const updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };
      // Declare mentalHealthHistory once for use throughout this function
    // Calculate combined score
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Emotio-Psycho-Rational Assessment Score';
    let interpretation = '';

    // Use helper function to get only completed category percentages
    // mentalHealthHistory already declared above if needed
    // Helper to get the MOST RECENT item for each category (not just any) to prevent flashing old data
    const getLatestByCategory = (categoryId: number) => {
      return mentalHealthHistory
        .filter(item => item.selectedCategory == categoryId && item.scores && item.results && item.createdAt)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        [0] || null;
    };
    
    const dassItem = getLatestByCategory(1);
    const phqItem = getLatestByCategory(2);
    const gadItem = getLatestByCategory(3);
    const genderItem = getLatestByCategory(4);
    const percents = [];
    if (dassItem) {
      // For DASS-21, use the total score (overall), not subscales
      percents.push(Math.round(clampPercentage((dassItem.scores.overall / 42) * 100)));
    }
    if (phqItem) percents.push(Math.round(clampPercentage((phqItem.scores.overall / 27) * 100)));
    if (gadItem) percents.push(Math.round(clampPercentage((gadItem.scores.overall / 21) * 100)));
    if (genderItem) percents.push(Math.round(clampPercentage(100 - ((genderItem.scores.overall / 20) * 100))));
    const completedPercents = percents;
    combinedScore = percents.length > 0 ? Math.round(percents.reduce((sum, score) => sum + score, 0) / percents.length) : 0;

    // Get individual scores and completion status for indicators
    // Check the merged assessment data to find specific category completions
    // mentalHealthHistory already declared above if needed
    
    // DASS-21 is selectedCategory: 1
    const dassCompleted = mentalHealthHistory.some(item => 
      item.selectedCategory == 1 && 
      item.scores && 
      item.results
    );
    
    // PHQ-9 is selectedCategory: 2
    const phqCompleted = mentalHealthHistory.some(item => 
      item.selectedCategory == 2 && 
      item.scores && 
      item.results
    );
    
    // GAD-7 is selectedCategory: 3
    const gadCompleted = mentalHealthHistory.some(item => 
      item.selectedCategory == 3 && 
      item.scores && 
      item.results
    );
    
    // Gender Sensitivity is selectedCategory: 4
    const genderCompleted = mentalHealthHistory.some(item =>
      item.selectedCategory == 4 &&
      item.scores &&
      item.results
    );

    // Determine level and interpretation based on the most recent assessment result
    // Use the actual results.level from the backend instead of recalculating
    // Only show "Incomplete Assessment" if no assessments are completed at all
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
      interpretation = 'Complete mental health assessments to see your overall emotional well-being analysis.';
    } else {
      // Get the level and interpretation from the most recent completed assessment
      const mostRecentItem = mentalHealthHistory
        .filter((item: any) => item.scores && item.results)
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      
      if (mostRecentItem && mostRecentItem.results && mostRecentItem.results.level) {
        // Use the actual level from the backend
        const backendLevel = mostRecentItem.results.level;
        
        // Map backend level to display level
        switch (backendLevel.toLowerCase()) {
          case 'low':
            level = 'Good Mental Health';
            break;
          case 'moderate':
            level = 'Moderate Mental Health';
            break;
          case 'high':
            level = 'Needs Mental Health Support';
            break;
          default:
            level = backendLevel;
        }
        
        interpretation = mostRecentItem.results.interpretation || 'Assessment completed.';
      } else {
        // Fallback to combined score logic if no backend level available
        if (combinedScore >= 75) {
          level = 'Needs Mental Health Support';
          interpretation = `Your mental health indicators suggest significant concerns. Seek professional mental health support and consider immediate intervention.`;
        } else if (combinedScore >= 60) {
          level = 'Moderate Mental Health';
          interpretation = `Your mental health needs attention. Consider professional support and implement stress management techniques.`;
        } else if (combinedScore >= 40) {
          level = 'Good Mental Health';
          interpretation = `Your mental health shows positive indicators. Focus on maintaining balance and addressing any emerging concerns early.`;
        } else {
          level = 'Excellent Mental Health';
          interpretation = `Your mental health indicators are strong across all completed assessments. Continue maintaining healthy habits and coping strategies.`;
        }
      }

      // Get DASS-21 subscale information if completed (append to interpretation)
      let dassSubscaleInfo = '';
      if (dassCompleted) {
        const dassItem = mentalHealthHistory.find(item => 
          item.selectedCategory === 1 && 
          item.scores && 
          item.results
        );
        if (dassItem && dassItem.scores.subscales) {
          const subscales = dassItem.scores.subscales;
          const depressionScore = (subscales as any).depression || 0;
          const anxietyScore = (subscales as any).anxiety || 0;
          const stressScore = (subscales as any).stress || 0;
          
          // Determine severity levels for each subscale
          const getSeverityLevel = (score: number, subscale: string) => {
            if (subscale === 'depression') {
              if (score <= 9) return 'Normal';
              else if (score <= 13) return 'Mild';
              else if (score <= 20) return 'Moderate';
              else if (score <= 27) return 'Severe';
              else return 'Extremely Severe';
            } else if (subscale === 'anxiety') {
              if (score <= 7) return 'Normal';
              else if (score <= 9) return 'Mild';
              else if (score <= 14) return 'Moderate';
              else if (score <= 19) return 'Severe';
              else return 'Extremely Severe';
            } else if (subscale === 'stress') {
              if (score <= 14) return 'Normal';
              else if (score <= 18) return 'Mild';
              else if (score <= 25) return 'Moderate';
              else if (score <= 33) return 'Severe';
              else return 'Extremely Severe';
            }
            return 'Unknown';
          };
          
          const depressionLevel = getSeverityLevel(depressionScore, 'depression');
          const anxietyLevel = getSeverityLevel(anxietyScore, 'anxiety');
          const stressLevel = getSeverityLevel(stressScore, 'stress');
          
          dassSubscaleInfo = ` DASS-21 subscales: Depression (${depressionScore}/42 - ${depressionLevel}), Anxiety (${anxietyScore}/42 - ${anxietyLevel}), Stress (${stressScore}/42 - ${stressLevel}).`;
          
          // Append subscale info to interpretation
          if (!interpretation.includes('DASS-21')) {
            interpretation += dassSubscaleInfo;
          }
        }
      }
    }

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];

    // Add completion status indicators
    if (dassCompleted && phqCompleted && gadCompleted && genderCompleted) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Emotio-Psycho-Rational Assessment Completed', status: 'positive' }
      );
    } else if (dassCompleted || phqCompleted || gadCompleted || genderCompleted) {
      mainIndicators.push(
        { icon: 'schedule', text: 'Emotio-Psycho-Rational Assessment Incomplete', status: 'neutral' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];

    // DASS-21 status
    completionIndicators.push({
      BadgeIcon: dassCompleted ? BadgeCheck : BadgeAlert,
      text: 'DASS-21',
      status: dassCompleted ? 'completed' : 'pending',
      badgeType: dassCompleted ? 'check' : 'alert'
    });
    completionIndicators.push({
      BadgeIcon: phqCompleted ? BadgeCheck : BadgeAlert,
      text: 'PHQ-9',
      status: phqCompleted ? 'completed' : 'pending',
      badgeType: phqCompleted ? 'check' : 'alert'
    });
    completionIndicators.push({
      BadgeIcon: gadCompleted ? BadgeCheck : BadgeAlert,
      text: 'GAD-7',
      status: gadCompleted ? 'completed' : 'pending',
      badgeType: gadCompleted ? 'check' : 'alert'
    });
    completionIndicators.push({
      BadgeIcon: genderCompleted ? BadgeCheck : BadgeAlert,
      text: 'Gender Sensitivity',
      status: genderCompleted ? 'completed' : 'pending',
      badgeType: genderCompleted ? 'check' : 'alert'
    });

    const isCompleted = dassCompleted || phqCompleted || gadCompleted || genderCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key="mental-health-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level)}`}
                style={{
                  '--progress': (combinedScore / combinedMaxScore),
                  borderColor: (() => {
                    switch (level?.toLowerCase()) {
                      case 'low': return '#059669'; // Green (good)
                      case 'moderate mental health': return '#ea580c'; // Orange
                      case 'high': return '#dc2626'; // Red (bad)
                      case 'needs mental health support': return '#dc2626'; // Red (bad)
                      case 'excellent mental health': return '#059669'; // Green (excellent)
                      case 'good mental health': return '#059669'; // Green (good)
                      case 'incomplete assessment': return '#6B7280'; // Gray (incomplete)
                      default: return '#6B7280';
                    }
                  })()
                } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text">
              <div className="status-level" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'low': return '#059669'; // Green (good)
                  case 'moderate mental health': return '#ea580c'; // Orange
                  case 'high': return '#dc2626'; // Red (bad)
                  case 'needs mental health support': return '#dc2626'; // Red (bad)
                  case 'excellent mental health': return '#059669'; // Green (excellent)
                  case 'good mental health': return '#059669'; // Green (good)
                  default: return '#6B7280';
                }
              })() }}>
                {level}
              </div>
              <div className="status-label" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'low': return '#059669'; // Green (good)
                  case 'moderate mental health': return '#ea580c'; // Orange
                  case 'high': return '#dc2626'; // Red (bad)
                  case 'needs mental health support': return '#dc2626'; // Red (bad)
                  case 'excellent mental health': return '#059669'; // Green (excellent)
                  case 'good mental health': return '#059669'; // Green (good)
                  default: return '#6B7280';
                }
              })() }}>
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Emotio-Psycho-Rational-Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('mental-health')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('mental-health', 'Emotio-Psycho-Rational')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render combined legendarial assessment card
  const renderCombinedLegendarialAssessment = (legendarialAssessments: LegendarialAssessmentResult[]) => {
    // Calculate combined score
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Legendarial Assessment Score';

    // Use helper function to get only completed category percentages
    const completedPercents = getCompletedCategoryPercents('legendarial', undefined, legendarialAssessments);
    if (completedPercents.length > 0) {
      combinedScore = Math.round(completedPercents.reduce((sum, score) => sum + score, 0) / completedPercents.length);
    }

    // Get individual scores and completion status for indicators
    const swlsAssessment = legendarialAssessments.find(a => a.selectedCategory == 1);
    const pilAssessment = legendarialAssessments.find(a => a.selectedCategory == 2);

    const swlsCompleted = swlsAssessment && swlsAssessment.scores && swlsAssessment.results;
    const pilCompleted = pilAssessment && pilAssessment.scores && pilAssessment.results;

    // Determine level based on combined percentage score
    // Only show "Incomplete Assessment" if no assessments are completed at all
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];

    // Add completion status indicators
    if (swlsCompleted || pilCompleted) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Legendarial Assessment Completed', status: 'positive' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];

    // SWLS status
    completionIndicators.push({
      BadgeIcon: swlsCompleted ? BadgeCheck : BadgeAlert,
      text: 'SWLS',
      status: swlsCompleted ? 'completed' : 'pending',
      badgeType: swlsCompleted ? 'check' : 'alert'
    });

    // PIL status
    completionIndicators.push({
      BadgeIcon: pilCompleted ? BadgeCheck : BadgeAlert,
      text: 'PIL',
      status: pilCompleted ? 'completed' : 'pending',
      badgeType: pilCompleted ? 'check' : 'alert'
    });

    // Update indicator
    let updateText = 'Not completed';
    const mostRecentLegendarial = legendarialAssessments
      .filter(a => a.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (mostRecentLegendarial && mostRecentLegendarial.createdAt) {
      const createdAt = new Date(mostRecentLegendarial.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        updateText = 'Updated just now';
      } else if (diffMinutes < 60) {
        updateText = `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        updateText = `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        updateText = `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }

    const updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };

    // Determine assessment status
    const isCompleted = swlsCompleted || pilCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key="legendarial-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level)}`}
                style={{ '--progress': (combinedScore / combinedMaxScore) } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text">
              <div className="status-level" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>
                {level}
              </div>
              <div className="status-label" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Legendarial Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('legendarial')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('legendarial', 'Legendarial')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render combined religio assessment card
  const renderCombinedReligioAssessment = (religioAssessments: ReligioTraditioCulturalAssessmentResult[]) => {
    // Calculate combined score
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Religio-Traditio-Cultural Assessment Score';

    // Use helper function to get only completed category percentages
    const completedPercents = getCompletedCategoryPercents('religio', religioAssessments);
    if (completedPercents.length > 0) {
      combinedScore = Math.round(completedPercents.reduce((sum, score) => sum + score, 0) / completedPercents.length);
    }

    // Get individual scores and completion status for indicators
    const briefRcopeAssessment = religioAssessments.find(a => a.selectedCategory == 1);
    const cvscaleAssessment = religioAssessments.find(a => a.selectedCategory == 2);
    const tvasAssessment = religioAssessments.find(a => a.selectedCategory == 3);

    const briefRcopeCompleted = briefRcopeAssessment && briefRcopeAssessment.scores && briefRcopeAssessment.results;
    const cvscaleCompleted = cvscaleAssessment && cvscaleAssessment.scores && cvscaleAssessment.results;
    const tvasCompleted = tvasAssessment && tvasAssessment.scores && tvasAssessment.results;

    // Determine level based on combined percentage score
    // Only show "Incomplete Assessment" if no assessments are completed at all
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];

    // Add completion status indicators
    if (briefRcopeCompleted || cvscaleCompleted || tvasCompleted) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Religio-Traditio-Cultural Assessment Completed', status: 'positive' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];

    // Brief RCOPE status
    completionIndicators.push({
      BadgeIcon: briefRcopeCompleted ? BadgeCheck : BadgeAlert,
      text: 'Brief RCOPE',
      status: briefRcopeCompleted ? 'completed' : 'pending',
      badgeType: briefRcopeCompleted ? 'check' : 'alert'
    });

    // CVSCALE status
    completionIndicators.push({
      BadgeIcon: cvscaleCompleted ? BadgeCheck : BadgeAlert,
      text: 'CVSCALE',
      status: cvscaleCompleted ? 'completed' : 'pending',
      badgeType: cvscaleCompleted ? 'check' : 'alert'
    });

    // TVAS status
    completionIndicators.push({
      BadgeIcon: tvasCompleted ? BadgeCheck : BadgeAlert,
      text: 'TVAS',
      status: tvasCompleted ? 'completed' : 'pending',
      badgeType: tvasCompleted ? 'check' : 'alert'
    });

    // Update indicator
    let updateText = 'Not completed';
    const mostRecentReligio = religioAssessments
      .filter(a => a.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (mostRecentReligio && mostRecentReligio.createdAt) {
      const createdAt = new Date(mostRecentReligio.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        updateText = 'Updated just now';
      } else if (diffMinutes < 60) {
        updateText = `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        updateText = `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        updateText = `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }

    const updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };

    // Determine assessment status
    const isCompleted = briefRcopeCompleted || cvscaleCompleted || tvasCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key="religio-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level)}`}
                style={{ '--progress': (combinedScore / combinedMaxScore) } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text">
              <div className="status-level" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>
                {level}
              </div>
              <div className="status-label" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Religio-Traditio-Cultural Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('religio')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('religio', 'Religio-Traditio-Cultural')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render combined occupational assessment card
  const renderCombinedOccupationalAssessment = (occupationalAssessments: OccupationalAssessmentResult[]) => {
    // Calculate combined score
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Occupational Assessment Score';

    // Use helper function to get only completed category percentages
    const completedPercents = getCompletedCategoryPercents('occupational', undefined, undefined, occupationalAssessments);
    if (completedPercents.length > 0) {
      combinedScore = Math.round(completedPercents.reduce((sum, score) => sum + score, 0) / completedPercents.length);
    }

    // Get individual scores and completion status for indicators
    const cbiAssessment = occupationalAssessments.find(a => a.selectedCategory == 1);
    const uwesAssessment = occupationalAssessments.find(a => a.selectedCategory == 2);
    const employeeEngagementAssessment = occupationalAssessments.find(a => a.selectedCategory == 3);

    const cbiCompleted = cbiAssessment && cbiAssessment.scores && cbiAssessment.results;
    const uwesCompleted = uwesAssessment && uwesAssessment.scores && uwesAssessment.results;
    const employeeEngagementCompleted = employeeEngagementAssessment && employeeEngagementAssessment.scores && employeeEngagementAssessment.results;

    // Determine level based on combined percentage score
    // Only show "Incomplete Assessment" if no assessments are completed at all
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];

    // Add completion status indicators
    if (cbiCompleted || uwesCompleted || employeeEngagementCompleted) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Occupational Assessment Completed', status: 'positive' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];

    // CBI status
    completionIndicators.push({
      BadgeIcon: cbiCompleted ? BadgeCheck : BadgeAlert,
      text: 'CBI',
      status: cbiCompleted ? 'completed' : 'pending',
      badgeType: cbiCompleted ? 'check' : 'alert'
    });

    // UWES status
    completionIndicators.push({
      BadgeIcon: uwesCompleted ? BadgeCheck : BadgeAlert,
      text: 'UWES',
      status: uwesCompleted ? 'completed' : 'pending',
      badgeType: uwesCompleted ? 'check' : 'alert'
    });

    // Employee Engagement status
    completionIndicators.push({
      BadgeIcon: employeeEngagementCompleted ? BadgeCheck : BadgeAlert,
      text: 'Employee Engagement',
      status: employeeEngagementCompleted ? 'completed' : 'pending',
      badgeType: employeeEngagementCompleted ? 'check' : 'alert'
    });

    // Update indicator
    let updateText = 'Not completed';
    const mostRecentAssessment = occupationalAssessments
      .filter(a => a.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (mostRecentAssessment && mostRecentAssessment.createdAt) {
      const createdAt = new Date(mostRecentAssessment.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        updateText = 'Updated just now';
      } else if (diffMinutes < 60) {
        updateText = `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
      } else if (diffHours < 24) {
        updateText = `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else {
        updateText = `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      }
    }

    const updateIndicator = { icon: 'schedule', text: updateText, status: 'neutral' };

    // Determine assessment status
    const isCompleted = cbiCompleted || uwesCompleted || employeeEngagementCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    // Use color logic for Employee Engagement completion
    let completionColor = getScoreColor(level, combinedScore, combinedMaxScore, 'occupational');

    return (
      <div className="assessment-card" key="occupational-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level, 'occupational')}`}
                style={{ '--progress': (combinedScore / combinedMaxScore), borderColor: completionColor } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text" style={{ color: completionColor }}>
              <div className="status-level">{level} Burnout</div>
              <div className="status-label">
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Occupational Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('occupational')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('occupational', 'Occupational')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render combined spiritual assessment card
  const renderCombinedSpiritualAssessment = (fasdlAssessment: any, facitAssessment: any) => {
    // Calculate combined score for spiritual assessment (FASDL and FACIT-Sp-12)
    let combinedScore = 0;
    let combinedMaxScore = 100; // Always out of 100 for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Spiritual Assessment Score';

    // Get completion and scores for both assessments
    const fasdlCompleted = fasdlAssessment && fasdlAssessment.assessmentData && fasdlAssessment.assessmentData.scores && fasdlAssessment.assessmentData.results;
    const facitCompleted = facitAssessment && facitAssessment.assessmentData && facitAssessment.assessmentData.scores && facitAssessment.assessmentData.results;

    let fasdlScore = fasdlCompleted ? fasdlAssessment.assessmentData.scores.overall : null;
    let facitScore = facitCompleted ? facitAssessment.assessmentData.scores.overall : null;
    let fasdlPercent = fasdlScore !== null ? Math.round((fasdlScore / 50) * 100) : null;
    let facitPercent = facitScore !== null ? Math.round((facitScore / 48) * 100) : null;

    // Compute average if both completed, else use whichever is available
    if (fasdlPercent !== null && facitPercent !== null) {
      combinedScore = Math.round((fasdlPercent + facitPercent) / 2);
    } else if (fasdlPercent !== null) {
      combinedScore = fasdlPercent;
    } else if (facitPercent !== null) {
      combinedScore = facitPercent;
    }

    // Determine level based on combinedScore
    if (fasdlPercent === null && facitPercent === null) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }

    // Set color for both progress and status text
    let spiritualColor = '#6B7280';
    if (level === 'High') spiritualColor = '#059669'; // green
    else if (level === 'Moderate') spiritualColor = '#ea580c'; // orange
    else if (level === 'Low') spiritualColor = '#dc2626'; // red

    // Get spiritualHistory for update indicator and completion indicators
    const spiritualHistory = assessmentData['spiritual']?.history || [];
    // Only declare these once
    let completedSpiritualAssessments = spiritualHistory.filter((item: any) =>
      item.assessmentData?.scores && item.assessmentData?.results
    );
    let mostRecentSpiritual = completedSpiritualAssessments.sort((a: any, b: any) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

    // Create status indicators for individual assessments
    const mainIndicators: any[] = [];

    // Add completion status indicators
    if (fasdlCompleted || facitCompleted) {
      mainIndicators.push(
        { icon: 'check_circle', text: 'Spiritual Assessment Completed', status: 'positive' }
      );
    }

    // Create completion status indicators
    const completionIndicators: any[] = [];

    // FASDL status
    completionIndicators.push({
      BadgeIcon: fasdlCompleted ? BadgeCheck : BadgeAlert,
      text: 'FASDL',
      status: fasdlCompleted ? 'completed' : 'pending',
      badgeType: fasdlCompleted ? 'check' : 'alert'
    });

    // FACIT-Sp-12 status
    completionIndicators.push({
      BadgeIcon: facitCompleted ? BadgeCheck : BadgeAlert,
      text: 'FACIT-Sp-12',
      status: facitCompleted ? 'completed' : 'pending',
      badgeType: facitCompleted ? 'check' : 'alert'
    });

    // Update indicator
    const updateIndicator = mostRecentSpiritual ? (() => {
      const now = new Date();
      const createdAt = new Date(mostRecentSpiritual.createdAt);
      const diffMs = now.getTime() - createdAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      let text;
      if (diffDays > 0) {
        text = `Updated ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        text = `Updated ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      } else {
        text = `Updated ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return { icon: 'schedule', text, status: 'neutral' };
    })() : { icon: 'schedule', text: 'Updated 2 days ago', status: 'neutral' };

    // Determine assessment status
    const isCompleted = fasdlCompleted || facitCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    // (Removed duplicate declaration here)

    return (
      <div className="assessment-card" key="spiritual-combined">
        <div className="card-content">
          <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level)}`}
                style={{ '--progress': (combinedScore / combinedMaxScore), borderColor: spiritualColor } as React.CSSProperties}
              >
                <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
              </div>
            <div className="status-text" style={{ color: spiritualColor }}>
              <div className="status-level">{level}</div>
              <div className="status-label">
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Spiritual Assessment</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => fetchAssessmentHistory('spiritual')}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('spiritual', 'Spiritual')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render individual assessment card
  const renderCombinedPhysicalAssessment = (assessment: any) => {
    // Calculate combined score
    let combinedScore = 0;
    let combinedMaxScore = 100; // Standard 0-100 scale for display
    let level = 'Please Complete the assessments first';
    let scoreLabel = 'Physical Health Assessment Score';

    // Use helper function to get only completed category percentages
    const completedPercents = getCompletedCategoryPercents('physical');
    if (completedPercents.length > 0) {
      combinedScore = Math.round(completedPercents.reduce((sum, score) => sum + score, 0) / completedPercents.length);
    }

    // Get individual scores and completion status for indicators by checking all physical history
    const physicalHistory = assessmentData['physical']?.history || [];
    const allPhysicalCategories: any[] = [];
    
    // Collect all categories from all physical assessments
    physicalHistory.forEach((assessment: any) => {
      const categories = assessment?.scores?.categories || assessment?.assessmentData?.scores?.categories || [];
      categories.forEach((category: any) => {
        if (category.name && !allPhysicalCategories.some(c => c.name === category.name)) {
          allPhysicalCategories.push(category);
        }
      });
    });
    
    const ipaqCompleted = allPhysicalCategories.some((c: any) => c.name === 'IPAQ-SF');
    const psqiCompleted = allPhysicalCategories.some((c: any) => c.name === 'PSQI');

    // Determine level based on combined percentage score
    // Only show "Incomplete Assessment" if no assessments are completed at all
    if (completedPercents.length === 0) {
      level = 'Incomplete Assessment';
    } else if (combinedScore >= 75) {
      level = 'High';
    } else if (combinedScore >= 50) {
      level = 'Moderate';
    } else {
      level = 'Low';
    }


    // Create completion status indicators
    const completionIndicators: any[] = [];

    // IPAQ-SF status
    completionIndicators.push({
      BadgeIcon: ipaqCompleted ? BadgeCheck : BadgeAlert,
      text: 'Physical Activity',
      status: ipaqCompleted ? 'completed' : 'pending',
      badgeType: ipaqCompleted ? 'check' : 'alert'
    });

    // PSQI status
    completionIndicators.push({
      BadgeIcon: psqiCompleted ? BadgeCheck : BadgeAlert,
      text: 'Sleep Quality',
      status: psqiCompleted ? 'completed' : 'pending',
      badgeType: psqiCompleted ? 'check' : 'alert'
    });

    // Determine assessment status
    const isCompleted = ipaqCompleted || psqiCompleted;
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key="physical-combined">
        <div className="card-content">
          <div className="progress-section">
            <div
              className={`progress-circle ${getProgressCircleClass(level)}`}
              style={{ '--progress': (combinedScore / combinedMaxScore) } as React.CSSProperties}
            >
              <span>{combinedScore > 0 ? `${combinedScore}%` : '0%'}</span>
            </div>
            <div className="status-text">
              <div className="status-level" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>{level}</div>
              <div className="status-label" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>{scoreLabel}</div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">Physical Health</p>
            </div>


            {/* Completion status indicators */}
            <div className="completion-indicators">
              {completionIndicators.map((indicator, index) => (
                <div
                  key={index}
                  className={`completion-item ${indicator.badgeType}`}
                  title={indicator.badgeType === 'check'
                    ? 'Completed'
                    : `Please complete ${indicator.text} in Learn tab`
                  }
                >
                  <indicator.BadgeIcon className={`badge-${indicator.badgeType}`} size={16} />
                  <span>{indicator.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => {
                fetchAssessmentHistory('physical');
              }}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart('physical', 'Physical Health')}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssessmentCard = (assessment: any, title: string, badge: string, type: string, retakePath: string, historyType: string) => {
    const { score, maxScore, percentage, level, scoreLabel } = getAssessmentDisplayData(assessment, type);
    const { mainIndicators, updateIndicator } = getStatusIndicators(assessment, type);

    // Determine assessment status
    const isCompleted = assessment && (
      (assessment.scores && assessment.results) ||
      (assessment.assessmentData && assessment.assessmentData.scores && assessment.assessmentData.results)
    );
    const statusIcon = isCompleted ? 'check_circle' : 'schedule';

    return (
      <div className="assessment-card" key={assessment.id}>
        <div className="card-content">
            <div className="progress-section">
              <div
                className={`progress-circle ${getProgressCircleClass(level, type)}`}
                style={{ '--progress': percentage / 100 } as React.CSSProperties}
              >
                <span>{percentage > 0 ? `${percentage}%` : '0%'}</span>
              </div>
              <div className="status-text" style={{ color: getScoreColor(level, score, maxScore, type) }}>
              <div className="status-level" style={{ color: (() => {
                switch (level?.toLowerCase()) {
                  case 'high': return '#059669'; // Green
                  case 'moderate': return '#ea580c'; // Orange
                  case 'low': return '#dc2626'; // Red
                  default: return '#6B7280';
                }
              })() }}>
                {level}
              </div>
              <div className="status-label">
                {scoreLabel.endsWith(' Score')
                  ? scoreLabel.slice(0, -6)
                  : scoreLabel}
              </div>
            </div>
          </div>

          <div className="details-section">
            <div className="title-with-status">
              <p className="assessment-title">{title}</p>
            </div>
            {/* Completion status indicators */}
            <div className="completion-indicators">
              {mainIndicators && mainIndicators.length > 0 ? (
                // Show category-specific indicators for assessments with categories
                mainIndicators.map((indicator, index) => (
                  <div
                    key={index}
                    className={`completion-item ${indicator.status === 'positive' ? 'check' : indicator.status === 'negative' ? 'alert' : 'neutral'}`}
                    title={`${indicator.text} - ${indicator.status === 'positive' ? 'Good' : indicator.status === 'negative' ? 'Needs Attention' : 'Moderate'}`}
                  >
                    {indicator.BadgeIcon ? (
                      <indicator.BadgeIcon className={`badge-${indicator.status === 'positive' ? 'check' : indicator.status === 'negative' ? 'alert' : 'neutral'}`} size={16} />
                    ) : (
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>{indicator.icon}</span>
                    )}
                    <span>{indicator.text}</span>
                  </div>
                ))
              ) : (
                // Fallback for assessments without categories
                <div
                  className={`completion-item ${isCompleted ? 'check' : 'alert'}`}
                  title={isCompleted ? 'Completed' : `Please complete ${title} in Learn tab`}
                >
                  {isCompleted ? (
                    <BadgeCheck className="badge-check" size={16} />
                  ) : (
                    <BadgeAlert className="badge-alert" size={16} />
                  )}
                  <span>{title}</span>
                </div>
              )}
            </div>
            {updateIndicator && (
              <div className="update-indicator">
                <div className="indicator-item">
                  <span>{updateIndicator.text}</span>
                </div>
              </div>
            )}
          </div>

          <div className="actions-section">
            <button
              className="btn btn-history"
              onClick={() => {
                fetchAssessmentHistory(historyType);
              }}
            >
              <span className="material-symbols-outlined">history</span>
              History
            </button>
            <button
              className="btn btn-progress"
              onClick={() => showProgressChart(type, title)}
            >
              <span className="material-symbols-outlined">trending_up</span>
              Progress
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`insights-page ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="insights-container">
        {/* Header Section */}
        <div className="insights-header-section">
          <div className="header-content">
            <div className="header-text">
              <h2>Assessment Results</h2>
              <p>Your personalized assessment</p>
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="layout-container">
          {/* Main Content Area */}
          <div className="main-content">
            <div className="assessment-results-grid">
            {/* Combined Spiritual Assessment */}
            {(() => {
              const spiritualHistory = assessmentData['spiritual']?.history || [];
              const hasAnySpiritualCompleted = spiritualHistory.some(item => 
                (item.assessmentType === 'fasdl' || item.assessmentType === 'facit-sp-12') &&
                item.assessmentData?.scores && 
                item.assessmentData?.results
              );
              return hasAnySpiritualCompleted && renderCombinedSpiritualAssessment(fasdlAssessment, facitAssessment);
            })()}

            {/* Combined Emotio-Psycho-Rational Assessment */}
            {(() => {
              const mentalHealthHistory = assessmentData['mental-health']?.history || [];
              const hasAnyMentalHealthCompleted = mentalHealthHistory.some(item => 
                // Check both nested assessmentData structure and direct structure
                (item.assessmentData?.scores && item.assessmentData?.results) ||
                (item.scores && item.results)
              );
              // Only show if we have fresh data or no cache was available (to prevent stale cache flicker)
              return (hasLoadedFreshData || !hasCache) && hasAnyMentalHealthCompleted && renderCombinedMentalHealthAssessment(dassAssessment, phqAssessment, gadAssessment, genderAssessment);
            })()}

            {/* Nutritional Assessment */}
            {nutritionalAssessment && renderAssessmentCard(nutritionalAssessment, 'Nutritional Assessment', 'Phil-HEI', 'nutritional', '/panel/learn/nutritional-assessment', 'nutritional')}

            {/* Physical Assessment */}
            {physicalAssessment && renderCombinedPhysicalAssessment(physicalAssessment)}


            {/* Combined Life Satisfaction Assessment */}
            {(() => {
              const legendarialHistory = assessmentData['legendarial']?.history || [];
              const hasAnyLegendarialCompleted = legendarialHistory.some(item => {
                const data = item.assessmentData || item;
                return data.scores && data.results;
              });
              return hasAnyLegendarialCompleted && legendarialAssessments.length > 0 && renderCombinedLegendarialAssessment(legendarialAssessments);
            })()}

            {/* Combined Religious Assessment */}
            {(() => {
              const religioHistory = assessmentData['religio-traditio-cultural']?.history || [];
              const hasAnyReligioCompleted = religioHistory.some(item => {
                const data = item.assessmentData || item;
                return data.scores && data.results;
              });
              return hasAnyReligioCompleted && religioAssessments.length > 0 && renderCombinedReligioAssessment(religioAssessments);
            })()}
          
            {/* Combined Socio-Relational Assessment */}
            {socioRationalAssessments.length > 0 && renderCombinedSocioRelationalAssessment(socioRationalAssessments[0])}
          
          {/* Financial Assessment */}
            {(() => {
              const financialHistory = assessmentData['financial']?.history || [];
              const hasAnyFinancialCompleted = financialHistory.some(item => {
                const data = item.assessmentData || item;
                return data.scores && data.results;
              });
              return hasAnyFinancialCompleted && financialAssessments.length > 0 && renderAssessmentCard(financialAssessments[0], 'Financial Well-Being', 'IFDFW', 'financial', '/panel/learn/financial-assessment', 'financial');
            })()}
          
          {/* Environmental Assessment */}
            {environmentalAssessment && renderAssessmentCard(environmentalAssessment, 'Environmental Quality of Life', 'WHOQOL-BREF', 'environmental', '/panel/learn/environmental-assessment', 'environmental')}

            {/* Combined Occupational Assessment */}
            {(() => {
              const occupationalHistory = assessmentData['occupational']?.history || [];
              const hasAnyOccupationalCompleted = occupationalHistory.some(item => {
                const data = item.assessmentData || item;
                return data.scores && data.results;
              });
              return hasAnyOccupationalCompleted && occupationalAssessments.length > 0 && renderCombinedOccupationalAssessment(occupationalAssessments);
            })()}

          
          {/* Empty State */}
          {(() => {
            // Check if any assessment has at least one completed category
            const spiritualHistory = assessmentData['spiritual']?.history || [];
            const mentalHealthHistory = assessmentData['mental-health']?.history || [];
            const legendarialHistory = assessmentData['legendarial']?.history || [];
            const religioHistory = assessmentData['religio-traditio-cultural']?.history || [];
            const financialHistory = assessmentData['financial']?.history || [];
            const occupationalHistory = assessmentData['occupational']?.history || [];
            
            const hasAnySpiritualCompleted = spiritualHistory.some(item => 
              item.assessmentData?.scores && item.assessmentData?.results
            );
            const hasAnyMentalHealthCompleted = mentalHealthHistory.some(item => 
              (item.assessmentData?.scores && item.assessmentData?.results) ||
              (item.scores && item.results)
            );
            const hasAnyLegendarialCompleted = legendarialHistory.some(item => 
              item.scores && item.results
            );
            const hasAnyReligioCompleted = religioHistory.some(item => 
              item.scores && item.results
            );
            const hasAnyFinancialCompleted = financialHistory.some(item => 
              item.scores && item.results
            );
            const hasAnyOccupationalCompleted = occupationalHistory.some(item => 
              item.scores && item.results
            );
            
            const hasAnyAssessment = hasAnySpiritualCompleted || hasAnyMentalHealthCompleted ||
                                     nutritionalAssessment || physicalAssessment ||
                                     hasAnyLegendarialCompleted || hasAnyReligioCompleted ||
                                     socioRationalAssessments.length > 0 || hasAnyFinancialCompleted ||
                                     environmentalAssessment || hasAnyOccupationalCompleted;
            
            return !hasAnyAssessment && (
              <div className="empty-state">
                <h3>No Assessment Results Yet</h3>
                <p>Complete your assessments to see your personalized insights.</p>
              </div>
            );
          })()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="sidebar">
            {/* Overall Well-being Card */}
            <div className="sidebar-card overall-wellbeing">
              <div className="card-header">
                <h3>Overall Well-being</h3>
              </div>
              <div className="overall-score-content">
                {(() => {
                  // Get chart data
                  const allChartData = getChartData();
                  
                  // Check if user has any actual completed assessments
                  // Only show data if we have at least one assessment with real data
                  const hasRealData = allChartData.length > 0 && 
                    allChartData.some((item: any) => item.percentage > 0);
                  
                  if (!hasRealData) {
                    return (
                      <div className="chart-empty-state">
                        <span className="material-symbols-outlined">bar_chart</span>
                        <p>No assessment data available</p>
                        <p>Complete assessments to see your overall well-being</p>
                      </div>
                    );
                  }
                  
                  // Assessments with inverted logic (higher percentage = worse performance)
                  const invertedAssessments: string[] = [];
                  
                  // Normalize percentages for proper averaging
                  // For inverted assessments, invert the percentage so higher is always better
                  const normalizedScores = allChartData.map((item: any) => {
                    if (invertedAssessments.includes(item.name)) {
                      return 100 - item.percentage; // Invert: 45% becomes 55% (better)
                    }
                    return item.percentage;
                  });
                  // Include latest SOWB in overall average when user has SOWB data (updates every time they take an assessment)
                  const scoresForAverage = sowbHistory?.length > 0
                    ? [...normalizedScores, sowbHistory[0].percent]
                    : normalizedScores;
                  const overallAverage = Math.round(
                    scoresForAverage.reduce((sum: number, score: number) => sum + score, 0) / scoresForAverage.length
                  );
                  
                  const getAverageColor = (percentage: number) => {
                    if (percentage >= 75) return '#4CAF50'; // Green
                    if (percentage >= 50) return '#FF9800'; // Orange
                    return '#F44336'; // Red
                  };

                  // Last updated: most recent date from assessmentData (lastUpdated / latest / history) and SOWB
                  const dateCandidates: string[] = [];
                  Object.values(assessmentData).forEach((data: any) => {
                    if (data?.lastUpdated) dateCandidates.push(data.lastUpdated);
                    if (data?.latest?.createdAt) dateCandidates.push(data.latest.createdAt);
                    if (data?.history?.length) dateCandidates.push(data.history[0].createdAt);
                  });
                  if (sowbHistory?.length > 0 && sowbHistory[0].createdAt) dateCandidates.push(sowbHistory[0].createdAt);
                  const lastUpdatedDate = dateCandidates.length > 0
                    ? dateCandidates.reduce((latest, d) => (new Date(d) > new Date(latest) ? d : latest))
                    : null;

                  return (
                    <>
                      <div 
                        className="overall-score-circle"
                        style={{
                          '--progress': (overallAverage / 100),
                          '--color': getAverageColor(overallAverage)
                        } as React.CSSProperties}
                      >
                        <span className="overall-score-text">{overallAverage}%</span>
                      </div>
                      <div className="overall-score-label">
                        {overallAverage >= 75 ? 'Excellent' : overallAverage >= 50 ? 'Good' : 'Needs Attention'}
                      </div>
                      {lastUpdatedDate && (
                        <p className="overall-score-last-updated">
                          Last updated: {formatDate(lastUpdatedDate)}
                        </p>
                      )}
                      <button
                        className="expand-btn"
                        onClick={() => setShowChartModal(true)}
                        title="Expand chart"
                      >
                        <span className="material-symbols-outlined">open_in_full</span>
                        Expand
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Assessment History - from insights assessments */}
            <div className={`consultant-users ${isDarkMode ? 'consultant-users--dark' : ''}`}>
              <div className="consultant-users__assessment-history">
                  <div className="consultant-users__section-header">
                    <h3 className="consultant-users__section-title">Assessment History</h3>
                  </div>
                  {isLoading ? (
                    <div className="consultant-users__timeline">
                      <div className="consultant-users__timeline-item">
                        <div className="consultant-users__timeline-marker">
                          <div className="consultant-users__timeline-dot consultant-users__timeline-dot--join"></div>
                        </div>
                        <span className="consultant-users__timeline-join-text">Loading assessment history...</span>
                      </div>
                    </div>
                  ) : (() => {
                    // Collect ALL recent assessments chronologically (not one per type)
                    
                    // Helper function to get label for assessment
                    const getAssessmentLabel = (assessmentType: string, category?: number, testType?: string): string => {
                      switch (assessmentType) {
                        case 'spiritual':
                          return testType === 'fasdl' ? 'Spiritual - FASDL' : 'Spiritual - FACIT-Sp-12';
                        case 'nutritional':
                          return 'Nutritional';
                        case 'physical':
                          return 'Physical Health';
                        case 'mental-health':
                          if (category === 1) return 'Emotio-Psycho-Rational - DASS-21';
                          if (category === 2) return 'Emotio-Psycho-Rational - PHQ-9';
                          if (category === 3) return 'Emotio-Psycho-Rational - GAD-7';
                          if (category === 4) return 'Gender Sensitivity';
                          return 'Emotio-Psycho-Rational';
                        case 'legendarial':
                          return category === 1 ? 'Life Satisfaction - SWLS' : 'Life Satisfaction - PIL';
                        case 'religio-traditio-cultural':
                          if (category === 1) return 'Religio-Traditio-Cultural - Brief RCOPE';
                          if (category === 2) return 'Religio-Traditio-Cultural - CV Scale';
                          if (category === 3) return 'Religio-Traditio-Cultural - TVAS';
                          return 'Religio-Traditio-Cultural';
                        case 'socio-relational':
                          return category === 1 ? 'Socio-Relational - UCLA' : 'Socio-Relational - MSPSS';
                        case 'financial':
                          if (category === 1) return 'Financial - Budgeting';
                          if (category === 2) return 'Financial - Savings';
                          if (category === 3) return 'Financial - Investment';
                          return 'Financial';
                        case 'environmental':
                          return 'Environmental Quality';
                        case 'occupational':
                          if (category === 1) return 'Occupational - Burnout (CBI)';
                          if (category === 2) return 'Occupational - Engagement (UWES)';
                          if (category === 3) return 'Occupational - Employee Engagement';
                          return 'Occupational';
                        default:
                          return 'Assessment';
                      }
                    };

                    // Collect from all history arrays and latest items
                    const allHistoryItems: any[] = [];
                    
                    // Spiritual history
                    spiritualHistory.forEach(item => {
                      if (item?.assessmentData?.scores && item?.assessmentData?.results) {
                        const score = (item.assessmentData.scores as any)?.overall || 0;
                        const maxScore = (item.assessmentType === 'fasdl') ? 50 : 48;
                        const percent = Math.round((score / maxScore) * 100);
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('spiritual', undefined, item.assessmentType),
                          percent: clampPercentage(percent),
                          level: (item.assessmentData.results as any)?.level,
                          assessmentType: 'spiritual',
                          testType: item.assessmentType
                        });
                      }
                    });

                    // Mental health history
                    mentalHealthHistory.forEach(item => {
                      if (item?.scores && item?.results) {
                        let maxScore = 100, score = item.scores?.overall || 0;
                        if (item.selectedCategory === 1) maxScore = 42;
                        else if (item.selectedCategory === 2) maxScore = 27;
                        else if (item.selectedCategory === 3) maxScore = 21;
                        else if (item.selectedCategory === 4) maxScore = 20;
                        let percent = Math.round(clampPercentage((score / maxScore) * 100));
                        
                        // Store original percentage (don't invert yet)
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('mental-health', item.selectedCategory),
                          percent: clampPercentage(percent), // Original percentage
                          level: item.results?.level,
                          assessmentType: 'mental-health',
                          category: item.selectedCategory
                        });
                      }
                    });

                    // Legendarial history
                    legendarialHistory.forEach(item => {
                      if (item?.scores && item?.results) {
                        let scoreValue = 0, maxScore = 100;
                        if (item.selectedCategory === 1) {
                          scoreValue = item.scores.swlsTotal || 0;
                          maxScore = 35;
                        } else if (item.selectedCategory === 2) {
                          scoreValue = item.scores.pilTotal || 0;
                          maxScore = 70;
                        }
                        const percent = Math.round((scoreValue / maxScore) * 100);
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('legendarial', item.selectedCategory),
                          percent: clampPercentage(percent),
                          level: item.results?.level,
                          assessmentType: 'legendarial',
                          category: item.selectedCategory
                        });
                      }
                    });

                    // Socio-relational history
                    socioRationalAssessments.forEach(item => {
                      if (item?.scores && item?.results) {
                        const socioScore = item.scores?.socioRelationalScore || 0;
                        // MSPSS (category 2) is scored as mean (1-7), UCLA (category 1) is /20
                        const maxScore = item.selectedCategory === 2 ? 7 : 20;
                        const percent = Math.round(((socioScore) / maxScore) * 100);
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('socio-relational', item.selectedCategory),
                          percent: clampPercentage(percent),
                          level: item.results?.level,
                          assessmentType: 'socio-relational',
                          category: item.selectedCategory
                        });
                      }
                    });

                    // Religio history
                    religioHistory.forEach(item => {
                      if (item?.scores && item?.results) {
                        let percent = 50;
                        if (item.selectedCategory === 1) {
                          const prc = (item.scores.category1 || item.scores.prcTotal || 0);
                          const nrc = item.scores.nrcTotal || 0;
                          percent = Math.round(clampPercentage(((prc - nrc + 21) / 42) * 100));
                        } else if (item.selectedCategory === 2) {
                          percent = Math.round(clampPercentage(((item.scores.cvscaleTotal || 0) / 5) * 100));
                        } else if (item.selectedCategory === 3) {
                          percent = Math.round(clampPercentage(((item.scores.respectForTraditionTotal || 0) / 50) * 100));
                        }
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('religio-traditio-cultural', item.selectedCategory),
                          percent: clampPercentage(percent),
                          level: item.results?.level,
                          assessmentType: 'religio-traditio-cultural',
                          category: item.selectedCategory
                        });
                      }
                    });

                    // Occupational history
                    occupationalHistory.forEach(item => {
                      if (item?.scores && item?.results) {
                        let percent = 0;
                        if (item.selectedCategory === 1) {
                          percent = Math.round(clampPercentage(((item.scores?.cbiTotal || 0) / 100) * 100));
                        } else if (item.selectedCategory === 2) {
                          percent = Math.round(clampPercentage((1 - ((item.scores?.uwesTotal || 0) / 6)) * 100));
                        } else if (item.selectedCategory === 3) {
                          percent = Math.round(clampPercentage(((5 - (item.scores?.engagementTotal || 0)) / 5) * 100));
                        }
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('occupational', item.selectedCategory),
                          percent: percent,
                          level: item.results?.level,
                          assessmentType: 'occupational',
                          category: item.selectedCategory
                        });
                      }
                    });

                    // Financial history
                    financialAssessments.forEach(item => {
                      if (item?.scores && item?.results) {
                        const rawScore = item.scores?.overall || item.scores?.categoryScore || 0;
                        const percent = Math.round(clampPercentage((rawScore / 80) * 100));
                        allHistoryItems.push({
                          createdAt: item.createdAt,
                          label: getAssessmentLabel('financial', item.selectedCategory),
                          percent: clampPercentage(percent),
                          level: item.results?.level,
                          assessmentType: 'financial'
                        });
                      }
                    });

                    // Nutritional latest
                    const nutritionalLatest = assessmentData['nutritional']?.latest as any;
                    if (nutritionalLatest?.scores && nutritionalLatest?.results) {
                      allHistoryItems.push({
                        createdAt: nutritionalLatest.createdAt,
                        label: getAssessmentLabel('nutritional'),
                        percent: clampPercentage(Math.round((nutritionalLatest.scores?.overall || 0) / 100 * 100)),
                        level: nutritionalLatest.results?.level,
                        assessmentType: 'nutritional'
                      });
                    }

                    // Physical latest
                    const physicalLatest = assessmentData['physical']?.latest as any;
                    if (physicalLatest?.scores && physicalLatest?.results) {
                      let physicalPercent = 0;
                      const categories = (physicalLatest.scores?.categories || physicalLatest.assessmentData?.scores?.categories) as any[];
                      if (categories && categories.length > 0) {
                        const ipaqCategory = categories.find((c: any) => c.name === 'IPAQ-SF');
                        const psqiCategory = categories.find((c: any) => c.name === 'PSQI');
                        const categoryScores = [];
                        if (ipaqCategory && typeof ipaqCategory.score === 'number') {
                          categoryScores.push(Math.round(ipaqCategory.score));
                        }
                        if (psqiCategory && typeof psqiCategory.score === 'number') {
                          categoryScores.push(Math.round(psqiCategory.score));
                        }
                        if (categoryScores.length > 0) {
                          physicalPercent = Math.round(categoryScores.reduce((a: number, b: number) => a + b, 0) / categoryScores.length);
                        }
                      } else {
                        physicalPercent = physicalLatest.scores?.overall || 0;
                      }
                      allHistoryItems.push({
                        createdAt: physicalLatest.createdAt,
                        label: getAssessmentLabel('physical'),
                        percent: clampPercentage(physicalPercent),
                        level: physicalLatest.results?.level,
                        assessmentType: 'physical'
                      });
                    }

                    // Environmental latest
                    const environmentalLatest = assessmentData['environmental']?.latest as any;
                    if (environmentalLatest?.scores && environmentalLatest?.results) {
                      const envPercent = Math.round(clampPercentage((environmentalLatest.scores?.transformedScore || 0) / 100 * 100));
                      allHistoryItems.push({
                        createdAt: environmentalLatest.createdAt,
                        label: getAssessmentLabel('environmental'),
                        percent: envPercent,
                        level: environmentalLatest.results?.level,
                        assessmentType: 'environmental'
                      });
                    }

                    // Sort all items by date descending and take the top 4
                    const recentAssessments = allHistoryItems.sort((a, b) => 
                      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );
                    const displayed = recentAssessments.slice(0, ASSESSMENT_HISTORY_MAX_VISIBLE);

                    // Helper function to get color based on assessment type, category, and level

                    return displayed.length > 0 ? (
                      <>
                        <div className="consultant-users__timeline">
                          {displayed.map((item, index) => {
                            // Calculate proper level based on assessment type (matching modal logic)
                            let displayLevel = item.level || 'incomplete assessment';
                            const percent = item.percent; // Original percentage
                            let displayPercent = percent; // Display percentage (may be inverted)
                            const assessmentType = item.assessmentType;
                            const category = item.category;
                            const label = (item.label || '').toLowerCase();
                            
                            // For Gender Sensitivity, invert the display percentage
                            if (assessmentType === 'mental-health' && category === 4) {
                              displayPercent = 100 - percent;
                            }
                            
                            // Recalculate level for specific assessments that use percentage-based thresholds
                            if (assessmentType === 'financial') {
                              // Financial: thresholds based on percentage
                              if (percent >= 70) displayLevel = 'High';
                              else if (percent >= 40) displayLevel = 'Moderate';
                              else displayLevel = 'Low';
                            } else if (assessmentType === 'mental-health' && category !== 4 && (label.includes('dass-21') || label.includes('phq-9') || label.includes('gad-7'))) {
                              // Mental Health Symptoms: recalculate based on percentage
                              if (percent >= 67) displayLevel = 'High';
                              else if (percent >= 34) displayLevel = 'Moderate';
                              else displayLevel = 'Low';
                            } else if (assessmentType === 'mental-health' && category === 4) {
                              // Gender Sensitivity: use original percentage to determine level
                              if (percent >= 70) displayLevel = 'Low';
                              else if (percent >= 41) displayLevel = 'Moderate';
                              else displayLevel = 'High';
                            } else if (assessmentType === 'occupational' && category === 1) {
                              // CBI: burnout level from percentage
                              if (percent >= 70) displayLevel = 'High';
                              else if (percent >= 40) displayLevel = 'Moderate';
                              else displayLevel = 'Low';
                            } else if (assessmentType === 'occupational' && (category === 2 || category === 3)) {
                              // UWES and Employee Engagement (displayed as burnout): high burnout = High level
                              if (percent >= 70) displayLevel = 'High';
                              else if (percent >= 40) displayLevel = 'Moderate';
                              else displayLevel = 'Low';
                            } else if (assessmentType === 'religio-traditio-cultural' && category === 1) {
                              // Brief RCOPE: recalculate based on coping quality percentage
                              if (percent >= 75) displayLevel = 'High';
                              else if (percent >= 50) displayLevel = 'Moderate';
                              else displayLevel = 'Low';
                            }
                            // For other assessments, use stored level
                            
                            // Get color based on displayLevel and assessment type
                            const levelColor = (() => {
                              const levelLower = (displayLevel || '').toLowerCase();
                              
                              // Mental health symptom assessments (DASS-21, PHQ-9, GAD-7) - high score = bad = red
                              const isMentalHealthSymptoms = assessmentType === 'mental-health' && category !== 4;
                              
                              // Gender Sensitivity (mental-health category 4) - LOW level = good = green
                              const isGenderSensitivity = assessmentType === 'mental-health' && category === 4;
                              
                              // Burnout-related assessments
                              const isBurnoutAssessment = assessmentType === 'occupational' && (category === 1 || category === 2 || category === 3);
                              
                              // Financial assessment
                              const isFinancial = assessmentType === 'financial';
                              
                              if (isFinancial) {
                                // Financial: High = green, Moderate = orange, Low = red
                                if (levelLower === 'high') return '#059669'; // Green (good)
                                if (levelLower === 'moderate') return '#ea580c'; // Orange
                                if (levelLower === 'low') return '#dc2626'; // Red (bad)
                              } else if (isMentalHealthSymptoms) {
                                // Mental health symptoms: High = red (bad), Moderate = orange, Low = green (good)
                                if (levelLower === 'high') return '#dc2626'; // Red (bad)
                                if (levelLower === 'moderate') return '#ea580c'; // Orange
                                if (levelLower === 'low') return '#059669'; // Green (good)
                              } else if (isGenderSensitivity) {
                                // Gender Sensitivity: Low = green, Moderate = orange, High = red
                                if (levelLower === 'low') return '#059669'; // Green (good)
                                if (levelLower === 'moderate') return '#ea580c'; // Orange
                                if (levelLower === 'high') return '#dc2626'; // Red (bad)
                              } else if (isBurnoutAssessment) {
                                // Burnout: Low = green, Moderate = orange, High = red
                                if (levelLower === 'low') return '#059669'; // Green (good)
                                if (levelLower === 'moderate') return '#ea580c'; // Orange
                                if (levelLower === 'high') return '#dc2626'; // Red (bad)
                              } else {
                                // Default: High = green, Moderate = orange, Low = red
                                if (levelLower === 'high') return '#059669'; // Green (good)
                                if (levelLower === 'moderate') return '#ea580c'; // Orange
                                if (levelLower === 'low') return '#dc2626'; // Red (bad)
                              }
                              
                              // Default for incomplete or unknown
                              if (levelLower.includes('incomplete')) return '#6B7280'; // Gray
                              return '#6B7280'; // Default gray
                            })();
                            
                            return (
                              <div key={`${item.label}-${item.createdAt}`} className="consultant-users__timeline-item">
                                <div className="consultant-users__timeline-marker">
                                  <div className={`consultant-users__timeline-dot ${index === 0 ? 'consultant-users__timeline-dot--current' : 'consultant-users__timeline-dot--past'}`}></div>
                                  {index < displayed.length - 1 && (
                                    <div className="consultant-users__timeline-line"></div>
                                  )}
                                </div>
                                <div className="consultant-users__timeline-content">
                                  <div className="consultant-users__timeline-header">
                                    <span className={`consultant-users__timeline-label ${index === 0 ? 'consultant-users__timeline-label--current' : ''}`}>
                                      {index === 0 ? 'Latest' : formatDate(item.createdAt)}
                                    </span>
                                    {index === 0 && (
                                      <span className="consultant-users__timeline-date">
                                        {formatDate(item.createdAt)}
                                      </span>
                                    )}
                                  </div>
                                  <div className="consultant-users__timeline-score">
                                    <span className="consultant-users__timeline-score-number">
                                      {displayPercent}%
                                    </span>
                                    <span className="consultant-users__timeline-score-change" style={{ color: levelColor }}>
                                      {displayLevel}
                                    </span>
                                  </div>
                                  <p className="consultant-users__timeline-notes">
                                    {item.label}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {recentAssessments.length > ASSESSMENT_HISTORY_MAX_VISIBLE && (
                          <button
                            type="button"
                            className="consultant-users__show-all-btn"
                            onClick={() => {
                              fetchAllAssessmentHistory();
                              setShowHistoryModal(true);
                            }}
                          >
                            Show all
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="consultant-users__timeline">
                        <div className="consultant-users__timeline-item">
                          <div className="consultant-users__timeline-marker">
                            <div className="consultant-users__timeline-dot consultant-users__timeline-dot--join"></div>
                          </div>
                          <span className="consultant-users__timeline-join-text">No assessments yet</span>
                        </div>
                      </div>
                    );
                  })()}
              </div>
            </div>

            {/* Daily Inspiration Card */}
            <div className="sidebar-card daily-inspiration">
              <div className="card-header">
                <h3>Daily Inspiration</h3>
              </div>
              <div className="inspiration-content">
                {isLoadingInspiration ? (
                  <div className="loading-spinner">
                    <span className="material-symbols-outlined">hourglass_empty</span>
                    <p>Generating inspiration...</p>
                  </div>
                ) : inspirationError ? (
                  <div className="error-message">
                    <span className="material-symbols-outlined">error</span>
                    <p>{inspirationError}</p>
                  </div>
                ) : dailyInspiration ? (
                  <>
                <span className="material-symbols-outlined quote-icon">format_quote</span>
                    <p className="quote-text">"{dailyInspiration}"</p>
                    <p className="quote-author">Quote of the day</p>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined quote-icon">format_quote</span>
                    <p className="quote-text">"Every small step counts towards a healthier you."</p>
                    <p className="quote-author">Quote of the day</p>
                  </>
                )}
              </div>

              <div className="insights-section">
                <h4>Your Assessment Highlights:</h4>
                <div className="insights-list">
                  {(() => {
                    const assessments = [
                      { name: 'Spiritual', data: fasdlAssessment || facitAssessment, type: 'spiritual' },
                      { name: 'Nutritional', data: nutritionalAssessment, type: 'nutritional' },
                      { name: 'Physical', data: physicalAssessment, type: 'physical' },
                      { name: 'Emotio-Psycho-Rational', data: dassAssessment || phqAssessment || gadAssessment || genderAssessment, type: 'mental-health' },
                      { name: 'Financial', data: financialAssessments.length > 0 ? financialAssessments[0] : null, type: 'financial' },
                      { name: 'Environmental', data: environmentalAssessment, type: 'environmental' },
                      { name: 'Legendarial', data: legendarialAssessments.length > 0 ? legendarialAssessments[0] : null, type: 'legendarial' },
                      { name: 'Religio-Traditio-Cultural', data: religioAssessments.length > 0 ? religioAssessments[0] : null, type: 'religio' },
                      { name: 'Socio-Relational', data: socioRationalAssessments.length > 0 ? socioRationalAssessments[0] : null, type: 'socio-relational' },
                      { name: 'Occupational', data: occupationalAssessments.length > 0 ? occupationalAssessments[0] : null, type: 'occupational' }
                    ];

                    const validAssessments = assessments.filter(({ data, type }) => {
                      if (!data) return false;

                      // Check if assessment has valid data based on type
                      switch (type) {
                        case 'spiritual':
                          // Spiritual assessments have assessmentData
                          const spiritualData = data as SpiritualAssessmentResult;
                          return spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
                        case 'nutritional':
                          const nutritionalData = data as NutritionalAssessmentResult;
                          return nutritionalData.scores && nutritionalData.results;
                        case 'physical':
                          const physicalData = data as PhysicalAssessmentResult;
                          return (physicalData.scores && physicalData.results) ||
                                 (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results);
                        case 'mental-health':
                          // Emotio-Psycho-Rational combines multiple assessments - check if any exist
                          return dassAssessment || phqAssessment || gadAssessment || genderAssessment;
                        case 'financial':
                          const financialData = data as FinancialAssessmentResult;
                          return financialData.scores && financialData.results;
                        case 'environmental':
                          const environmentalData = data as EnvironmentalAssessmentResult;
                          return environmentalData.scores && environmentalData.results;
                        case 'legendarial':
                          const legendarialData = data as LegendarialAssessmentResult;
                          return legendarialData.scores && legendarialData.results;
                        case 'religio':
                          const religioData = data as ReligioTraditioCulturalAssessmentResult;
                          return religioData.scores && religioData.results;
                        case 'socio-relational':
                          const socioData = data as SocioRationalAssessmentResult;
                          return (socioData.scores && socioData.results) ||
                                 (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results);
                        case 'occupational':
                          const occupationalData = data as OccupationalAssessmentResult;
                          return occupationalData.scores && occupationalData.results;
                        default:
                          return false;
                      }
                    });

                    if (validAssessments.length === 0) {
                      return (
                        <div className="insight-item neutral">
                          <span className="material-symbols-outlined">info</span>
                          <div className="insight-content">
                            <p className="insight-title">Complete assessments to see highlights</p>
                            <p className="insight-text">Take assessments in the Learn tab to get personalized insights.</p>
                          </div>
                        </div>
                      );
                    }

                    // Calculate percentages using the same logic as the chart
                    const assessmentsWithPercentages = validAssessments.map(({ name, data, type }) => {
                      let percentage = 0;

                      switch (type) {
                        case 'spiritual':
                          const spiritualData = data as SpiritualAssessmentResult;
                          const fasdlCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;
                          const facitCompleted = spiritualData.assessmentData && spiritualData.assessmentData.scores && spiritualData.assessmentData.results;

                          if (fasdlCompleted && facitCompleted) {
                            const fasdlPercentage = Math.round((spiritualData.assessmentData.scores.overall / 50) * 100);
                            const facitPercentage = Math.round((spiritualData.assessmentData.scores.overall / 48) * 100);
                            percentage = Math.round((fasdlPercentage + facitPercentage) / 2);
                          } else if (fasdlCompleted) {
                            percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 50) * 100));
                          } else if (facitCompleted) {
                            percentage = Math.round(clampPercentage((spiritualData.assessmentData.scores.overall / 48) * 100));
                          }
                          break;

                        case 'mental-health':
                          // Emotio-Psycho-Rational combines multiple assessments - use global variables since data is not passed here
                          const dassCompleted = dassAssessment && dassAssessment.scores && dassAssessment.results;
                          const phqCompleted = phqAssessment && phqAssessment.scores && phqAssessment.results;
                          const gadCompleted = gadAssessment && gadAssessment.scores && gadAssessment.results;
                          const genderCompleted = genderAssessment && genderAssessment.scores && genderAssessment.results;

                          const percentages = [];
                          if (dassCompleted) {
                            // For DASS-21, use the overall score for percentage calculation
                            percentages.push(Math.round(clampPercentage((dassAssessment.scores.overall / 42) * 100)));
                          }
                          if (phqCompleted) percentages.push(Math.round(clampPercentage((phqAssessment.scores.overall / 27) * 100)));
                          if (gadCompleted) percentages.push(Math.round(clampPercentage((gadAssessment.scores.overall / 21) * 100)));
                          if (genderCompleted) percentages.push(Math.round(clampPercentage(100 - ((genderAssessment.scores.overall / 20) * 100))));

                          if (percentages.length > 0) {
                            percentage = Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length);
                          }
                          break;

                        case 'legendarial':
                          const legendarialData = data as LegendarialAssessmentResult;
                          const swlsAssessment = legendarialAssessments.find(a => a.selectedCategory === 1);
                          const pilAssessment = legendarialAssessments.find(a => a.selectedCategory === 2);

                          if (swlsAssessment && pilAssessment) {
                          const swlsPercentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                          const pilPercentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                            percentage = Math.round((swlsPercentage + pilPercentage) / 2);
                          } else if (swlsAssessment) {
                            percentage = Math.round(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100);
                          } else if (pilAssessment) {
                            percentage = Math.round(((pilAssessment.scores.pilTotal || 0) / 70) * 100);
                          }
                          break;

                        case 'religio':
                          // Compute average of all completed religio categories
                          const religioCategoryPercents: number[] = [];
                          religioAssessments.forEach(a => {
                            if (a.selectedCategory === 1 && a.scores && a.results) {
                              const prcTotal = a.scores.prcTotal || 0;
                              const nrcTotal = a.scores.nrcTotal || 0;
                              religioCategoryPercents.push(Math.round(((prcTotal - nrcTotal + 21) / 42) * 100));
                            } else if (a.selectedCategory === 2 && a.scores && a.results) {
                              religioCategoryPercents.push(Math.round(((a.scores.cvscaleTotal || 0) / 5) * 100));
                            } else if (a.selectedCategory === 3 && a.scores && a.results) {
                              religioCategoryPercents.push(Math.round(((a.scores.respectForTraditionTotal || 0) / 50) * 100));
                            }
                          });
                          if (religioCategoryPercents.length > 0) {
                            percentage = Math.round(religioCategoryPercents.reduce((sum, p) => sum + p, 0) / religioCategoryPercents.length);
                          }
                          break;

                        case 'occupational':
                          const occupationalData = data as OccupationalAssessmentResult;
                          const cbiAssessment = occupationalAssessments.find(a => a.selectedCategory === 1);
                          const uwesAssessment = occupationalAssessments.find(a => a.selectedCategory === 2);
                          const employeeEngagementAssessment = occupationalAssessments.find(a => a.selectedCategory === 3);

                const occupationalPercentages = [];
                if (cbiAssessment) occupationalPercentages.push(Math.round(clampPercentage(((cbiAssessment.scores.cbiTotal || 0) / 100) * 100)));
                if (uwesAssessment) occupationalPercentages.push(Math.round(clampPercentage(((uwesAssessment.scores.uwesTotal || 0) / 6) * 100)));
                if (employeeEngagementAssessment) occupationalPercentages.push(Math.round(clampPercentage(((employeeEngagementAssessment.scores.engagementTotal || 0) / 5) * 100)));

                          if (occupationalPercentages.length > 0) {
                            percentage = Math.round(occupationalPercentages.reduce((sum, p) => sum + p, 0) / occupationalPercentages.length);
                          }
                          break;

                        case 'nutritional':
                          const nutritionalData = data as NutritionalAssessmentResult;
                          if (nutritionalData.scores && nutritionalData.results) {
                            percentage = Math.round(clampPercentage((nutritionalData.scores.overall / 100) * 100));
                          }
                          break;

                        case 'physical':
                          const physicalData = data as PhysicalAssessmentResult;
                          if (physicalData.scores && physicalData.results) {
                            percentage = Math.round(clampPercentage((physicalData.scores.overall / 100) * 100));
                          } else if (physicalData.assessmentData && physicalData.assessmentData.scores && physicalData.assessmentData.results) {
                            percentage = Math.round(clampPercentage((physicalData.assessmentData.scores.overall / 100) * 100));
                          }
                          break;

                        case 'financial':
                          const financialData = data as FinancialAssessmentResult;
                          if (financialData.scores && financialData.results) {
                            const rawScore = Number(
                              (financialData.scores as any).overall ??
                              (financialData.scores as any).categoryScore ??
                              (financialData.scores as any).financialTotal ?? 0
                            );
                            percentage = Math.round(clampPercentage((rawScore / 80) * 100));
                          }
                          break;

                        case 'environmental':
                          const environmentalData = data as EnvironmentalAssessmentResult;
                          if (environmentalData.scores && environmentalData.results) {
                            percentage = Math.round(clampPercentage((environmentalData.scores.transformedScore / 100) * 100));
                          }
                          break;

                        case 'socio-relational':
                          const socioData = data as SocioRationalAssessmentResult;
                          if (socioData.scores && socioData.results) {
                            percentage = Math.round(clampPercentage((socioData.scores.socioRelationalScore / 20) * 100));
                          } else if (socioData.assessmentData && socioData.assessmentData.scores && socioData.assessmentData.results) {
                            percentage = Math.round(clampPercentage((socioData.assessmentData.scores.socioRelationalScore / 20) * 100));
                          }
                          break;
                      }

                      return { name, data, type, percentage };
                    }).filter(item => item.percentage > 0);

                    if (assessmentsWithPercentages.length === 0) {
                      return (
                        <div className="insight-item neutral">
                          <span className="material-symbols-outlined">info</span>
                          <div className="insight-content">
                            <p className="insight-title">Complete assessments to see highlights</p>
                            <p className="insight-text">Take assessments in the Learn tab to get personalized insights.</p>
                          </div>
                        </div>
                      );
                    }

                    // Sort by percentage to find highest and lowest
                    // Account for assessments with inverted meaning (low score = good)
                    const invertedMeaningAssessments = ['Emotio-Psycho-Rational', 'Occupational'];
                    
                    // Normalize scores for proper comparison
                    const itemsWithNormalizedScore = assessmentsWithPercentages.map(item => {
                      let normalizedScore = item.percentage;
                      
                      // For inverted assessments, invert the score for comparison
                      if (invertedMeaningAssessments.includes(item.name)) {
                        normalizedScore = 100 - item.percentage;
                      }
                      
                      return {
                        ...item,
                        normalizedScore
                      };
                    });

                    const sortedAssessments = itemsWithNormalizedScore.sort((a, b) => b.normalizedScore - a.normalizedScore);

                    const highest = sortedAssessments[0];
                    const lowest = sortedAssessments[sortedAssessments.length - 1];

                    // Determine display messages based on whether assessment is inverted
                    const isHighestInverted = invertedMeaningAssessments.includes(highest.name);
                    const isLowestInverted = invertedMeaningAssessments.includes(lowest.name);

                    return (
                      <>
                        {/* Highest Score */}
                    <div className="insight-item positive">
                      <span className="material-symbols-outlined">celebration</span>
                      <div className="insight-content">
                            <p className="insight-title">Excellent {highest.name} Performance!</p>
                            {isHighestInverted ? (
                              <p className="insight-text">Your {highest.name.toLowerCase()} wellness is excellent with a score of {clampPercentage(highest.percentage)}%. {highest.name === 'Occupational' ? 'Low burnout means you\'re thriving!' : 'Low symptoms indicate great mental health!'}</p>
                            ) : (
                              <p className="insight-text">Your {highest.name.toLowerCase()} wellness is thriving at {clampPercentage(highest.percentage)}%. Keep up the great work!</p>
                            )}
              </div>
            </div>

                        {/* Lowest Score */}
                    <div className={`insight-item ${isLowestInverted ? 'positive' : 'negative'}`}>
                      <span className="material-symbols-outlined">{isLowestInverted ? 'celebration' : 'self_improvement'}</span>
                      <div className="insight-content">
                            <p className="insight-title">{isLowestInverted ? 'Excellent' : 'Focus on'} {lowest.name} Wellness</p>
                            {isLowestInverted ? (
                              <p className="insight-text">Your {lowest.name.toLowerCase()} score of {clampPercentage(lowest.percentage)}% is fantastic! {lowest.name === 'Occupational' ? 'Low burnout means great work-life balance.' : 'Low symptoms show excellent psychological wellbeing.'}</p>
                            ) : (
                              <p className="insight-text">Your {lowest.name.toLowerCase()} score of {clampPercentage(lowest.percentage)}% could use some attention. Small daily improvements can make a big difference!</p>
                            )}
                      </div>
                    </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Modal */}
      {showProgressModal && (
        <div className="modal-overlay" onClick={() => setShowProgressModal(false)}>
          <div className="modal-content chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{progressTitle}</h2>
              <button
                className="modal-close"
                onClick={() => setShowProgressModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              {progressData.length === 0 ? (
                <div className="no-chart-data">
                  <p>No progress data available to display.</p>
                </div>
              ) : (
                <div className="chart-container">
                  <div className="chart-wrapper chart-bar">
                    <Suspense fallback={<div className="loading-spinner">Loading chart...</div>}>
                      <Charts chartData={progressData} chartType="bar" />
                    </Suspense>
                  </div>

                  <div className="chart-legend">
                    <h3>Category Scores</h3>
                    <div className="legend-items">
                      {progressData.map((item, index) => (
                        <div key={index} className="legend-item">
                          <div
                            className="legend-color"
                            style={{ backgroundColor: item.fill }}
                          ></div>
                          <div className="legend-text">
                            <span className="legend-name">{item.name}</span>
                            <span className="legend-details">{item.score}/{item.maxScore}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chart Modal */}
      {showChartModal && (
        <div className="modal-overlay" onClick={() => setShowChartModal(false)}>
          <div className="modal-content chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assessment Overview Chart</h2>
              <div className="chart-type-selector">
                <button
                  className={`btn btn-sm ${chartType === 'line' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('line')}
                  title="Line Chart"
                >
                  📈 Line
                </button>
                <button
                  className={`btn btn-sm ${chartType === 'bar' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('bar')}
                  title="Bar Chart"
                >
                  📊 Bar
                </button>
                <button
                  className={`btn btn-sm ${chartType === 'pie' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('pie')}
                  title="Pie Chart"
                >
                  🥧 Pie
                </button>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowChartModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const chartData = getChartData();
                if (chartData.length === 0) {
                  return (
                    <div className="no-chart-data">
                      <p>No assessment data available to display chart.</p>
                    </div>
                  );
                }

                return (
                  <div className="chart-container">
                    <div className={`chart-wrapper chart-${chartType}`}>
                      <React.Suspense fallback={<div className="loading-spinner">Loading chart...</div>}>
                        <Charts chartData={chartData} chartType={chartType} />
                      </React.Suspense>
                    </div>
                    
                    <div className="chart-legend">
                      <h3>Assessment Results</h3>
                      <div className="legend-items">
                        {chartData.map((item: any, index: number) => (
                          <div key={index} className="legend-item">
                            <div 
                              className="legend-color" 
                              style={{ backgroundColor: item.fill }}
                            ></div>
                            <div className="legend-text">
                              <span className="legend-name">{item.name}</span>
                              <span className="legend-details">
                                {item.percentage}% ({item.level})
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <>
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{
                historyType === 'all-assessments' ? 'All Assessment History'
                : historyType === 'financial' ? 'Financial Assessment History'
                : historyType === 'environmental' ? 'Environmental Assessment History'
                : historyType === 'fasdl' ? 'FASDL Assessment History'
                : historyType === 'facit-sp-12' ? 'FACIT-Sp-12 Assessment History'
                : historyType === 'spiritual' ? 'Spiritual Assessment History'
                : historyType === 'nutritional' ? 'Nutritional Assessment History'
                : (historyType === 'physical' || historyType === 'physical-ipaq' || historyType === 'physical-psqi') ? 'Physical Assessment History'
                : historyType === 'dass-21' ? 'DASS-21 Assessment History'
                : historyType === 'phq-9' ? 'PHQ-9 Assessment History'
                : historyType === 'gad-7' ? 'GAD-7 Assessment History'
                : historyType === 'gender' ? 'Gender Sensitivity Assessment History'
                : historyType === 'mental-health' ? 'Emotio-Psycho-Rational Assessment History'
                : historyType === 'legendarial' ? 'Legendarial Assessment History'
                : historyType === 'religio' ? 'Religio-Traditio-Cultural Assessment History'
                : historyType === 'socio-relational' ? 'Socio-Relational Assessment History'
                : historyType === 'occupational' ? 'Occupational Assessment History'
                : 'Assessment History'
              }</h2>
              <button
                className="modal-close"
                onClick={() => setShowHistoryModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              {/* Category selection for combined assessments - NOT shown for all-assessments */}
              {historyType !== 'all-assessments' && (() => {
                const assessmentGroup = getAssessmentGroup(historyType);
                // Remove category selector for financial
                if (assessmentGroup === 'financial') return null;
                const config = assessmentGroup ? ASSESSMENT_CONFIGS[assessmentGroup] : null;
                return config ? (
                  <div className="category-selector">
                    <h4>Select Category</h4>
                    <div className="category-buttons">
                      {config.categories.map((category) => {
                        let isActive = false;
                        if (category.id === 0) {
                          isActive = historyType === assessmentGroup && selectedHistoryCategory === null;
                        } else {
                          if (assessmentGroup === 'legendarial' || assessmentGroup === 'religio' || assessmentGroup === 'occupational' || assessmentGroup === 'socio-relational') {
                            isActive = selectedHistoryCategory === category.id;
                          } else {
                            isActive = historyType === (category.historyType || `${assessmentGroup}-${category.id}`);
                          }
                        }
                        return (
                          <button
                            key={category.id}
                            className={`category-btn ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              if (!assessmentGroup) return;
                              if (category.id === 0) {
                                fetchAssessmentHistory(assessmentGroup);
                              } else {
                                if (assessmentGroup === 'legendarial' || assessmentGroup === 'religio' || assessmentGroup === 'occupational' || assessmentGroup === 'socio-relational') {
                                  fetchAssessmentHistory(assessmentGroup, category.id);
                                } else {
                                  fetchAssessmentHistory(category.historyType || assessmentGroup);
                                }
                              }
                            }}
                          >
                            {category.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null;
              })()}
              <div className={`consultant-users consultant-users--in-modal ${isDarkMode ? 'consultant-users--dark' : ''}`}>
                {historyData.length > 0 ? (
                  <div className="consultant-users__timeline consultant-users__timeline--modal">
                    {historyData.filter((assessment: any) => {
                      if (assessment._categoryName === 'General Physical Assessment') return false;
                      return true;
                    }).map((assessment, index) => {
                      // Calculate display percentage and level consistently for all assessment types
                      const assessmentType = assessment._assessmentType || assessment.assessmentType || historyType;
                      
                      // Determine category: prioritize explicit historyType filters, then fall back to assessment data
                      let category: number | undefined;
                      
                      // For individual mental health filters, historyType takes precedence
                      if (historyType === 'dass-21') category = 1;
                      else if (historyType === 'phq-9') category = 2;
                      else if (historyType === 'gad-7') category = 3;
                      else if (historyType === 'gender') category = 4;
                      // For individual spiritual filters
                      else if (historyType === 'fasdl') category = 1;
                      else if (historyType === 'facit-sp-12') category = 2;
                      // For all other views, use assessment data
                      else category = assessment.selectedCategory || 1;
                      
                      const baseLabel = assessment._label || 'Assessment';
                      
                      // Get the assessment group and category name for better labeling
                      const getAssessmentGroup = (type: string): string => {
                        if (['spiritual', 'fasdl', 'facit-sp-12'].includes(type)) return 'spiritual';
                        if (['mental-health', 'dass-21', 'phq-9', 'gad-7', 'gender'].includes(type)) return 'mental-health';
                        if (type === 'legendarial') return 'legendarial';
                        if (type === 'religio' || type === 'religio-traditio-cultural') return 'religio';
                        if (type === 'socio-relational') return 'socio-relational';
                        if (type === 'occupational') return 'occupational';
                        if (['physical', 'physical-ipaq', 'physical-psqi'].includes(type)) return 'physical';
                        if (type === 'financial') return 'financial';
                        if (type === 'environmental') return 'environmental';
                        if (type === 'nutritional') return 'nutritional';
                        return type;
                      };
                      
                      const assessmentGroup = getAssessmentGroup(assessmentType);
                      const groupConfig = ASSESSMENT_CONFIGS[assessmentGroup];
                      let categoryName = baseLabel;
                      
                      // Get category name if available
                      if (groupConfig && category !== 0) {
                        const categoryConfig = groupConfig.categories.find(cat => cat.id === category);
                        if (categoryConfig) {
                          categoryName = categoryConfig.name;
                        }
                      }
                      
                      // For spiritual, use the test type instead
                      if (assessmentType === 'spiritual') {
                        if (assessment._testType === 'fasdl') {
                          categoryName = 'FASDL';
                        } else if (assessment._testType === 'facit-sp-12') {
                          categoryName = 'FACIT-Sp-12';
                        }
                      }
                      
                      // For physical assessments
                      if (assessmentType === 'physical' && assessment._categoryName) {
                        categoryName = assessment._categoryName === 'IPAQ-SF' ? 'IPAQ-SF' :
                                       assessment._categoryName === 'PSQI' ? 'PSQI' : assessment._categoryName;
                      }
                      
                      const completedDate = formatDate(assessment.createdAt || assessment.completedAt || '');
                      
                      let displayPercent = 0;
                      let displayLevel = 'Incomplete';
                      
                      // Calculate percentage based on assessment type
                      if (assessmentType === 'spiritual') {
                        const maxScore = assessment._testType === 'fasdl' ? 50 : 48;
                        const score = assessment.assessmentData?.scores?.overall || assessment.scores?.overall || 0;
                        displayPercent = Math.round((score / maxScore) * 100);
                      } else if (assessmentType === 'mental-health' || assessmentType === 'dass-21' || assessmentType === 'phq-9' || assessmentType === 'gad-7' || assessmentType === 'gender') {
                        let maxScore = 42;
                        if (category === 2) maxScore = 27;
                        else if (category === 3) maxScore = 21;
                        else if (category === 4) maxScore = 20;
                        
                        const score = assessment.assessmentData?.scores?.overall || assessment.scores?.overall || 0;
                        let percent = Math.round((score / maxScore) * 100);
                        percent = clampPercentage(percent);
                        const origPercent = percent;
                        
                        if (category === 4) {
                          displayPercent = 100 - percent;
                          if (displayPercent <= 30) displayLevel = 'Low';
                          else if (displayPercent <= 70) displayLevel = 'Moderate';
                          else displayLevel = 'High';
                        } else {
                          displayPercent = percent;
                          if (percent >= 67) displayLevel = 'High';
                          else if (percent >= 34) displayLevel = 'Moderate';
                          else displayLevel = 'Low';
                        }
                      } else if (assessmentType === 'financial') {
                        const score = assessment.assessmentData?.scores?.overall || assessment.scores?.overall || 0;
                        displayPercent = Math.round((score / 80) * 100);
                        if (displayPercent >= 70) displayLevel = 'High';
                        else if (displayPercent >= 40) displayLevel = 'Moderate';
                        else displayLevel = 'Low';
                      } else if (assessmentType === 'occupational') {
                        if (category === 1) { // CBI
                          const score = assessment.assessmentData?.scores?.cbiTotal || assessment.scores?.cbiTotal || 0;
                          displayPercent = Math.round((score / 100) * 100);
                          if (displayPercent >= 70) displayLevel = 'High';
                          else if (displayPercent >= 40) displayLevel = 'Moderate';
                          else displayLevel = 'Low';
                        } else if (category === 2) { // UWES
                          const score = assessment.assessmentData?.scores?.uwesTotal || assessment.scores?.uwesTotal || 0;
                          const percent = Math.round((score / 6) * 100);
                          displayPercent = 100 - clampPercentage(percent);
                          if (displayPercent >= 70) displayLevel = 'High';
                          else if (displayPercent >= 40) displayLevel = 'Moderate';
                          else displayLevel = 'Low';
                        } else { // Employee Engagement
                          const score = assessment.assessmentData?.scores?.engagementTotal || assessment.scores?.engagementTotal || 0;
                          displayPercent = Math.round(((5 - score) / 5) * 100);
                          if (displayPercent >= 70) displayLevel = 'High';
                          else if (displayPercent >= 40) displayLevel = 'Moderate';
                          else displayLevel = 'Low';
                        }
                      } else if (assessmentType === 'religio-traditio-cultural') {
                        if (category === 1) { // Brief RCOPE
                          const prcTotal = assessment.scores?.prcTotal || assessment.assessmentData?.scores?.prcTotal || 0;
                          const nrcTotal = assessment.scores?.nrcTotal || assessment.assessmentData?.scores?.nrcTotal || 0;
                          displayPercent = Math.round(((prcTotal - nrcTotal + 21) / 42) * 100);
                          if (displayPercent >= 75) displayLevel = 'High';
                          else if (displayPercent >= 50) displayLevel = 'Moderate';
                          else displayLevel = 'Low';
                        } else if (category === 2) { // CV Scale
                          const score = assessment.assessmentData?.scores?.cvscaleTotal || assessment.scores?.cvscaleTotal || 0;
                          displayPercent = Math.round((score / 5) * 100);
                        } else { // TVAS
                          const score = assessment.assessmentData?.scores?.respectForTraditionTotal || assessment.scores?.respectForTraditionTotal || 0;
                          displayPercent = Math.round((score / 50) * 100);
                        }
                      } else if (assessmentType === 'socio-relational') {
                        const score = assessment.assessmentData?.scores?.socioRelationalScore || assessment.scores?.socioRelationalScore || 0;
                        const maxScore = category === 2 ? 7 : 20;
                        displayPercent = Math.round((score / maxScore) * 100);
                      } else if (assessmentType === 'legendarial') {
                        const maxScore = category === 1 ? 35 : 70;
                        const scoreField = category === 1 ? 'swlsTotal' : 'pilTotal';
                        const score = assessment.assessmentData?.scores?.[scoreField] || assessment.scores?.[scoreField] || 0;
                        displayPercent = Math.round((score / maxScore) * 100);
                      } else if (assessmentType === 'physical') {
                        if (assessment._categoryScore !== undefined) {
                          displayPercent = Math.round(assessment._categoryScore);
                        }
                      } else if (assessmentType === 'nutritional') {
                        const score = assessment.assessmentData?.scores?.overall || assessment.scores?.overall || 0;
                        displayPercent = Math.round((score / 100) * 100);
                      } else if (assessmentType === 'environmental') {
                        const score = assessment.assessmentData?.scores?.transformedScore || assessment.scores?.transformedScore || 0;
                        displayPercent = Math.round((score / 100) * 100);
                      }
                      
                      displayPercent = clampPercentage(displayPercent);
                      // Only override displayLevel from assessment data if not Gender Sensitivity or specific individual filters
                      // (Gender Sensitivity needs inverted logic which we already calculated above)
                      const isGenderSensitivityCheck = historyType === 'gender' || (assessmentType === 'gender' || (assessmentType === 'mental-health' && category === 4));
                      if (!isGenderSensitivityCheck && (!displayLevel || displayLevel === 'Incomplete')) {
                        displayLevel = assessment.assessmentData?.results?.level || assessment.results?.level || 'Incomplete';
                      }
                      
                      // Get level color based on assessment type and level
                      const levelColor = (() => {
                        const lvl = (displayLevel || '').toLowerCase();
                        // Mental health symptoms: DASS-21, PHQ-9, GAD-7 (not Gender Sensitivity)
                        const isMentalHealthSymptoms = (assessmentType === 'mental-health' && category !== 4) || 
                                                       ['dass-21', 'phq-9', 'gad-7'].includes(assessmentType);
                        // Gender Sensitivity (mental-health category 4 or specific 'gender' type)
                        const isGenderSensitivity = (assessmentType === 'mental-health' && category === 4) || 
                                                    assessmentType === 'gender';
                        const isBurnout = assessmentType === 'occupational' && (category === 1 || category === 2 || category === 3);
                        const isFinancial = assessmentType === 'financial';
                        
                        if (isFinancial) {
                          if (lvl === 'high') return '#059669';
                          if (lvl === 'moderate') return '#ea580c';
                          if (lvl === 'low') return '#dc2626';
                        } else if (isMentalHealthSymptoms) {
                          // Mental health symptoms: High = red (bad), Moderate = orange, Low = green (good)
                          // 0% = Low = Green (good mental health), 100% = High = Red (bad mental health)
                          if (lvl === 'high') return '#dc2626';
                          if (lvl === 'moderate') return '#ea580c';
                          if (lvl === 'low') return '#059669';
                        } else if (isGenderSensitivity) {
                          if (lvl === 'low') return '#059669';
                          if (lvl === 'moderate') return '#ea580c';
                          if (lvl === 'high') return '#dc2626';
                        } else if (isBurnout) {
                          if (lvl === 'low') return '#059669';
                          if (lvl === 'moderate') return '#ea580c';
                          if (lvl === 'high') return '#dc2626';
                        } else {
                          if (lvl === 'high') return '#059669';
                          if (lvl === 'moderate') return '#ea580c';
                          if (lvl === 'low') return '#dc2626';
                        }
                        return '#6B7280';
                      })();
                      
                      return (
                        <div key={`${categoryName}-${assessment.id}-${index}`} className="consultant-users__timeline-item">
                          <div className="consultant-users__timeline-marker">
                            <div className={`consultant-users__timeline-dot ${index === 0 ? 'consultant-users__timeline-dot--current' : 'consultant-users__timeline-dot--past'}`} style={{ backgroundColor: levelColor }}></div>
                            {index < historyData.length - 1 && (
                              <div className="consultant-users__timeline-line"></div>
                            )}
                          </div>
                          <div className="consultant-users__timeline-content">
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '1rem',
                              marginBottom: '0.75rem'
                            }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: levelColor,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                  }}>
                                    {categoryName}
                                  </span>
                                </div>
                                <p style={{
                                  margin: 0,
                                  fontSize: '0.875rem',
                                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                                  fontWeight: 500
                                }}>
                                  {completedDate}
                                </p>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                textAlign: 'right'
                              }}>
                                <div>
                                  <div style={{
                                    fontSize: '1.75rem',
                                    fontWeight: 800,
                                    color: isDarkMode ? '#F3F4F6' : '#1F2937'
                                  }}>
                                    {displayPercent}%
                                  </div>
                                  <div style={{
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    color: levelColor
                                  }}>
                                    {displayLevel}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-history">
                    <p>No assessment history found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* SOWB Assessment History Modal - same design as consultant user page */}
      {showSowbHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowSowbHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assessment History</h2>
              <button
                className="modal-close"
                onClick={() => setShowSowbHistoryModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              <div className={`consultant-users consultant-users--in-modal ${isDarkMode ? 'consultant-users--dark' : ''}`}>
                <div className="consultant-users__timeline consultant-users__timeline--modal">
                  {sowbHistory?.length > 0 ? sowbHistory.map((item, index) => (
                    <div key={item.id} className="consultant-users__timeline-item">
                      <div className="consultant-users__timeline-marker">
                        <div className={`consultant-users__timeline-dot ${index === 0 ? 'consultant-users__timeline-dot--current' : 'consultant-users__timeline-dot--past'}`}></div>
                        {index < (sowbHistory?.length ?? 0) - 1 && (
                          <div className="consultant-users__timeline-line"></div>
                        )}
                      </div>
                      <div className="consultant-users__timeline-content">
                        <div className="consultant-users__timeline-header">
                          <span className={`consultant-users__timeline-label ${index === 0 ? 'consultant-users__timeline-label--current' : ''}`}>
                            {index === 0 ? 'Latest' : formatDate(item.createdAt)}
                          </span>
                          {index === 0 && (
                            <span className="consultant-users__timeline-date">
                              {formatDate(item.createdAt)}
                            </span>
                          )}
                        </div>
                        <div className="consultant-users__timeline-score">
                          <span className="consultant-users__timeline-score-number">
                            {item.overallScore == null || item.overallScore === 0 || !Number.isFinite(item.overallScore)
                              ? '—'
                              : item.percent}
                          </span>
                          <span className="consultant-users__timeline-score-change consultant-users__timeline-score-change--positive">
                            {item.overallLevel}
                          </span>
                        </div>
                        <p className="consultant-users__timeline-notes">
                          SOWB10 Assessment completed
                        </p>
                        <button
                          type="button"
                          className="consultant-users__timeline-report-btn"
                          onClick={() => { setShowSowbHistoryModal(false); navigate('/panel/wellness/details'); }}
                        >
                          View Report
                        </button>
                      </div>
                    </div>
                  )) : (
                    <div className="no-history">
                      <p>No assessment history found.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsPage;


